import { saveRecording } from "./finalizeRecording";
import { chooseRecordingMimeType } from "./recordingFormat";
// VideoLessonRoom.jsx

import React, {
  useEffect,
  useRef,
  useState
} from "react";

import {
  useNavigate,
  useParams
} from "react-router-dom";

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc
} from "firebase/firestore";

import { db } from "./firebase";

import CollaborativeWhiteboard from "./CollaborativeWhiteboard";

// Halloween appearance and camera background controls (kept in this file).
// Switch off after the special session to restore the usual room colours.
const HALLOWEEN_LESSON_THEME_ENABLED = true;

const HALLOWEEN_LESSON_STYLES = String.raw`
.halloween-lesson-room{--lesson-primary:#b94d10;--lesson-panel:#21162e;--lesson-deep:#100c19;--lesson-control:#3b294b;--lesson-accent:#f5bd87;position:relative;isolation:isolate;background:radial-gradient(ellipse at 0 0,#44234866,transparent 47%),radial-gradient(ellipse at 100% 20%,#7e351b33,transparent 45%),linear-gradient(145deg,#100c19,#181021 55%,#100c19)!important;color:#fff5e9!important}
.halloween-lesson-room .hl-page-decorations{position:fixed;inset:0;pointer-events:none;z-index:0;overflow:hidden}.halloween-lesson-room .hl-page-decorations .hl-pumpkin{position:absolute;left:20px;top:210px;width:48px;height:52px;opacity:.65}.halloween-lesson-room .hl-page-decorations .hl-moon-bats{position:absolute;right:25px;top:180px;opacity:.7}@media(max-width:700px){.halloween-lesson-room .hl-page-decorations .hl-pumpkin{left:3px;top:175px;width:28px;height:32px}.halloween-lesson-room .hl-page-decorations .hl-moon-bats{right:2px;top:110px;width:48px}}
.halloween-lesson-room .hl-content{position:relative;z-index:1}
.halloween-lesson-room .hl-surface{background:linear-gradient(145deg,#281a36,#1b1329)!important;outline:1px solid #aa724c66;outline-offset:-1px;box-shadow:0 18px 52px #05020b66,inset 0 1px 0 #edb48513!important}
.halloween-lesson-room .hl-join-card,.halloween-lesson-room .hl-waiting-card{outline-color:#ed963d88}
.halloween-lesson-room .hl-video-frame{outline:1px solid #ab744866;outline-offset:-1px;box-shadow:0 0 0 3px #f29c3510}
.halloween-lesson-room .hl-controls{background:#21172ff5!important;outline:1px solid #ab744877;outline-offset:-1px}
.halloween-lesson-room .hl-chat{background:linear-gradient(180deg,#291b37,#160f22)!important;border-left-color:#b97d4a88!important}
.halloween-lesson-room .hl-surface input:not([type=color]),.halloween-lesson-room .hl-surface select,.halloween-lesson-room .hl-chat textarea{background-color:#130e1d!important;color:#fff4e6!important;border-color:#755583!important;color-scheme:dark}
.halloween-lesson-room input::placeholder,.halloween-lesson-room textarea::placeholder{color:#bcb1c8;opacity:1}
.halloween-lesson-room button:focus-visible,.halloween-lesson-room input:focus-visible,.halloween-lesson-room select:focus-visible,.halloween-lesson-room textarea:focus-visible{outline:3px solid #ffbd73;outline-offset:3px}
.halloween-lesson-room .hl-session-banner{position:relative;z-index:1;max-width:1400px;margin:0 auto 20px;display:flex;align-items:center;justify-content:space-between;gap:16px;padding:15px 22px;border:1px solid #dd913f70;border-radius:20px;background:linear-gradient(110deg,#35203dee,#24172fe8 60%,#48271eaa);box-shadow:0 12px 38px #0003;overflow:hidden}
.halloween-lesson-room .hl-session-brand{display:flex;align-items:center;gap:15px;min-width:0}
.halloween-lesson-room .hl-pumpkin{width:54px;height:58px;flex-shrink:0;filter:drop-shadow(0 0 12px #ed7d263b)}
.halloween-lesson-room .hl-session-eyebrow{margin:0 0 3px;font:700 10px/1.3 system-ui,sans-serif;letter-spacing:2.5px;color:#e8b5ff}
.halloween-lesson-room .hl-session-title{display:block;color:#fff0d9;font:700 clamp(20px,2vw,28px)/1.15 Georgia,serif;letter-spacing:.2px}
.halloween-lesson-room .hl-session-caption{margin:5px 0 0;color:#e6c3a6;font:400 12px/1.4 system-ui,sans-serif}
.halloween-lesson-room .hl-moon-bats{width:132px;height:60px;flex-shrink:0}
.halloween-lesson-room .hl-corner-web{position:fixed;top:0;width:190px;height:190px;color:#d9b8ed;opacity:.13;pointer-events:none;z-index:0}
.halloween-lesson-room .hl-corner-web-left{left:0}.halloween-lesson-room .hl-corner-web-right{right:0;transform:scaleX(-1)}
@media(max-width:700px){.halloween-lesson-room{padding:16px!important}.halloween-lesson-room .hl-session-banner{padding:12px 14px;margin-bottom:14px;border-radius:16px}.halloween-lesson-room .hl-session-brand{gap:10px}.halloween-lesson-room .hl-pumpkin{width:41px;height:46px}.halloween-lesson-room .hl-session-title{font-size:21px}.halloween-lesson-room .hl-session-eyebrow{font-size:9px;letter-spacing:1.8px}.halloween-lesson-room .hl-moon-bats{width:58px}.halloween-lesson-room .hl-corner-web{width:120px;height:120px}.halloween-lesson-room .hl-join-card{padding:20px!important;margin:16px auto!important}}
`;

function CornerWeb({ className }) {
  return <svg className={className} viewBox="0 0 200 200" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden="true">
    <path d="M0 0V194M0 0H194M0 0L184 61M0 0L142 142M0 0L61 184" />
    {[38, 77, 116, 155, 193].map(r => <path key={r} d={`M0 ${r}Q${r * .21} ${r * .7} ${r * .316} ${r * .948}Q${r * .42} ${r * .5} ${r * .707} ${r * .707}Q${r * .5} ${r * .42} ${r * .948} ${r * .316}Q${r * .7} ${r * .21} ${r} 0`} />)}
  </svg>;
}

function HalloweenLessonTheme() {
  if (!HALLOWEEN_LESSON_THEME_ENABLED) return null;
  return <>
    <style>{HALLOWEEN_LESSON_STYLES}</style>
    <CornerWeb className="hl-corner-web hl-corner-web-left" />
    <CornerWeb className="hl-corner-web hl-corner-web-right" />
    <div className="hl-page-decorations" aria-hidden="true">
      <div>
        <svg className="hl-pumpkin" viewBox="0 0 70 76" aria-hidden="true">
          <path d="M34 17Q29 6 40 3L44 8Q35 9 39 20" fill="#8ca76d" />
          <ellipse cx="21" cy="44" rx="19" ry="26" fill="#dd6a22" />
          <ellipse cx="49" cy="44" rx="19" ry="26" fill="#dc6b24" />
          <ellipse cx="35" cy="44" rx="24" ry="28" fill="#f39435" />
          <path d="M26 20Q16 45 26 67M44 20Q54 45 44 67" fill="none" stroke="#d76b22" strokeWidth="2" />
          <path d="M17 39L29 33L29 43ZM53 39L41 33L41 43ZM32 47L35 40L39 47ZM19 51L27 54L30 51L35 57L42 52L45 55L53 51Q47 66 35 65Q23 65 19 51" fill="#4a251e" />
          <path d="M22 53L28 56M45 57L50 54" stroke="#ffd36e" strokeWidth="2" strokeLinecap="round" />
        </svg>

      </div>
      <svg className="hl-moon-bats" viewBox="0 0 132 60" aria-hidden="true">
        <circle cx="99" cy="28" r="22" fill="#f4d6a4" opacity=".9" />
        <circle cx="108" cy="21" r="20" fill="#392032" />
        <path d="M4 30Q14 13 24 29L29 23L34 29Q44 13 54 30Q41 27 35 38L29 34L23 38Q17 27 4 30M57 16Q64 5 71 16L75 12L79 16Q86 5 93 16Q84 14 79 22L75 19L71 22Q66 14 57 16" fill="#100d1b" />
        <path d="M59 46h4m-2-2v4M12 10h4m-2-2v4M119 48h4m-2-2v4" stroke="#e8b5ff" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    </div>
  </>;
}

const HALLOWEEN_BACKGROUNDS = [
  { value: 'haunted-gates', label: 'Haunted gates' },
  { value: 'pumpkin-candles', label: 'Pumpkins and candles' },
  { value: 'gothic-room', label: 'Gothic room' },
  { value: 'moonlit-path', label: 'Moonlit path' }
].map(item => ({ ...item, src: `/lesson-backgrounds/${item.value}.png` }));

function CameraBackgroundPicker({ value, busy, error, onChange, brightness, onBrightnessChange }) {
  const [customOpen, setCustomOpen] = useState(false);
  const customSelected = HALLOWEEN_BACKGROUNDS.some(option => option.value === value);
  const buttonStyle = selected => ({ padding: '10px', borderRadius: 10, border: selected ? '2px solid #ffbd73' : '1px solid #795681', background: '#21162e', color: '#fff5e9', cursor: busy ? 'wait' : 'pointer', flex: '1 1 125px', minWidth: 110 });
  return <section aria-label="Camera background" style={{ margin: '16px 0', padding: 16, border: '1px solid #795681', borderRadius: 16, background: '#160f22', color: '#fff5e9' }}>
    <strong>Camera background</strong>
    <p style={{ fontSize: 12 }}>Choose how your camera background appears in the lesson.</p>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {[{ value: 'none', label: 'None' }, { value: 'blur', label: 'Blur' }, { value: 'strong-blur', label: 'Strong Blur' }].map(option => <button type="button" key={option.value} disabled={busy} aria-pressed={value === option.value} onClick={() => { setCustomOpen(false); onChange(option.value); }} style={buttonStyle(value === option.value)}>
        {option.label}
      </button>)}
      <button type="button" disabled={busy} aria-expanded={customOpen} aria-pressed={customSelected} onClick={() => setCustomOpen(open => !open)} style={buttonStyle(customSelected)}>Custom</button>
    </div>
    {customOpen && <div role="group" aria-label="Custom background choices" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
      <p style={{ width: '100%', fontSize: 12, margin: '4px 0' }}>Choose a background.</p>
      {HALLOWEEN_BACKGROUNDS.map(option => <button type="button" key={option.value} disabled={busy} aria-pressed={value === option.value} onClick={() => onChange(option.value)} style={buttonStyle(value === option.value)}>
        <img src={option.src} alt="" style={{ width: '100%', height: 68, objectFit: 'cover', borderRadius: 6, display: 'block', marginBottom: 6 }} />{option.label}
      </button>)}

    </div>}
    {customSelected && <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 11, color: '#bdb2c6', fontWeight: 400 }}>
      <span>Brightness</span>
      <input type="range" min="60" max="160" step="1" value={brightness} aria-label="Camera face brightness" aria-valuetext={brightness + '%'} onChange={event => onBrightnessChange(Number(event.target.value))} style={{ width: 110, height: 3, accentColor: '#a99bb5', cursor: 'pointer', margin: 0 }} />
      <span style={{ minWidth: 30, fontVariantNumeric: 'tabular-nums' }}>{brightness}%</span>
      {brightness !== 100 && <button type="button" onClick={() => onBrightnessChange(100)} style={{ background: 'transparent', border: 0, color: '#bdb2c6', fontSize: 10, padding: '2px 4px', cursor: 'pointer' }}>Reset</button>}
    </label>}
    {busy && <p role="status">Preparing background…</p>}
    {error && <p role="alert" style={{ color: '#ffc4bf' }}>{error}</p>}
  </section>;
}

// Drawn into the processed camera stream so everyone sees the disappearance.
const SMOKE_EXIT_DURATION_MS = 1400;
const WITCH_LAUGH_DURATION_MS = 2900;
function drawExitSmoke(context, width, height, progress) {
  const fade = Math.sin(Math.PI * Math.min(1, progress / 0.95));
  if (fade <= 0) return;
  context.save();
  context.globalCompositeOperation = 'source-over';
  for (let index = 0; index < 26; index++) {
    const angle = index * 2.39996;
    const spread = Math.min(width, height) * (0.06 + progress * 0.42);
    const x = width * 0.5 + Math.cos(angle) * spread * (0.4 + index % 5 / 6);
    const y = height * 0.57 + Math.sin(angle) * spread * 0.7 - progress * height * 0.2;
    const radius = Math.min(width, height) * (0.09 + progress * 0.12) * (0.8 + index % 4 / 6);
    const cloud = context.createRadialGradient(x, y, 0, x, y, radius);
    cloud.addColorStop(0, 'rgba(230,220,238,' + (fade * 0.32) + ')');
    cloud.addColorStop(0.5, 'rgba(179,164,194,' + (fade * 0.21) + ')');
    cloud.addColorStop(1, 'rgba(140,120,160,0)');
    context.fillStyle = cloud;
    context.fillRect(x - radius, y - radius, radius * 2, radius * 2);
  }
  context.restore();
}

const renderChatMessage = (value) => {
  const text = String(value ?? "");
  const urls = /(?:https?:\/\/|www\.)[^\s<>"']+|(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}(?::\d+)?(?:[/?#][^\s<>"']*)?/gi;
  const content = [];
  let cursor = 0;
  for (const match of text.matchAll(urls)) {
    // Keep email addresses and fragments of other schemes as plain text.
    if (match.index > 0 && /[\w@/:-]/.test(text[match.index - 1])) continue;
    let label = match[0].replace(/[.,!?;:]+$/, "");
    for (const [open, close] of [["(", ")"], ["[", "]"], ["{", "}"]]) {
      while (label.endsWith(close) && label.split(close).length > label.split(open).length) label = label.slice(0, -1);
    }
    const href = /^https?:\/\//i.test(label) ? label : `https://${label}`;
    try {
      const url = new URL(href);
      if (!["http:", "https:"].includes(url.protocol) || !url.hostname) continue;
    } catch {
      continue;
    }
    content.push(text.slice(cursor, match.index));
    content.push(
      <a key={match.index} href={href} target="_blank" rel="noopener noreferrer" style={{ color: "inherit", textDecoration: "underline", textUnderlineOffset: "3px" }}>
        {label}
      </a>
    );
    cursor = match.index + label.length;
  }
  content.push(text.slice(cursor));
  return content;
};

const FrozenScreenPreview = ({ image }) => image ? (
  <img src={image} alt="Frozen shared screen" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", background: "#000", borderRadius: "inherit", pointerEvents: "none" }} />
) : null;

const PresenterNotice = ({ name }) => {
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const timer = window.setTimeout(() => setVisible(false), 5000);
    return () => window.clearTimeout(timer);
  }, []);
  return <div role="status" style={{ position: "absolute", top: 18, left: "50%", transform: "translateX(-50%)", zIndex: 3, padding: "0.55rem 0.9rem", borderRadius: 999, background: "rgba(15,23,42,.88)", pointerEvents: "none", opacity: visible ? 1 : 0, transition: "opacity 500ms ease" }}><strong>{name || "Participant"}</strong> is presenting</div>;
};

const DISPLAY_NAME_STORAGE_KEY =
  "learnWithRyan.lessonDisplayName";

const getSavedDisplayName = () => {
  try {
    return window.localStorage.getItem(
      DISPLAY_NAME_STORAGE_KEY
    ) || "";
  } catch (error) {
    console.warn(
      "Could not restore the saved lesson name:",
      error
    );
    return "";
  }
};

const saveDisplayName = (name) => {
  try {
    window.localStorage.setItem(
      DISPLAY_NAME_STORAGE_KEY,
      name
    );
  } catch (error) {
    console.warn(
      "Could not remember the lesson name:",
      error
    );
  }
};

const getVoiceAudioConstraints = () => {
  const supported =
    navigator.mediaDevices?.getSupportedConstraints?.() || {};

  return {
    ...(supported.echoCancellation
      ? { echoCancellation: true }
      : {}),
    ...(supported.noiseSuppression
      ? { noiseSuppression: true }
      : {}),
    ...(supported.autoGainControl
      ? { autoGainControl: false }
      : {}),
    ...(supported.voiceIsolation
      ? { voiceIsolation: true }
      : {}),
    ...(supported.channelCount
      ? { channelCount: { ideal: 1 } }
      : {}),
    ...(supported.sampleRate
      ? { sampleRate: { ideal: 48000 } }
      : {}),
    ...(supported.sampleSize
      ? { sampleSize: { ideal: 16 } }
      : {}),
    ...(supported.latency
      ? { latency: { ideal: 0.01 } }
      : {})
  };
};

const CameraStatusBadges = ({
  micEnabled = true,
  handRaised = false
}) => (
  <div
    style={{
      position: "absolute",
      top: "9px",
      right: "9px",
      zIndex: 3,
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-end",
      gap: "6px",
      pointerEvents: "none"
    }}
  >
    {!micEnabled && (
      <span
        title="Microphone muted"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: "34px",
          height: "34px",
          padding: "0 8px",
          borderRadius: "999px",
          background: "rgba(153,27,27,.94)",
          border: "1px solid rgba(254,202,202,.75)",
          boxShadow: "0 4px 14px rgba(0,0,0,.35)",
          fontSize: "1rem"
        }}
      >
        🔇
      </span>
    )}

    {handRaised && (
      <span
        title="Hand raised"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: "34px",
          height: "34px",
          padding: "0 8px",
          borderRadius: "999px",
          background: "rgba(180,83,9,.96)",
          border: "1px solid rgba(253,230,138,.85)",
          boxShadow: "0 4px 14px rgba(0,0,0,.35)",
          fontSize: "1rem"
        }}
      >
        ✋
      </span>
    )}
  </div>
);

const VideoLessonRoom = () => {
  // =========================================================
  // ROUTER
  // =========================================================

  const { roomId } = useParams();
  const navigate = useNavigate();

  // =========================================================
  // ELECTRON DESKTOP DETECTION
  // =========================================================

  const isElectronDesktop =
    window.learnWithRyanDesktop?.isDesktopApp === true;

  // =========================================================
  // LOCAL MEDIA
  // =========================================================

  const localVideoRef = useRef(null);
  const smokeExitStartedRef = useRef(null);
  const leavingRoomRef = useRef(false);
  const exitLaughRef = useRef(null);
  const localStreamRef = useRef(null);
  const voiceProcessedStreamRef = useRef(null);
  const voiceAudioContextRef = useRef(null);
  const voiceProcessorRef = useRef(null);
  const voiceSourceRef = useRef(null);
  const microphoneTestRecorderRef = useRef(null);
  const microphoneTestTrackRef = useRef(null);
  const microphoneTestTimerRef = useRef(null);
  const microphoneTestStopTimerRef = useRef(null);
  const microphoneTestAudioRef = useRef(null);
  const microphoneTestUrlRef = useRef(null);
  const mutedSpeechTrackRef = useRef(null);
  const mutedSpeechAudioContextRef = useRef(null);
  const mutedSpeechAnimationRef = useRef(null);
  const mutedSpeechWarningTimerRef = useRef(null);

  // =========================================================
  // CAMERA BACKGROUND EFFECTS
  // =========================================================

  const processedCameraStreamRef = useRef(null);
  const processedCameraVideoRef = useRef(null);
  const processedCameraCanvasRef = useRef(null);
  const processedCameraForegroundCanvasRef =
    useRef(null);
  const processedCameraMaskCanvasRef =
    useRef(null);

  const processedCameraAnimationRef =
    useRef(null);
  const processedCameraSourceTrackIdRef =
    useRef(null);

  const backgroundSegmenterRef =
    useRef(null);
  const backgroundSegmenterPromiseRef =
    useRef(null);

  const backgroundImageRef = useRef(null);
  const [cameraBrightness, setCameraBrightness] = useState(100);
  const cameraBrightnessRef = useRef(100);
  const changeCameraBrightness = value => {
    const brightness = Math.max(60, Math.min(160, Number(value) || 100));
    cameraBrightnessRef.current = brightness;
    setCameraBrightness(brightness);
  };
  const [backgroundImageLoading, setBackgroundImageLoading] = useState(false);
  const backgroundSelectionBusyRef = useRef(false);
  const backgroundEffectRef =
    useRef("none");
  const backgroundProcessingBusyRef =
    useRef(false);
  const backgroundMaskReadyRef =
    useRef(false);
  const backgroundLastSegmentationMsRef =
    useRef(0);

  // =========================================================
  // SCREEN SHARE
  // =========================================================

  const screenStreamRef = useRef(null);
  const screenAudioContextRef = useRef(null);
  const mixedAudioStreamRef = useRef(null);
  const screenRecorderRef = useRef(null);
  const screenRecordingStreamRef = useRef(null);
  const screenRecordingChunksRef = useRef([]);
  const screenRecordingAudioContextRef = useRef(null);
  const screenRecordingAudioSourcesRef = useRef([]);
  const screenRecordingStatusRef = useRef("idle");
  const screenRecordingTimerRef = useRef(null);
  const screenRecordingNoticeTimerRef = useRef(null);
  const screenRecordingNoticeRemoveTimerRef = useRef(null);
  const screenRecordingCanvasRef = useRef(null);
  const screenRecordingAnimationRef = useRef(null);
  const screenRecordingScreenVideoRef = useRef(null);
  const screenRecordingCameraVideoRef = useRef(null);

  const screenPreviewVideoRef = useRef(null);
  const screenReadyRef = useRef(false);
  const screenReadyPromiseRef = useRef(null);

  // =========================================================
  // FREEZE SCREEN
  // =========================================================

  const frozenScreenStreamRef = useRef(null);
  const frozenScreenVideoRef = useRef(null);
  const freezeCanvasRef = useRef(null);
  const [frozenScreenImage, setFrozenScreenImage] = useState(null);

  // =========================================================
  // DOCUMENT PICTURE-IN-PICTURE
  // =========================================================

  const pipWindowRef = useRef(null);

  const pipControlsRef = useRef({
    status: null,
    freezeButton: null,
    micButton: null,
    stopButton: null,
    participantsContainer: null,
    participantCount: null
  });

  // Small local preview elements used only to feed the native
  // Electron floating-controls participant camera thumbnails.
  const floatingPreviewCacheRef = useRef(new Map());

  // =========================================================
  // STATE REFS
  // =========================================================

  const screenFrozenStateRef = useRef(false);
  const micEnabledStateRef = useRef(true);
  const screenSharingStateRef = useRef(false);

  // =========================================================
  // WEBRTC
  // =========================================================

  const peerConnectionsRef = useRef(new Map());
  const remoteStreamsRef = useRef(new Map());
  const remoteCameraStreamsRef = useRef(new Map());
  const cameraSendersRef = useRef(new Map());
  const peerListenersRef = useRef(new Map());
  const iceQueuesRef = useRef(new Map());

  // =========================================================
  // REMOTE CONTROL DATA CHANNELS
  // =========================================================

  const controlChannelsRef = useRef(new Map());
  const remoteControlMoveTimerRef = useRef(0);

  // =========================================================
  // COLLABORATIVE WHITEBOARD
  // =========================================================

  const whiteboardOpenRef = useRef(false);
  const whiteboardSnapshotRef = useRef([]);
  const whiteboardSaveTimerRef = useRef(null);
  const whiteboardSaveQueueRef = useRef(Promise.resolve());
  const whiteboardPersistenceReadyRef = useRef(false);
  const whiteboardOpenRetryTimersRef = useRef([]);
  const whiteboardSnapshotTransfersRef = useRef(new Map());
  const WHITEBOARD_CHUNK_SIZE = 650000;
  const WHITEBOARD_LIVE_CHUNK_SIZE = 12000;

  // =========================================================
  // PARTICIPANT PRESENCE
  // =========================================================

  const participantIdRef = useRef(null);
  const participantDocRef = useRef(null);
  const participantListenerRef = useRef(null);
  const heartbeatTimerRef = useRef(null);
  const admissionListenerRef = useRef(null);
const lobbySoundRef = useRef(null);
const previousWaitingIdsRef = useRef(
  new Set()
);
  // =========================================================
  // CHAT
  // =========================================================

  const chatListenerRef = useRef(null);
  const chatEndRef = useRef(null);
  const chatOpenRef = useRef(false);
  const chatTypingStopTimerRef = useRef(null);
  const chatTypingLastSentAtRef = useRef(0);
  const chatTypingStateRef = useRef(false);
const chatSoundRef = useRef(null);

// =========================================================
// CHAT SOUND
// =========================================================

const playIncomingChatSound = () => {
  try {
    if (!chatSoundRef.current) {
      chatSoundRef.current =
        new Audio(
          "/assets/Pop.mp3"
        );

      chatSoundRef.current.volume =
        0.5;
    }

    const sound =
      chatSoundRef.current;

    // Allow repeated messages to play the
    // sound again from the beginning.
    sound.currentTime = 0;

    sound
      .play()
      .catch((error) => {
        console.warn(
          "Could not play incoming chat sound:",
          error
        );
      });
  } catch (error) {
    console.warn(
      "Chat sound error:",
      error
    );
  }
};

// =========================================================
// LOBBY SOUND
// =========================================================

const playLobbySound = () => {
  if (!isElectronDesktop) {
    return;
  }

  try {
    if (!lobbySoundRef.current) {
      lobbySoundRef.current =
        new Audio(
          "/assets/bell.mp3"
        );

      lobbySoundRef.current.volume =
        0.3;
    }

    const sound =
      lobbySoundRef.current;

    sound.currentTime = 0;

    sound
      .play()
      .catch((error) => {
        console.warn(
          "Could not play lobby bell:",
          error
        );
      });
  } catch (error) {
    console.warn(
      "Lobby sound error:",
      error
    );
  }
};

  // =========================================================
  // MEDIA STATE
  // =========================================================

  const [
    cameraEnabled,
    setCameraEnabled
  ] = useState(true);

  const [
    micEnabled,
    setMicEnabled
  ] = useState(true);

  const [
    handRaised,
    setHandRaised
  ] = useState(false);

  const [
    cameraStarted,
    setCameraStarted
  ] = useState(false);

  const [mediaDevices, setMediaDevices] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState("");
  const [selectedMicrophoneId, setSelectedMicrophoneId] = useState("");
  const [switchingDevice, setSwitchingDevice] = useState(false);
  const [microphoneTestStatus, setMicrophoneTestStatus] = useState("idle");
  const [microphoneTestSeconds, setMicrophoneTestSeconds] = useState(5);
  const [microphoneTestMessage, setMicrophoneTestMessage] = useState("");
  const [mutedSpeechWarning, setMutedSpeechWarning] = useState(false);

  // =========================================================
  // CAMERA BACKGROUND STATE
  // =========================================================

  const [
    backgroundEffect,
    setBackgroundEffect
  ] = useState("none");

  const [
    backgroundEffectLoading,
    setBackgroundEffectLoading
  ] = useState(false);

  const [
    backgroundEffectError,
    setBackgroundEffectError
  ] = useState("");

  // =========================================================
  // JOIN STATE
  // =========================================================

  const [
    hasJoinedLesson,
    setHasJoinedLesson
  ] = useState(false);

const [
  hasLeftLesson,
  setHasLeftLesson
] = useState(false);

  const [
    isWaitingForAdmission,
    setIsWaitingForAdmission
  ] = useState(false);

  const [
    waitingParticipants,
    setWaitingParticipants
  ] = useState([]);

  const [
    joining,
    setJoining
  ] = useState(false);

  const [
    role,
    setRole
  ] = useState("");

  const [
    displayName,
    setDisplayName
  ] = useState(getSavedDisplayName);

  const [
    error,
    setError
  ] = useState("");

  // =========================================================
  // REMOTE PARTICIPANTS
  // =========================================================

  const [
    remoteParticipants,
    setRemoteParticipants
  ] = useState([]);

  const [
    whiteboardOpen,
    setWhiteboardOpen
  ] = useState(false);

  const [
    whiteboardCommand,
    setWhiteboardCommand
  ] = useState([]);

  // =========================================================
  // REMOTE CONTROL STATE
  // =========================================================

  const [
    remoteControlOffer,
    setRemoteControlOffer
  ] = useState(null);

  const [
    remoteControlSession,
    setRemoteControlSession
  ] = useState({
    active: false,
    tutorParticipantId: null,
    tutorName: "Tutor"
  });

  // =========================================================
  // SCREEN SHARE STATE
  // =========================================================

  const [
    isScreenSharing,
    setIsScreenSharing
  ] = useState(false);

  const [
    isScreenFrozen,
    setIsScreenFrozen
  ] = useState(false);

  const [
    screenShareMessage,
    setScreenShareMessage
  ] = useState("");

  const [screenRecordingStatus, setScreenRecordingStatus] = useState("idle");
  const [screenRecordingSeconds, setScreenRecordingSeconds] = useState(0);
  const [screenRecordingNotice, setScreenRecordingNotice] = useState(null);

  const [
    pipOpen,
    setPipOpen
  ] = useState(false);

  // =========================================================
  // CHAT STATE
  // =========================================================

  const [
    chatOpen,
    setChatOpen
  ] = useState(false);

  const [
    chatMessages,
    setChatMessages
  ] = useState([]);

  const [
    chatMessage,
    setChatMessage
  ] = useState("");

  const [
    unreadMessages,
    setUnreadMessages
  ] = useState(0);

  const [
    sendingMessage,
    setSendingMessage
  ] = useState(false);

  const [
    clearingChat,
    setClearingChat
  ] = useState(false);

  const typingParticipants = remoteParticipants.filter(
    (participant) => participant.chatTyping === true
  );

  const typingParticipantNames = typingParticipants.map(
    (participant) => participant.name || "Participant"
  );

  const typingIndicatorText =
    typingParticipantNames.length === 1
      ? `${typingParticipantNames[0]} is typing…`
      : typingParticipantNames.length === 2
        ? `${typingParticipantNames[0]} and ${typingParticipantNames[1]} are typing…`
        : typingParticipantNames.length > 2
          ? `${typingParticipantNames.slice(0, -1).join(", ")} and ${typingParticipantNames.at(-1)} are typing…`
          : "";

  const raisedHandParticipants = remoteParticipants.filter(
    (participant) => participant.handRaised === true
  );

  // =========================================================
  // STATE REF SYNCHRONISATION
  // =========================================================

  useEffect(() => {
    const track = getCameraVideoTrack();
    if (track) {
      cameraSendersRef.current.forEach((sender) => sender.replaceTrack(track).catch(() => {}));
    }
  }, [backgroundEffect, selectedCameraId, cameraStarted, hasJoinedLesson]);

  useEffect(() => {
    screenFrozenStateRef.current =
      isScreenFrozen;
  }, [isScreenFrozen]);

  useEffect(() => {
    micEnabledStateRef.current =
      micEnabled;
  }, [micEnabled]);

  useEffect(() => {
    screenSharingStateRef.current =
      isScreenSharing;
  }, [isScreenSharing]);

  useEffect(() => {
    backgroundEffectRef.current =
      backgroundEffect;
  }, [backgroundEffect]);

  useEffect(() => () => {
    if (screenRecordingNoticeTimerRef.current) {
      window.clearTimeout(screenRecordingNoticeTimerRef.current);
    }
    if (screenRecordingNoticeRemoveTimerRef.current) {
      window.clearTimeout(screenRecordingNoticeRemoveTimerRef.current);
    }
    clearScreenRecordingTimer();
    whiteboardOpenRetryTimersRef.current.forEach((timer) =>
      window.clearTimeout(timer)
    );
    whiteboardOpenRetryTimersRef.current = [];
    whiteboardSnapshotTransfersRef.current.clear();
  }, []);

  useEffect(() => {
    chatOpenRef.current =
      chatOpen;

    if (chatOpen) {
      setUnreadMessages(0);
    }
  }, [chatOpen]);

  useEffect(() => {
    whiteboardOpenRef.current =
      whiteboardOpen;
  }, [whiteboardOpen]);

  useEffect(() => {
    if (!roomId || !hasJoinedLesson || isElectronDesktop) {
      return undefined;
    }

    const listenerStartedAt = Date.now();
    const unsubscribe = onSnapshot(
      getRoomRef(),
      (snapshot) => {
        const signal = snapshot.data()?.whiteboardSignal;
        if (
          !signal ||
          signal.senderId === participantIdRef.current ||
          !Number.isFinite(signal.issuedAt) ||
          signal.issuedAt < listenerStartedAt - 5000
        ) {
          return;
        }

        whiteboardOpenRef.current = signal.open === true;
        setWhiteboardOpen(signal.open === true);
      },
      (error) => {
        console.warn("Could not listen for whiteboard activation:", error);
      }
    );

    return unsubscribe;
  }, [roomId, hasJoinedLesson, isElectronDesktop]);

  // =========================================================
  // WEBRTC CONFIG
  // =========================================================

  const rtcConfiguration = {
    iceServers: [
      {
        urls:
          "stun:stun.l.google.com:19302"
      }
    ]
  };

  // =========================================================
  // PRESENCE SETTINGS
  // =========================================================

  const HEARTBEAT_MS = 10000;
  const STALE_AFTER_MS = 35000;

  // =========================================================
  // PARTICIPANT ID
  // =========================================================

  const createParticipantId = () => {
    if (
      window.crypto &&
      window.crypto.randomUUID
    ) {
      return window.crypto.randomUUID();
    }

    return (
      Date.now().toString(36) +
      "-" +
      Math.random()
        .toString(36)
        .slice(2)
    );
  };

  // =========================================================
  // FIRESTORE REFS
  // =========================================================

  const getRoomRef = () =>
    doc(
      db,
      "videoRooms",
      roomId
    );

  const getParticipantsCollection = () =>
    collection(
      getRoomRef(),
      "participants"
    );

  const getMessagesCollection = () =>
    collection(
      getRoomRef(),
      "messages"
    );

  const getWhiteboardStateRef = () =>
    doc(
      getRoomRef(),
      "whiteboardState",
      "current"
    );

  const getWhiteboardChunksCollection = () =>
    collection(
      getWhiteboardStateRef(),
      "chunks"
    );

  const getPairId = (
    firstId,
    secondId
  ) =>
    [
      firstId,
      secondId
    ]
      .sort()
      .join("__");

  // =========================================================
  // REMOTE PARTICIPANT STATE
  // =========================================================

  const updateRemoteParticipant = (
    participantId,
    updates
  ) => {
    setRemoteParticipants(
      (current) => {
        const existing =
          current.find(
            (person) =>
              person.id ===
              participantId
          );

        if (existing) {
          return current.map(
            (person) =>
              person.id ===
              participantId
                ? {
                    ...person,
                    ...updates
                  }
                : person
          );
        }

        return [
          ...current,
          {
            id: participantId,
            name:
              updates.name ||
              "Participant",
            connected: false,
            ...updates
          }
        ];
      }
    );
  };

  const removeRemoteParticipant = (
    participantId
  ) => {
    setRemoteParticipants(
      (current) =>
        current.filter(
          (person) =>
            person.id !==
            participantId
        )
    );
  };

  // =========================================================
  // CAMERA BACKGROUND HELPERS
  // =========================================================

  const getCameraPreviewStream = () => {
    if (
      backgroundEffectRef.current !==
        "none" &&
      processedCameraStreamRef.current
    ) {
      return processedCameraStreamRef.current;
    }

    return localStreamRef.current;
  };

  const getCameraVideoTrack = () => {
    if (
      backgroundEffectRef.current !==
        "none" &&
      processedCameraStreamRef.current
    ) {
      const processedTrack =
        processedCameraStreamRef.current
          .getVideoTracks()[0];

      if (processedTrack) {
        return processedTrack;
      }
    }

    return (
      localStreamRef.current
        ?.getVideoTracks()[0] ||
      null
    );
  };

  const showCurrentLocalCamera = () => {
    if (
      !localVideoRef.current ||
      screenSharingStateRef.current
    ) {
      return;
    }

    const stream =
      getCameraPreviewStream();

    if (!stream) {
      return;
    }

    if (
      localVideoRef.current.srcObject !==
      stream
    ) {
      localVideoRef.current.srcObject =
        stream;
    }

    localVideoRef.current
      .play()
      .catch(() => {});
  };

  const stopBackgroundProcessing = () => {
    if (
      processedCameraAnimationRef.current
    ) {
      cancelAnimationFrame(
        processedCameraAnimationRef.current
      );

      processedCameraAnimationRef.current =
        null;
    }

    backgroundProcessingBusyRef.current =
      false;

    backgroundMaskReadyRef.current =
      false;

    backgroundLastSegmentationMsRef.current =
      0;

    if (
      processedCameraVideoRef.current
    ) {
      try {
        processedCameraVideoRef.current.pause();
        processedCameraVideoRef.current.srcObject =
          null;
      } catch {
        // Ignore.
      }

      processedCameraVideoRef.current =
        null;
    }

    if (
      processedCameraStreamRef.current
    ) {
      processedCameraStreamRef.current
        .getTracks()
        .forEach(
          (track) =>
            track.stop()
        );

      processedCameraStreamRef.current =
        null;
    }

    processedCameraCanvasRef.current =
      null;

    processedCameraForegroundCanvasRef.current =
      null;

    processedCameraMaskCanvasRef.current =
      null;

    processedCameraSourceTrackIdRef.current =
      null;
  };

  const closeBackgroundSegmenter = () => {
    const segmenter =
      backgroundSegmenterRef.current;

    backgroundSegmenterRef.current =
      null;

    backgroundSegmenterPromiseRef.current =
      null;

    if (
      segmenter &&
      typeof segmenter.close ===
        "function"
    ) {
      try {
        segmenter.close();
      } catch {
        // Ignore.
      }
    }
  };

  const ensureBackgroundSegmenter =
    async () => {
      if (
        backgroundSegmenterRef.current
      ) {
        return backgroundSegmenterRef.current;
      }

      if (
        backgroundSegmenterPromiseRef.current
      ) {
        return backgroundSegmenterPromiseRef.current;
      }

      const createSegmenter =
        async () => {
          // MediaPipe is a large optional WebAssembly dependency. Loading it
          // at module startup can prevent the entire lesson page rendering on
          // older iPad/Safari versions. Keep the core call independent and
          // download MediaPipe only if a background effect is actually chosen.
          const {
            FilesetResolver,
            ImageSegmenter
          } = await import(
            "@mediapipe/tasks-vision"
          );

          const vision =
            await FilesetResolver.forVisionTasks(
              "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@1.0.1/wasm"
            );

          const options = {
            baseOptions: {
              modelAssetPath:
                "https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter_landscape/float16/latest/selfie_segmenter_landscape.tflite",
              delegate: "GPU"
            },
            runningMode: "VIDEO",
            outputCategoryMask: false,
            outputConfidenceMasks: true
          };

          try {
            return await ImageSegmenter.createFromOptions(
              vision,
              options
            );
          } catch (gpuError) {
            console.warn(
              "GPU background segmentation could not start. Falling back to CPU:",
              gpuError
            );

            return ImageSegmenter.createFromOptions(
              vision,
              {
                ...options,
                baseOptions: {
                  ...options.baseOptions,
                  delegate: "CPU"
                }
              }
            );
          }
        };

      const promise =
        createSegmenter();

      backgroundSegmenterPromiseRef.current =
        promise;

      try {
        const segmenter =
          await promise;

        backgroundSegmenterRef.current =
          segmenter;

        return segmenter;
      } finally {
        backgroundSegmenterPromiseRef.current =
          null;
      }
    };

  const waitForCameraVideoReady =
    (
      video,
      timeoutMs = 5000
    ) =>
      new Promise(
        (resolve, reject) => {
          if (
            video.readyState >= 2 &&
            video.videoWidth > 0 &&
            video.videoHeight > 0
          ) {
            resolve();
            return;
          }

          let finished = false;

          const timeout =
            setTimeout(
              () => {
                if (finished) {
                  return;
                }

                finished = true;

                reject(
                  new Error(
                    "Timed out waiting for the camera preview."
                  )
                );
              },
              timeoutMs
            );

          const finish = () => {
            if (finished) {
              return;
            }

            if (
              video.videoWidth <= 0 ||
              video.videoHeight <= 0
            ) {
              return;
            }

            finished = true;

            clearTimeout(timeout);

            video.removeEventListener(
              "loadeddata",
              finish
            );

            video.removeEventListener(
              "loadedmetadata",
              finish
            );

            resolve();
          };

          video.addEventListener(
            "loadeddata",
            finish
          );

          video.addEventListener(
            "loadedmetadata",
            finish
          );
        }
      );

  const updatePersonMaskCanvas = (
    result,
    outputWidth,
    outputHeight
  ) => {
    const confidenceMasks =
      result?.confidenceMasks;

    if (
      !confidenceMasks ||
      confidenceMasks.length === 0
    ) {
      return false;
    }

    let personIndex = -1;

    try {
      const labels =
        backgroundSegmenterRef.current
          ?.getLabels?.() ||
        [];

      personIndex =
        labels.findIndex(
          (label) =>
            String(label)
              .toLowerCase()
              .includes(
                "person"
              )
        );
    } catch {
      personIndex = -1;
    }

    if (
      personIndex < 0 ||
      personIndex >=
        confidenceMasks.length
    ) {
      personIndex =
        confidenceMasks.length > 1
          ? 1
          : 0;
    }

    const personMask =
      confidenceMasks[
        personIndex
      ];

    if (!personMask) {
      return false;
    }

    const maskValues =
      personMask.getAsFloat32Array();

    if (
      !maskValues ||
      maskValues.length === 0
    ) {
      return false;
    }

    let maskWidth =
      Number(
        personMask.width
      ) || 0;

    let maskHeight =
      Number(
        personMask.height
      ) || 0;

    if (
      maskWidth <= 0 ||
      maskHeight <= 0 ||
      maskWidth *
        maskHeight !==
        maskValues.length
    ) {
      if (
        outputWidth *
          outputHeight ===
        maskValues.length
      ) {
        maskWidth =
          outputWidth;

        maskHeight =
          outputHeight;
      } else {
        const landscapeRatio =
          16 / 9;

        maskHeight =
          Math.max(
            1,
            Math.round(
              Math.sqrt(
                maskValues.length /
                  landscapeRatio
              )
            )
          );

        maskWidth =
          Math.max(
            1,
            Math.round(
              maskValues.length /
                maskHeight
            )
          );

        if (
          maskWidth *
            maskHeight !==
          maskValues.length
        ) {
          maskWidth =
            maskValues.length;

          maskHeight =
            1;
        }
      }
    }

    let maskCanvas =
      processedCameraMaskCanvasRef.current;

    if (!maskCanvas) {
      maskCanvas =
        document.createElement(
          "canvas"
        );

      processedCameraMaskCanvasRef.current =
        maskCanvas;
    }

    if (
      maskCanvas.width !==
        maskWidth ||
      maskCanvas.height !==
        maskHeight
    ) {
      maskCanvas.width =
        maskWidth;

      maskCanvas.height =
        maskHeight;
    }

    const maskContext =
      maskCanvas.getContext(
        "2d",
        {
          alpha: true,
          willReadFrequently: true
        }
      );

    if (!maskContext) {
      return false;
    }

    const imageData =
      maskContext.createImageData(
        maskWidth,
        maskHeight
      );

    const pixels =
      imageData.data;

    for (
      let i = 0;
      i < maskValues.length;
      i++
    ) {
      const confidence =
        Math.max(
          0,
          Math.min(
            1,
            maskValues[i]
          )
        );

      // Smooth the edge instead of using a hard cut-out.
      const softened =
        Math.max(
          0,
          Math.min(
            1,
            (
              confidence -
              0.12
            ) / 0.76
          )
        );

      const smooth =
        softened *
        softened *
        (
          3 -
          2 * softened
        );

      const index =
        i * 4;

      pixels[index] = 255;
      pixels[index + 1] = 255;
      pixels[index + 2] = 255;
      pixels[index + 3] =
        Math.round(
          smooth * 255
        );
    }

    maskContext.putImageData(
      imageData,
      0,
      0
    );

    backgroundMaskReadyRef.current =
      true;

    return true;
  };

  const closeSegmentationResultMasks =
    (result) => {
      try {
        result?.confidenceMasks?.forEach(
          (mask) => {
            try {
              mask?.close?.();
            } catch {
              // Ignore.
            }
          }
        );

        try {
          result?.categoryMask?.close?.();
        } catch {
          // Ignore.
        }
      } catch {
        // Ignore.
      }
    };

  const drawProcessedCameraFrame = (
    video,
    canvas,
    foregroundCanvas
  ) => {
    if (
      video.readyState < 2 ||
      video.videoWidth <= 0 ||
      video.videoHeight <= 0
    ) {
      return;
    }

    const context =
      canvas.getContext(
        "2d",
        {
          alpha: false
        }
      );

    const foregroundContext =
      foregroundCanvas.getContext(
        "2d",
        {
          alpha: true
        }
      );

    if (
      !context ||
      !foregroundContext
    ) {
      return;
    }

    const width =
      canvas.width;

    const height =
      canvas.height;

    const effect =
      backgroundEffectRef.current;

    if (
      effect === "none" ||
      !backgroundMaskReadyRef.current ||
      !processedCameraMaskCanvasRef.current
    ) {
      context.filter =
        "none";

      context.globalCompositeOperation =
        "source-over";

      context.drawImage(
        video,
        0,
        0,
        width,
        height
      );

      return;
    }

    if (backgroundImageRef.current && !['blur', 'strong-blur'].includes(effect)) {
      const image = backgroundImageRef.current;
      const scale = Math.max(width / image.width, height / image.height);
      context.filter = 'none';
      context.globalCompositeOperation = 'source-over';
      context.drawImage(image, (width-image.width*scale)/2, (height-image.height*scale)/2, image.width*scale, image.height*scale);
    } else {
    const blurPixels =
      effect ===
        "strong-blur"
        ? 20
        : 7;

    // Draw a slightly oversized blurred frame. The overscan
    // prevents dark edges caused by the blur kernel sampling
    // outside the canvas.
    const overscan =
      Math.max(
        8,
        Math.ceil(
          blurPixels * 1.25
        )
      );

    context.save();

    context.globalCompositeOperation =
      "source-over";

    context.filter =
      `blur(${blurPixels}px)`;

    context.drawImage(
      video,
      -overscan,
      -overscan,
      width +
        overscan * 2,
      height +
        overscan * 2
    );

    context.restore();

    }

    foregroundContext.clearRect(
      0,
      0,
      width,
      height
    );

    foregroundContext.globalCompositeOperation =
      "source-over";

    foregroundContext.filter =
      backgroundImageRef.current && !["blur", "strong-blur"].includes(effect) ? `brightness(${cameraBrightnessRef.current / 100})` : "none";

    foregroundContext.drawImage(
      video,
      0,
      0,
      width,
      height
    );

    // Keep the segmentation mask untouched by the brightness adjustment.
    foregroundContext.filter = "none";
    foregroundContext.globalCompositeOperation =
      "destination-in";

    foregroundContext.drawImage(
      processedCameraMaskCanvasRef.current,
      0,
      0,
      width,
      height
    );

    foregroundContext.globalCompositeOperation =
      "source-over";

    context.filter =
      "none";

    context.globalCompositeOperation =
      "source-over";

    const exitProgress = smokeExitStartedRef.current === null ? 0 : Math.min(1, (performance.now() - smokeExitStartedRef.current) / SMOKE_EXIT_DURATION_MS);
    context.save();
    // The person fades while the smoke builds, leaving only the chosen backdrop.
    context.globalAlpha = smokeExitStartedRef.current === null ? 1 : 1 - Math.min(1, Math.max(0, (exitProgress - 0.08) / 0.44));
    context.drawImage(foregroundCanvas, 0, 0);
    context.restore();
    if (smokeExitStartedRef.current !== null) drawExitSmoke(context, width, height, exitProgress);

  };

  const startBackgroundProcessing =
    async () => {
      const rawTrack =
        localStreamRef.current
          ?.getVideoTracks()[0];

      if (!rawTrack) {
        throw new Error(
          "The camera is not available."
        );
      }

      if (
        backgroundEffectRef.current ===
        "none"
      ) {
        return null;
      }

      if (
        processedCameraStreamRef.current &&
        processedCameraSourceTrackIdRef.current ===
          rawTrack.id
      ) {
        return processedCameraStreamRef.current;
      }

      stopBackgroundProcessing();

      setBackgroundEffectLoading(
        true
      );

      setBackgroundEffectError("");

      try {
        const segmenter =
          await ensureBackgroundSegmenter();

        const sourceVideo =
          document.createElement(
            "video"
          );

        sourceVideo.autoplay =
          true;

        sourceVideo.muted =
          true;

        sourceVideo.playsInline =
          true;

        sourceVideo.srcObject =
          new MediaStream(
            [rawTrack]
          );

        processedCameraVideoRef.current =
          sourceVideo;

        await sourceVideo.play();

        await waitForCameraVideoReady(
          sourceVideo
        );

        const settings =
          rawTrack.getSettings();

        const width =
          sourceVideo.videoWidth ||
          settings.width ||
          1280;

        const height =
          sourceVideo.videoHeight ||
          settings.height ||
          720;

        const canvas =
          document.createElement(
            "canvas"
          );

        canvas.width =
          width;

        canvas.height =
          height;

        processedCameraCanvasRef.current =
          canvas;

        const foregroundCanvas =
          document.createElement(
            "canvas"
          );

        foregroundCanvas.width =
          width;

        foregroundCanvas.height =
          height;

        processedCameraForegroundCanvasRef.current =
          foregroundCanvas;

        const processedStream =
          canvas.captureStream(
            24
          );

        processedCameraStreamRef.current =
          processedStream;

        processedCameraSourceTrackIdRef.current =
          rawTrack.id;

        const processedTrack =
          processedStream
            .getVideoTracks()[0];

        if (!processedTrack) {
          throw new Error(
            "Could not create the processed camera track."
          );
        }

        processedTrack.enabled =
          rawTrack.enabled;

        backgroundMaskReadyRef.current =
          false;

        backgroundLastSegmentationMsRef.current =
          0;

        const loop = (
          timestamp
        ) => {
          if (
            processedCameraStreamRef.current !==
              processedStream ||
            processedCameraVideoRef.current !==
              sourceVideo
          ) {
            return;
          }

          drawProcessedCameraFrame(
            sourceVideo,
            canvas,
            foregroundCanvas
          );

          if (
            backgroundEffectRef.current !==
              "none" &&
            // Keep the person mask live while screen sharing, too.
            !backgroundProcessingBusyRef.current &&
            sourceVideo.readyState >= 2 &&
            timestamp -
              backgroundLastSegmentationMsRef.current >=
              70
          ) {
            backgroundProcessingBusyRef.current =
              true;

            backgroundLastSegmentationMsRef.current =
              timestamp;

            Promise.resolve()
              .then(
                () =>
                  segmenter.segmentForVideo(
                    sourceVideo,
                    timestamp
                  )
              )
              .then(
                (result) => {
                  try {
                    updatePersonMaskCanvas(
                      result,
                      width,
                      height
                    );
                  } finally {
                    closeSegmentationResultMasks(
                      result
                    );
                  }
                }
              )
              .catch(
                (segmentationError) => {
                  console.warn(
                    "Background segmentation frame failed:",
                    segmentationError
                  );
                }
              )
              .finally(
                () => {
                  backgroundProcessingBusyRef.current =
                    false;
                }
              );
          }

          processedCameraAnimationRef.current =
            requestAnimationFrame(
              loop
            );
        };

        processedCameraAnimationRef.current =
          requestAnimationFrame(
            loop
          );

        return processedStream;
      } catch (err) {
        stopBackgroundProcessing();

        throw err;
      } finally {
        setBackgroundEffectLoading(
          false
        );
      }
    };

  const selectCameraBackground = async (effect) => {
    if (backgroundSelectionBusyRef.current || backgroundEffectLoading) return;
    backgroundSelectionBusyRef.current = true;
    setBackgroundImageLoading(true);
    setBackgroundEffectError('');
    try {
      const preset = HALLOWEEN_BACKGROUNDS.find(item => item.value === effect);
      if (preset) {
        const image = new Image();
        image.src = preset.src;
        await image.decode();
        const canvas = document.createElement('canvas');
        const scale = Math.min(1, 1920 / Math.max(image.naturalWidth, image.naturalHeight));
        canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
        canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
        backgroundImageRef.current = canvas;
      }
      await changeBackgroundEffect(effect);
    } catch (err) {
      setBackgroundEffectError('Could not load background: ' + err.message);
    } finally {
      backgroundSelectionBusyRef.current = false;
      setBackgroundImageLoading(false);
    }
  };

  const changeBackgroundEffect =
    async (effect) => {
      if (
        ![
          "none",
          "blur",
          "strong-blur", ...HALLOWEEN_BACKGROUNDS.map(item => item.value)
        ].includes(effect)
      ) {
        return;
      }

      backgroundEffectRef.current =
        effect;

      setBackgroundEffect(
        effect
      );

      setBackgroundEffectError("");

      if (
        !localStreamRef.current
      ) {
        return;
      }

      try {
        if (
          effect === "none"
        ) {
          stopBackgroundProcessing();

          const rawTrack =
            localStreamRef.current
              ?.getVideoTracks()[0];

          if (
            hasJoinedLesson &&
            !screenSharingStateRef.current &&
            rawTrack
          ) {
            await replaceVideoTrackForAll(
              rawTrack
            );
          }

          showCurrentLocalCamera();

          return;
        }

        const processedStream =
          await startBackgroundProcessing();

        const processedTrack =
          processedStream
            ?.getVideoTracks()[0];

        if (
          hasJoinedLesson &&
          !screenSharingStateRef.current &&
          processedTrack
        ) {
          await replaceVideoTrackForAll(
            processedTrack
          );
        }

        showCurrentLocalCamera();
      } catch (err) {
        console.error(
          "Background effect error:",
          err
        );

        backgroundEffectRef.current =
          "none";

        setBackgroundEffect(
          "none"
        );

        stopBackgroundProcessing();

        setBackgroundEffectError(
          `Camera background could not be started: ${err.message}`
        );

        if (
          hasJoinedLesson &&
          !screenSharingStateRef.current
        ) {
          const rawTrack =
            localStreamRef.current
              ?.getVideoTracks()[0];

          if (rawTrack) {
            await replaceVideoTrackForAll(
              rawTrack
            );
          }
        }

        showCurrentLocalCamera();
      }
    };

  // =========================================================
  // START CAMERA + MICROPHONE
  // =========================================================

  const startPreview = async () => {
    try {
      setError("");

      const stream =
        await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: getVoiceAudioConstraints()
        });

      localStreamRef.current =
        stream;

      await startVoiceProcessing(stream);

      setSelectedCameraId(
        stream.getVideoTracks()[0]?.getSettings().deviceId || ""
      );
      setSelectedMicrophoneId(
        stream.getAudioTracks()[0]?.getSettings().deviceId || ""
      );
      await refreshMediaDevices();

      setCameraEnabled(true);
      setMicEnabled(true);

      if (
        backgroundEffectRef.current !==
        "none"
      ) {
        try {
          await startBackgroundProcessing();
        } catch (backgroundError) {
          console.error(
            "Could not start selected background effect:",
            backgroundError
          );

          backgroundEffectRef.current =
            "none";

          setBackgroundEffect(
            "none"
          );

          setBackgroundEffectError(
            `Background blur could not be started: ${backgroundError.message}`
          );
        }
      }

      setCameraStarted(true);
    } catch (err) {
      console.error(
        "Camera error:",
        err
      );

      setError(
        `Camera or microphone could not be started: ${err.message}`
      );
    }
  };

  // =========================================================
  // LOCAL VIDEO / SCREEN SHARE PREVIEW
  //
  // IMPORTANT CHANGE:
  //
  // Normal lesson:
  //   local tile shows webcam
  //
  // Screen sharing:
  //   local tile shows shared screen
  //
  // When sharing ends:
  //   local tile automatically returns to webcam
  // =========================================================

  useEffect(() => {
  if (!localVideoRef.current) {
    return;
  }

  let streamToShow = null;

  // Screen sharing AND frozen:
  // show exactly the same frozen frame
  // that the participants are receiving.
  if (
    isScreenSharing &&
    isScreenFrozen &&
    frozenScreenStreamRef.current
  ) {
    streamToShow =
      frozenScreenStreamRef.current;
  }

  // Screen sharing normally:
  // show the live shared screen.
  else if (
    isScreenSharing &&
    screenStreamRef.current
  ) {
    streamToShow =
      screenStreamRef.current;
  }

  // Not screen sharing:
  // show webcam.
  else if (
    cameraStarted &&
    localStreamRef.current
  ) {
    streamToShow =
      getCameraPreviewStream();
  }

  if (streamToShow) {
    localVideoRef.current.srcObject =
      streamToShow;

    localVideoRef.current
      .play()
      .catch(() => {});
  }
}, [
  cameraStarted,
  hasJoinedLesson,
  isScreenSharing,
  isScreenFrozen,
  backgroundEffect
]);

  // =========================================================
  // ATTACH / RE-ATTACH LOCAL VIDEO ELEMENT
  // =========================================================
  //
  // IMPORTANT:
  // React swaps the normal lesson layout for a separate
  // presentation layout when another participant shares their
  // screen. That means the <video> element using localVideoRef
  // is unmounted and a brand-new <video> element is mounted.
  //
  // A normal object ref does not cause the media-attachment
  // effect above to run when that DOM element changes. The new
  // element therefore used to have no srcObject, which is why
  // the student saw a black/blank self-camera tile while the
  // tutor could still see the student's camera perfectly.
  //
  // Using this callback ref means EVERY newly-mounted local
  // video element is immediately connected to the correct
  // stream.

  const attachLocalVideo = (element) => {
    localVideoRef.current = element;

    if (!element) {
      return;
    }

    let streamToShow = null;

    if (
      screenSharingStateRef.current &&
      screenFrozenStateRef.current &&
      frozenScreenStreamRef.current
    ) {
      streamToShow =
        frozenScreenStreamRef.current;
    } else if (
      screenSharingStateRef.current &&
      screenStreamRef.current
    ) {
      streamToShow =
        screenStreamRef.current;
    } else if (
      localStreamRef.current
    ) {
      streamToShow =
        getCameraPreviewStream();
    }

    if (!streamToShow) {
      element.srcObject = null;
      return;
    }

    if (
      element.srcObject !==
      streamToShow
    ) {
      element.srcObject =
        streamToShow;
    }

    element
      .play()
      .catch(() => {});
  };

  // =========================================================
  // ATTACH REMOTE VIDEO
  // =========================================================

  const attachRemoteVideo = (
  participantId,
  element
) => {
  if (!element) {
    return;
  }

  const stream =
    remoteStreamsRef.current.get(
      participantId
    );

  if (!stream) {
    return;
  }

  // IMPORTANT:
  // Do not repeatedly assign the same MediaStream.
  // Reassigning srcObject can restart Chromium's video
  // renderer and cause a brief black flash.
  if (
    element.srcObject !==
    stream
  ) {
    element.srcObject =
      stream;
  }

  // Only call play if playback is not already running.
  if (element.paused) {
    element
      .play()
      .catch(() => {});
  }
};

  // =========================================================
  // CURRENT OUTGOING VIDEO
  // =========================================================

  const getCurrentOutgoingVideoTrack = () => {
    if (
      screenFrozenStateRef.current &&
      frozenScreenStreamRef.current
    ) {
      const frozenTrack =
        frozenScreenStreamRef.current
          .getVideoTracks()[0];

      if (frozenTrack) {
        return frozenTrack;
      }
    }

    if (
      screenSharingStateRef.current &&
      screenStreamRef.current
    ) {
      const screenTrack =
        screenStreamRef.current
          .getVideoTracks()[0];

      if (screenTrack) {
        return screenTrack;
      }
    }

    return getCameraVideoTrack();
  };

  // =========================================================
  // CURRENT OUTGOING AUDIO
  // =========================================================

  const getCurrentOutgoingAudioTrack = () => {
    const mixedTrack =
      mixedAudioStreamRef.current
        ?.getAudioTracks()[0];

    if (
      screenSharingStateRef.current &&
      mixedTrack
    ) {
      return mixedTrack;
    }

    return (
      voiceProcessedStreamRef.current
        ?.getAudioTracks()[0] ||
      localStreamRef.current
        ?.getAudioTracks()[0] ||
      null
    );
  };

  const cleanupVoiceProcessing = () => {
    const processedStream = voiceProcessedStreamRef.current;
    voiceProcessedStreamRef.current = null;
    processedStream?.getTracks().forEach((track) => track.stop());

    if (voiceProcessorRef.current) {
      voiceProcessorRef.current.onaudioprocess = null;
      try { voiceProcessorRef.current.disconnect(); } catch {}
      voiceProcessorRef.current = null;
    }
    if (voiceSourceRef.current) {
      try { voiceSourceRef.current.disconnect(); } catch {}
      voiceSourceRef.current = null;
    }

    const context = voiceAudioContextRef.current;
    voiceAudioContextRef.current = null;
    if (context && context.state !== "closed") {
      context.close().catch(() => {});
    }
  };

  const startVoiceProcessing = async (stream) => {
    cleanupVoiceProcessing();
    const microphoneTrack = stream?.getAudioTracks()[0];
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!microphoneTrack || !AudioContextClass) return;

    try {
      const context = new AudioContextClass({ sampleRate: 48000 });
      const source = context.createMediaStreamSource(
        new MediaStream([microphoneTrack])
      );
      const highPass = context.createBiquadFilter();
      highPass.type = "highpass";
      highPass.frequency.value = 70;
      highPass.Q.value = 0.7;

      const lowPass = context.createBiquadFilter();
      lowPass.type = "lowpass";
      lowPass.frequency.value = 14000;
      lowPass.Q.value = 0.7;

      const presence = context.createBiquadFilter();
      presence.type = "peaking";
      presence.frequency.value = 3200;
      presence.Q.value = 0.8;
      presence.gain.value = 1.5;

      const compressor = context.createDynamicsCompressor();
      compressor.threshold.value = -18;
      compressor.knee.value = 18;
      compressor.ratio.value = 2;
      compressor.attack.value = 0.01;
      compressor.release.value = 0.25;

      const destination = context.createMediaStreamDestination();
      source.connect(highPass);
      highPass.connect(lowPass);
      lowPass.connect(presence);
      presence.connect(compressor);
      compressor.connect(destination);
      if (context.state === "suspended") await context.resume();

      const processedTrack = destination.stream.getAudioTracks()[0];
      if (!processedTrack) throw new Error("No processed microphone track");
      processedTrack.enabled = microphoneTrack.enabled;
      voiceAudioContextRef.current = context;
      voiceSourceRef.current = source;
      voiceProcessorRef.current = null;
      voiceProcessedStreamRef.current = destination.stream;
    } catch (processingError) {
      console.warn("Could not enable enhanced microphone filtering:", processingError);
      cleanupVoiceProcessing();
    }
  };

  const refreshMediaDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      setMediaDevices(devices);
      return devices;
    } catch (deviceError) {
      console.warn("Could not list cameras and microphones:", deviceError);
      return [];
    }
  };

  const cleanupMicrophoneTest = () => {
    if (microphoneTestTimerRef.current) {
      window.clearInterval(microphoneTestTimerRef.current);
      microphoneTestTimerRef.current = null;
    }
    if (microphoneTestStopTimerRef.current) {
      window.clearTimeout(microphoneTestStopTimerRef.current);
      microphoneTestStopTimerRef.current = null;
    }
    const recorder = microphoneTestRecorderRef.current;
    microphoneTestRecorderRef.current = null;
    if (recorder && recorder.state !== "inactive") {
      try { recorder.stop(); } catch {}
    }
    if (microphoneTestTrackRef.current) {
      microphoneTestTrackRef.current.stop();
      microphoneTestTrackRef.current = null;
    }
    if (microphoneTestAudioRef.current) {
      microphoneTestAudioRef.current.pause();
      microphoneTestAudioRef.current.removeAttribute("src");
    }
    if (microphoneTestUrlRef.current) {
      URL.revokeObjectURL(microphoneTestUrlRef.current);
      microphoneTestUrlRef.current = null;
    }
  };

  const switchCameraDevice = async (deviceId) => {
    if (!deviceId || switchingDevice) return;
    setSwitchingDevice(true);
    setError("");
    try {
      const replacement = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: deviceId } },
        audio: false
      });
      const nextTrack = replacement.getVideoTracks()[0];
      const currentStream = localStreamRef.current;
      const previousTrack = currentStream?.getVideoTracks()[0];
      nextTrack.enabled = cameraEnabled;
      if (currentStream) {
        if (previousTrack) currentStream.removeTrack(previousTrack);
        currentStream.addTrack(nextTrack);
      }
      if (hasJoinedLesson && !screenSharingStateRef.current) await replaceVideoTrackForAll(nextTrack);
      if (hasJoinedLesson) await Promise.allSettled([...cameraSendersRef.current.values()].map((sender) => sender.replaceTrack(nextTrack)));
      previousTrack?.stop();
      setSelectedCameraId(nextTrack.getSettings().deviceId || deviceId);

      if (backgroundEffectRef.current !== "none") {
        stopBackgroundProcessing();
        await startBackgroundProcessing();
      }
      showCurrentLocalCamera();
      await refreshMediaDevices();
    } catch (deviceError) {
      setError(`Could not switch camera: ${deviceError.message}`);
    } finally {
      setSwitchingDevice(false);
    }
  };

  const switchMicrophoneDevice = async (deviceId) => {
    if (!deviceId || switchingDevice) return;
    setSwitchingDevice(true);
    setError("");
    cleanupMicrophoneTest();
    setMicrophoneTestStatus("idle");
    setMicrophoneTestMessage("");
    try {
      const replacement = await navigator.mediaDevices.getUserMedia({
        video: false,
        audio: {
          ...getVoiceAudioConstraints(),
          deviceId: { exact: deviceId }
        }
      });
      const nextTrack = replacement.getAudioTracks()[0];
      const currentStream = localStreamRef.current;
      const previousTrack = currentStream?.getAudioTracks()[0];
      nextTrack.enabled = micEnabled;
      if (currentStream) {
        if (previousTrack) currentStream.removeTrack(previousTrack);
        currentStream.addTrack(nextTrack);
      }
      await startVoiceProcessing(new MediaStream([nextTrack]));
      if (!micEnabled) {
        await startMutedSpeechMonitoring(nextTrack);
      }
      const outgoingTrack = getCurrentOutgoingAudioTrack();
      if (hasJoinedLesson && outgoingTrack) {
        await replaceAudioTrackForAll(outgoingTrack);
      }
      previousTrack?.stop();
      setSelectedMicrophoneId(nextTrack.getSettings().deviceId || deviceId);
      await refreshMediaDevices();
    } catch (deviceError) {
      setError(`Could not switch microphone: ${deviceError.message}`);
    } finally {
      setSwitchingDevice(false);
    }
  };

  const testMicrophone = () => {
    if (microphoneTestStatus === "recording") return;
    const sourceTrack = getCurrentOutgoingAudioTrack();
    if (!sourceTrack || !window.MediaRecorder) {
      setMicrophoneTestMessage("Microphone testing is not supported here.");
      return;
    }

    cleanupMicrophoneTest();
    if (microphoneTestUrlRef.current) {
      URL.revokeObjectURL(microphoneTestUrlRef.current);
      microphoneTestUrlRef.current = null;
    }

    const testTrack = sourceTrack.clone();
    testTrack.enabled = true;
    microphoneTestTrackRef.current = testTrack;
    const recorder = new MediaRecorder(new MediaStream([testTrack]), {
      audioBitsPerSecond: 128000
    });
    const chunks = [];
    microphoneTestRecorderRef.current = recorder;
    setMicrophoneTestSeconds(5);
    setMicrophoneTestStatus("recording");
    setMicrophoneTestMessage("Speak normally now…");

    recorder.ondataavailable = (event) => {
      if (event.data?.size) chunks.push(event.data);
    };
    recorder.onstop = () => {
      microphoneTestRecorderRef.current = null;
      testTrack.stop();
      microphoneTestTrackRef.current = null;
      const blob = new Blob(chunks, {
        type: recorder.mimeType || "audio/webm"
      });
      const url = URL.createObjectURL(blob);
      microphoneTestUrlRef.current = url;
      const audio = microphoneTestAudioRef.current;
      if (audio) {
        audio.src = url;
        audio.currentTime = 0;
        setMicrophoneTestStatus("playing");
        setMicrophoneTestMessage("Playing your five-second microphone test…");
        audio.play().catch(() => {
          setMicrophoneTestStatus("ready");
          setMicrophoneTestMessage("Recording ready — press play to listen.");
        });
      }
    };
    recorder.start();
    microphoneTestTimerRef.current = window.setInterval(() => {
      setMicrophoneTestSeconds((seconds) => Math.max(0, seconds - 1));
    }, 1000);
    microphoneTestStopTimerRef.current = window.setTimeout(() => {
      if (recorder.state !== "inactive") recorder.stop();
      if (microphoneTestTimerRef.current) {
        window.clearInterval(microphoneTestTimerRef.current);
        microphoneTestTimerRef.current = null;
      }
      microphoneTestStopTimerRef.current = null;
    }, 5000);
  };

  // =========================================================
  // ICE
  // =========================================================

  const queueIceCandidate = (
    participantId,
    candidate
  ) => {
    const queue =
      iceQueuesRef.current.get(
        participantId
      ) || [];

    queue.push(candidate);

    iceQueuesRef.current.set(
      participantId,
      queue
    );
  };

  const flushIceQueue = async (
    participantId,
    pc
  ) => {
    const queue =
      iceQueuesRef.current.get(
        participantId
      ) || [];

    for (
      const candidate of queue
    ) {
      try {
        await pc.addIceCandidate(
          candidate
        );
      } catch (err) {
        console.error(
          "Queued ICE candidate error:",
          err
        );
      }
    }

    iceQueuesRef.current.set(
      participantId,
      []
    );
  };

  const addRemoteIceCandidate = async (
    participantId,
    pc,
    candidateData
  ) => {
    const candidate =
      new RTCIceCandidate(
        candidateData
      );

    if (
      pc.remoteDescription
    ) {
      try {
        await pc.addIceCandidate(
          candidate
        );
      } catch (err) {
        console.error(
          "ICE candidate error:",
          err
        );
      }
    } else {
      queueIceCandidate(
        participantId,
        candidate
      );
    }
  };

  // =========================================================
  // CLOSE PEER
  // =========================================================

  const closePeerConnection = (
    participantId
  ) => {
    const pc =
      peerConnectionsRef.current.get(
        participantId
      );

    if (pc) {
      try {
        pc.close();
      } catch {
        // Ignore.
      }

      peerConnectionsRef.current.delete(
        participantId
      );
    }

    const listeners =
      peerListenersRef.current.get(
        participantId
      ) || [];

    listeners.forEach(
      (unsubscribe) => {
        try {
          unsubscribe();
        } catch {
          // Ignore.
        }
      }
    );

    peerListenersRef.current.delete(
      participantId
    );

    remoteStreamsRef.current.delete(
      participantId
    );
    remoteCameraStreamsRef.current.delete(participantId);
    cameraSendersRef.current.delete(participantId);

    iceQueuesRef.current.delete(
      participantId
    );

    const controlChannel =
      controlChannelsRef.current.get(
        participantId
      );

    if (controlChannel) {
      try {
        controlChannel.close();
      } catch {
        // Ignore.
      }

      controlChannelsRef.current.delete(
        participantId
      );
    }

    setRemoteControlOffer(
      (current) =>
        current?.participantId === participantId
          ? null
          : current
    );

    setRemoteControlSession(
      (current) =>
        current.tutorParticipantId === participantId
          ? {
              active: false,
              tutorParticipantId: null,
              tutorName: "Tutor"
            }
          : current
    );

    removeRemoteParticipant(
      participantId
    );
  };

  // =========================================================
  // REMOTE CONTROL DATA CHANNEL HELPERS
  // =========================================================

  const sendControlMessage = (
    participantId,
    message
  ) => {
    const channel =
      controlChannelsRef.current.get(
        participantId
      );

    if (
      !channel ||
      channel.readyState !== "open"
    ) {
      return false;
    }

    try {
      channel.send(
        JSON.stringify(message)
      );

      return true;
    } catch (err) {
      console.error(
        "Could not send remote-control message:",
        err
      );

      return false;
    }
  };

  const broadcastWhiteboardMessage = (
    message
  ) => {
    controlChannelsRef.current.forEach(
      (channel) => {
        if (
          channel?.readyState ===
          "open"
        ) {
          try {
            channel.send(
              JSON.stringify(
                message
              )
            );
          } catch (err) {
            console.error(
              "Could not send whiteboard message:",
              err
            );
          }
        }
      }
    );
  };

  const sendWhiteboardImageInChunks = (
    send,
    image
  ) => {
    const dataUrl =
      image?.dataUrl ||
      "";

    if (!dataUrl) {
      return;
    }

    const metadata = {
      ...image
    };
    delete metadata.dataUrl;

    const chunkSize = 12000;
    const totalChunks = Math.ceil(
      dataUrl.length /
        chunkSize
    );

    send({
      type:
        "whiteboard-image-start",
      image: metadata,
      totalChunks
    });

    for (
      let index = 0;
      index < totalChunks;
      index += 1
    ) {
      send({
        type:
          "whiteboard-image-chunk",
        imageId:
          image.id,
        index,
        data:
          dataUrl.slice(
            index * chunkSize,
            (index + 1) * chunkSize
          )
      });
    }

    send({
      type:
        "whiteboard-image-end",
      imageId:
        image.id
    });
  };

  const sendWhiteboardSnapshot = (
    send,
    snapshot
  ) => {
    let snapshotToSend = snapshot || [];
    let images = [];

    if (
      snapshot &&
      !Array.isArray(snapshot) &&
      Array.isArray(snapshot.items)
    ) {
      images = snapshot.items.filter(
        (item) =>
          item.type ===
          "image"
      );
      snapshotToSend = {
        ...snapshot,
      // Keep an image metadata placeholder at its original array position.
      // The chunked data replaces that placeholder later, rather than
      // appending the image above counters, coins, text or drawings.
        items: snapshot.items.map((item) => {
          if (item.type !== "image") {
            return item;
          }

          const metadata = { ...item };
          delete metadata.dataUrl;
          return metadata;
        })
      };
    }

    const serialisedSnapshot = JSON.stringify(snapshotToSend);

    if (serialisedSnapshot.length <= WHITEBOARD_LIVE_CHUNK_SIZE) {
      send({
        type: "whiteboard-open",
        strokes: snapshotToSend
      });
    } else {
      const transferId =
        window.crypto?.randomUUID?.() ||
        `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const totalChunks = Math.ceil(
        serialisedSnapshot.length / WHITEBOARD_LIVE_CHUNK_SIZE
      );

      send({
        type: "whiteboard-snapshot-start",
        transferId,
        totalChunks
      });
      for (let index = 0; index < totalChunks; index += 1) {
        send({
          type: "whiteboard-snapshot-chunk",
          transferId,
          index,
          data: serialisedSnapshot.slice(
            index * WHITEBOARD_LIVE_CHUNK_SIZE,
            (index + 1) * WHITEBOARD_LIVE_CHUNK_SIZE
          )
        });
      }
      send({
        type: "whiteboard-snapshot-end",
        transferId
      });
    }

    images.forEach(
      (image) =>
        sendWhiteboardImageInChunks(
          send,
          image
        )
    );
  };

  const saveWhiteboardSnapshot = async (snapshot) => {
    if (
      !isElectronDesktop ||
      !roomId ||
      !snapshot ||
      !Array.isArray(snapshot.pages) ||
      !Array.isArray(snapshot.items)
    ) {
      return;
    }

    const persistentSnapshot = {
      version: snapshot.version || 2,
      pages: snapshot.pages,
      activePageId: snapshot.activePageId,
      items: snapshot.items,
      calculator: snapshot.calculator
    };
    const serialised = JSON.stringify(persistentSnapshot);
    const chunks = [];

    for (
      let offset = 0;
      offset < serialised.length;
      offset += WHITEBOARD_CHUNK_SIZE
    ) {
      chunks.push(
        serialised.slice(offset, offset + WHITEBOARD_CHUNK_SIZE)
      );
    }

    const chunkCollection = getWhiteboardChunksCollection();

    await Promise.all(
      chunks.map((data, index) =>
        setDoc(
          doc(chunkCollection, String(index).padStart(5, "0")),
          { index, data }
        )
      )
    );

    await setDoc(
      getWhiteboardStateRef(),
      {
        formatVersion: 1,
        chunkCount: chunks.length,
        updatedAt: serverTimestamp()
      },
      { merge: true }
    );
  };

  const scheduleWhiteboardSave = (snapshot) => {
    if (!isElectronDesktop || !whiteboardPersistenceReadyRef.current) {
      return;
    }

    if (whiteboardSaveTimerRef.current) {
      window.clearTimeout(whiteboardSaveTimerRef.current);
    }

    whiteboardSaveTimerRef.current = window.setTimeout(() => {
      whiteboardSaveTimerRef.current = null;
      const snapshotToSave = whiteboardSnapshotRef.current;

      whiteboardSaveQueueRef.current = whiteboardSaveQueueRef.current
        .catch(() => {})
        .then(() => saveWhiteboardSnapshot(snapshotToSave))
        .catch((error) => {
          console.error("Could not save the lesson whiteboard:", error);
        });
    }, 750);
  };

  useEffect(() => {
    // Restore only after the tutor has actually joined the lesson. Attempting
    // this on the route's first render can happen before Firestore permits
    // access to the room, leaving the component on its initial blank board.
    if (
      !roomId ||
      !isElectronDesktop ||
      !hasJoinedLesson
    ) {
      return undefined;
    }

    let cancelled = false;
    whiteboardPersistenceReadyRef.current = false;

    const restoreWhiteboard = async () => {
      let safeToSave = false;

      try {
        const stateSnapshot = await getDoc(getWhiteboardStateRef());

        if (!stateSnapshot.exists()) {
          safeToSave = true;
          return;
        }

        const { chunkCount = 0 } = stateSnapshot.data() || {};
        const chunksSnapshot = await getDocs(
          query(getWhiteboardChunksCollection(), orderBy("index", "asc"))
        );

        if (cancelled) return;

        if (chunksSnapshot.size < chunkCount) {
          throw new Error(
            `Only ${chunksSnapshot.size} of ${chunkCount} saved whiteboard chunks could be loaded.`
          );
        }

        const serialised = chunksSnapshot.docs
          .filter((chunk) => (chunk.data()?.index ?? -1) < chunkCount)
          .slice(0, chunkCount)
          .map((chunk) => chunk.data()?.data || "")
          .join("");

        if (!serialised || chunkCount < 1) {
          throw new Error("The saved whiteboard contains no readable data.");
        }

        const restored = JSON.parse(serialised);

        if (
          !restored ||
          !Array.isArray(restored.pages) ||
          !Array.isArray(restored.items)
        ) {
          throw new Error("The saved whiteboard has an invalid format.");
        }

        whiteboardSnapshotRef.current = restored;
        queueWhiteboardCommand("saved-lesson", {
          type: "whiteboard-sync",
          strokes: restored
        });
        safeToSave = true;
      } catch (error) {
        console.error("Could not restore the lesson whiteboard:", error);
        if (!cancelled) {
          setError(
            "Your saved whiteboard could not be loaded. Its stored data has been protected from being overwritten. Please leave the board unchanged and try rejoining the lesson."
          );
        }
      } finally {
        if (!cancelled) {
          // Never allow an initial blank board to overwrite a saved board
          // when restoring it failed or returned only part of its data.
          whiteboardPersistenceReadyRef.current = safeToSave;
        }
      }
    };

    restoreWhiteboard();

    return () => {
      cancelled = true;
      whiteboardPersistenceReadyRef.current = false;
      if (whiteboardSaveTimerRef.current) {
        window.clearTimeout(whiteboardSaveTimerRef.current);
        whiteboardSaveTimerRef.current = null;
      }
    };
  }, [roomId, isElectronDesktop, hasJoinedLesson]);

  const queueWhiteboardCommand = (
    remoteParticipantId,
    message
  ) => {
    setWhiteboardCommand(
      (current) => [
        ...current.slice(-999),
        {
          nonce:
            Date.now() +
            Math.random(),
          remoteParticipantId,
          message
        }
      ]
    );
  };

  const openWhiteboard = () => {
    if (!isElectronDesktop) {
      return;
    }

    whiteboardOpenRef.current = true;
    setWhiteboardOpen(true);

    whiteboardOpenRetryTimersRef.current.forEach((timer) =>
      window.clearTimeout(timer)
    );
    whiteboardOpenRetryTimersRef.current = [];

    const sendOpeningSnapshot = () => {
      if (!whiteboardOpenRef.current) return;
      sendWhiteboardSnapshot(
        broadcastWhiteboardMessage,
        whiteboardSnapshotRef.current
      );
    };

    sendOpeningSnapshot();
    whiteboardOpenRetryTimersRef.current = [300, 1000, 2500].map(
      (delay) => window.setTimeout(sendOpeningSnapshot, delay)
    );

    setDoc(
      getRoomRef(),
      {
        whiteboardSignal: {
          open: true,
          issuedAt: Date.now(),
          senderId: participantIdRef.current || "desktop-tutor"
        }
      },
      { merge: true }
    ).catch((error) => {
      console.warn("Could not publish whiteboard activation:", error);
    });
  };

  const closeWhiteboard = () => {
    if (!isElectronDesktop) {
      return;
    }

    whiteboardOpenRef.current = false;
    whiteboardOpenRetryTimersRef.current.forEach((timer) =>
      window.clearTimeout(timer)
    );
    whiteboardOpenRetryTimersRef.current = [];

    broadcastWhiteboardMessage({
      type: "whiteboard-close"
    });

    setWhiteboardOpen(false);

    setDoc(
      getRoomRef(),
      {
        whiteboardSignal: {
          open: false,
          issuedAt: Date.now(),
          senderId: participantIdRef.current || "desktop-tutor"
        }
      },
      { merge: true }
    ).catch((error) => {
      console.warn("Could not publish whiteboard closure:", error);
    });
  };

  const handleControlDataMessage = (
    remoteParticipantId,
    rawData
  ) => {
    let message = null;

    try {
      message =
        typeof rawData === "string"
          ? JSON.parse(rawData)
          : rawData;
    } catch (err) {
      console.warn(
        "Invalid control-channel message:",
        err
      );

      return;
    }

    if (
      !message ||
      typeof message.type !== "string"
    ) {
      return;
    }

    if (
      message.type === "whiteboard-snapshot-start" &&
      message.transferId &&
      Number.isInteger(message.totalChunks) &&
      message.totalChunks > 0 &&
      message.totalChunks <= 5000
    ) {
      whiteboardSnapshotTransfersRef.current.set(message.transferId, {
        totalChunks: message.totalChunks,
        chunks: new Array(message.totalChunks)
      });
      return;
    }

    if (
      message.type === "whiteboard-snapshot-chunk" &&
      message.transferId &&
      Number.isInteger(message.index) &&
      typeof message.data === "string"
    ) {
      const transfer = whiteboardSnapshotTransfersRef.current.get(
        message.transferId
      );
      if (
        transfer &&
        message.index >= 0 &&
        message.index < transfer.totalChunks
      ) {
        transfer.chunks[message.index] = message.data;
      }
      return;
    }

    if (
      message.type === "whiteboard-snapshot-end" &&
      message.transferId
    ) {
      const transfer = whiteboardSnapshotTransfersRef.current.get(
        message.transferId
      );
      whiteboardSnapshotTransfersRef.current.delete(message.transferId);

      if (!transfer || transfer.chunks.some((chunk) => typeof chunk !== "string")) {
        console.warn("An incomplete whiteboard snapshot was received.");
        return;
      }

      try {
        const strokes = JSON.parse(transfer.chunks.join(""));
        whiteboardOpenRef.current = true;
        setWhiteboardOpen(true);
        queueWhiteboardCommand(remoteParticipantId, {
          type: "whiteboard-sync",
          strokes
        });
      } catch (error) {
        console.warn("Could not read the received whiteboard snapshot:", error);
      }
      return;
    }

    if (
      message.type ===
      "whiteboard-open"
    ) {
      whiteboardOpenRef.current = true;
      setWhiteboardOpen(true);

      if (
        Array.isArray(
          message.strokes
        ) ||
        (
          message.strokes &&
          typeof message.strokes ===
            "object"
        )
      ) {
        queueWhiteboardCommand(
          remoteParticipantId,
          {
            type:
              "whiteboard-sync",
            strokes:
              message.strokes
          }
        );
      }

      return;
    }

    if (
      message.type ===
      "whiteboard-close"
    ) {
      whiteboardOpenRef.current = false;
      setWhiteboardOpen(false);
      return;
    }

    if (
      message.type === "screen-recording-status" &&
      ["idle", "recording", "paused", "resumed"].includes(message.status)
    ) {
      showScreenRecordingNotice(message.status);
      return;
    }

    if (
      message.type.startsWith(
        "whiteboard-"
      )
    ) {
      queueWhiteboardCommand(
        remoteParticipantId,
        message
      );
      return;
    }

    if (
      message.type ===
      "remote-control-offer"
    ) {
      setRemoteControlOffer({
        participantId:
          remoteParticipantId,
        tutorName:
          message.tutorName ||
          "Tutor"
      });
      return;
    }

    if (
      message.type ===
      "remote-control-revoke"
    ) {
      setRemoteControlOffer(null);
      setRemoteControlSession({
        active: false,
        tutorParticipantId: null,
        tutorName: "Tutor"
      });
      return;
    }

    if (
      message.type ===
        "remote-control-accepted" &&
      isElectronDesktop
    ) {
      window.learnWithRyanDesktop
        ?.remoteControlAccepted?.({
          id: remoteParticipantId,
          name:
            message.participantName ||
            "Participant"
        });
      return;
    }

    if (
      message.type ===
        "remote-control-declined" &&
      isElectronDesktop
    ) {
      window.learnWithRyanDesktop
        ?.remoteControlDeclined?.({
          id: remoteParticipantId,
          name:
            message.participantName ||
            "Participant"
        });
      return;
    }

    if (
      message.type ===
        "remote-control-released" &&
      isElectronDesktop
    ) {
      window.learnWithRyanDesktop
        ?.remoteControlReleased?.({
          id: remoteParticipantId,
          name:
            message.participantName ||
            "Participant"
        });
      return;
    }

    if (
      message.type ===
        "remote-control-input" &&
      isElectronDesktop
    ) {
      window.learnWithRyanDesktop
        ?.sendRemoteInput?.({
          participantId:
            remoteParticipantId,
          ...(message.input || {})
        });
    }
  };

  const setupControlDataChannel = (
    remoteParticipantId,
    channel
  ) => {
    if (!channel) {
      return;
    }

    controlChannelsRef.current.set(
      remoteParticipantId,
      channel
    );

    channel.onopen = () => {
      console.log(
        "Remote-control data channel open:",
        remoteParticipantId
      );

      if (
        isElectronDesktop &&
        whiteboardOpenRef.current
      ) {
        sendWhiteboardSnapshot(
          (message) =>
            sendControlMessage(
              remoteParticipantId,
              message
            ),
          whiteboardSnapshotRef.current
        );
      }

      if (
        isElectronDesktop &&
        screenRecordingStatusRef.current !== "idle"
      ) {
        sendControlMessage(remoteParticipantId, {
          type: "screen-recording-status",
          status: screenRecordingStatusRef.current
        });
      }
    };

    channel.onmessage = (event) => {
      handleControlDataMessage(
        remoteParticipantId,
        event.data
      );
    };

    channel.onclose = () => {
      const current =
        controlChannelsRef.current.get(
          remoteParticipantId
        );

      if (current === channel) {
        controlChannelsRef.current.delete(
          remoteParticipantId
        );
      }

      setRemoteControlOffer(
        (offer) =>
          offer?.participantId === remoteParticipantId
            ? null
            : offer
      );

      setRemoteControlSession(
        (session) =>
          session.tutorParticipantId === remoteParticipantId
            ? {
                active: false,
                tutorParticipantId: null,
                tutorName: "Tutor"
              }
            : session
      );
    };

    channel.onerror = (event) => {
      console.warn(
        "Remote-control data channel error:",
        event
      );
    };
  };

  const acceptRemoteControlOffer = () => {
    if (!remoteControlOffer) {
      return;
    }

    const participantName =
      displayName.trim() ||
      "Student";

    const sent =
      sendControlMessage(
        remoteControlOffer.participantId,
        {
          type:
            "remote-control-accepted",
          participantId:
            participantIdRef.current,
          participantName
        }
      );

    if (!sent) {
      setError(
        "Remote control could not be accepted because the lesson control connection is not ready."
      );
      return;
    }

    setRemoteControlSession({
      active: true,
      tutorParticipantId:
        remoteControlOffer.participantId,
      tutorName:
        remoteControlOffer.tutorName ||
        "Tutor"
    });

    setRemoteControlOffer(null);
  };

  const declineRemoteControlOffer = () => {
    if (!remoteControlOffer) {
      return;
    }

    sendControlMessage(
      remoteControlOffer.participantId,
      {
        type:
          "remote-control-declined",
        participantId:
          participantIdRef.current,
        participantName:
          displayName.trim() ||
          "Student"
      }
    );

    setRemoteControlOffer(null);
  };

  const releaseRemoteControl = () => {
    const tutorId =
      remoteControlSession
        .tutorParticipantId;

    if (tutorId) {
      sendControlMessage(
        tutorId,
        {
          type:
            "remote-control-released",
          participantId:
            participantIdRef.current,
          participantName:
            displayName.trim() ||
            "Student"
        }
      );
    }

    setRemoteControlSession({
      active: false,
      tutorParticipantId: null,
      tutorName: "Tutor"
    });
  };

  const getNormalisedVideoPoint = (
    event,
    videoElement
  ) => {
    if (!videoElement) {
      return null;
    }

    const rect =
      videoElement.getBoundingClientRect();
    const sourceWidth =
      videoElement.videoWidth || 16;
    const sourceHeight =
      videoElement.videoHeight || 9;

    if (
      rect.width <= 0 ||
      rect.height <= 0
    ) {
      return null;
    }

    const scale = Math.min(
      rect.width / sourceWidth,
      rect.height / sourceHeight
    );
    const visibleWidth =
      sourceWidth * scale;
    const visibleHeight =
      sourceHeight * scale;
    const offsetX =
      (rect.width - visibleWidth) / 2;
    const offsetY =
      (rect.height - visibleHeight) / 2;
    const localX =
      event.clientX - rect.left - offsetX;
    const localY =
      event.clientY - rect.top - offsetY;

    if (
      localX < 0 ||
      localY < 0 ||
      localX > visibleWidth ||
      localY > visibleHeight
    ) {
      return null;
    }

    return {
      x: Math.max(
        0,
        Math.min(
          1,
          localX / visibleWidth
        )
      ),
      y: Math.max(
        0,
        Math.min(
          1,
          localY / visibleHeight
        )
      )
    };
  };

  const sendRemoteControlInput = (
    tutorParticipantId,
    input
  ) => {
    if (
      !remoteControlSession.active ||
      remoteControlSession
        .tutorParticipantId !==
        tutorParticipantId
    ) {
      return false;
    }

    return sendControlMessage(
      tutorParticipantId,
      {
        type:
          "remote-control-input",
        input
      }
    );
  };

  // =========================================================
  // CREATE PEER
  // =========================================================

  const createPeerConnection = (
    remoteParticipantId,
    remoteName
  ) => {
    if (
      peerConnectionsRef.current.has(
        remoteParticipantId
      )
    ) {
      return (
        peerConnectionsRef.current.get(
          remoteParticipantId
        )
      );
    }

    const pc =
      new RTCPeerConnection(
        rtcConfiguration
      );

    peerConnectionsRef.current.set(
      remoteParticipantId,
      pc
    );

    pc.ondatachannel = (event) => {
      if (
        event.channel?.label ===
        "lesson-control"
      ) {
        setupControlDataChannel(
          remoteParticipantId,
          event.channel
        );
      }
    };

    const remoteStream =
      new MediaStream();

    remoteStreamsRef.current.set(
      remoteParticipantId,
      remoteStream
    );

    updateRemoteParticipant(
      remoteParticipantId,
      {
        name:
          remoteName ||
          "Participant",
        connected: false
      }
    );

    const videoTrack =
      getCurrentOutgoingVideoTrack();

    if (videoTrack) {
      pc.addTrack(
        videoTrack,
        new MediaStream(
          [videoTrack]
        )
      );
    }

    // Negotiate the webcam separately so sharing does not replace it.
    const cameraTrack = getCameraVideoTrack();
    if (cameraTrack) {
      // addTrack-created transceivers can match an incoming offer. A manually
      // added transceiver may instead leave the offered camera channel receive-only.
      // Start with a clone because addTrack rejects adding the same track twice.
      const initialCameraTrack = cameraTrack.clone();
      const sender = pc.addTrack(initialCameraTrack, new MediaStream([initialCameraTrack]));
      cameraSendersRef.current.set(remoteParticipantId, sender);
      sender.replaceTrack(cameraTrack).then(() => initialCameraTrack.stop()).catch(() => {});
    }

    const audioTrack =
      getCurrentOutgoingAudioTrack();

    if (audioTrack) {
      pc.addTrack(
        audioTrack,
        new MediaStream(
          [audioTrack]
        )
      );
    }

    pc.ontrack = (event) => {
      const videoReceivers = pc.getTransceivers().filter((item) => item.receiver.track.kind === "video");
      if (event.track.kind === "video" && videoReceivers[1]?.receiver === event.receiver) {
        remoteCameraStreamsRef.current.set(remoteParticipantId, new MediaStream([event.track]));
        setRemoteParticipants((current) => [...current]);
        return;
      }
      const stream =
        remoteStreamsRef.current.get(
          remoteParticipantId
        );

      if (!stream) {
        return;
      }

      const tracks =
        event.streams?.[0]
          ?.getTracks() ||
        [event.track];

      tracks.forEach(
        (track) => {
          const exists =
            stream
              .getTracks()
              .some(
                (existing) =>
                  existing.id ===
                  track.id
              );

          if (!exists) {
            stream.addTrack(
              track
            );
          }
        }
      );

      updateRemoteParticipant(
        remoteParticipantId,
        {
          name:
            remoteName ||
            "Participant",
          connected: true
        }
      );

      setRemoteParticipants(
        (current) =>
          [...current]
      );
    };

    pc.onconnectionstatechange =
      () => {
        const state =
          pc.connectionState;

        if (
          state === "connected"
        ) {
          updateRemoteParticipant(
            remoteParticipantId,
            {
              connected: true
            }
          );
        }

        if (
          state === "failed" ||
          state === "closed" ||
          state === "disconnected"
        ) {
          updateRemoteParticipant(
            remoteParticipantId,
            {
              connected: false
            }
          );
        }
      };

    return pc;
  };

  // =========================================================
  // ESTABLISH PEER
  // =========================================================

  const establishPeerConnection = async (
    remoteParticipantId,
    remoteName
  ) => {
    const ownId =
      participantIdRef.current;

    if (
      !ownId ||
      remoteParticipantId ===
        ownId
    ) {
      return;
    }

    if (
      peerConnectionsRef.current.has(
        remoteParticipantId
      )
    ) {
      updateRemoteParticipant(
        remoteParticipantId,
        {
          name:
            remoteName ||
            "Participant"
        }
      );

      return;
    }

    const pc =
      createPeerConnection(
        remoteParticipantId,
        remoteName
      );

    const weAreOfferer =
      ownId.localeCompare(
        remoteParticipantId
      ) < 0;

    if (weAreOfferer) {
      const controlChannel =
        pc.createDataChannel(
          "lesson-control",
          {
            ordered: true
          }
        );

      setupControlDataChannel(
        remoteParticipantId,
        controlChannel
      );
    }

    const pairId =
      getPairId(
        ownId,
        remoteParticipantId
      );

    const callRef =
      doc(
        getRoomRef(),
        "calls",
        pairId
      );

    const offerCandidatesRef =
      collection(
        callRef,
        "offerCandidates"
      );

    const answerCandidatesRef =
      collection(
        callRef,
        "answerCandidates"
      );

    const unsubscribers = [];

    peerListenersRef.current.set(
      remoteParticipantId,
      unsubscribers
    );

    // =====================================================
    // OFFERER
    // =====================================================

    if (weAreOfferer) {
      pc.onicecandidate =
        async (event) => {
          if (
            event.candidate
          ) {
            try {
              await addDoc(
                offerCandidatesRef,
                event.candidate.toJSON()
              );
            } catch (err) {
              console.error(
                "Offer ICE error:",
                err
              );
            }
          }
        };

      const unsubscribeAnswerCandidates =
        onSnapshot(
          answerCandidatesRef,
          (snapshot) => {
            snapshot
              .docChanges()
              .forEach(
                (change) => {
                  if (
                    change.type ===
                    "added"
                  ) {
                    addRemoteIceCandidate(
                      remoteParticipantId,
                      pc,
                      change.doc.data()
                    );
                  }
                }
              );
          }
        );

      unsubscribers.push(
        unsubscribeAnswerCandidates
      );

      const unsubscribeCall =
        onSnapshot(
          callRef,
          async (snapshot) => {
            const data =
              snapshot.data();

            if (
              data?.answer &&
              !pc.remoteDescription
            ) {
              try {
                await pc.setRemoteDescription(
                  new RTCSessionDescription(
                    data.answer
                  )
                );

                await flushIceQueue(
                  remoteParticipantId,
                  pc
                );
              } catch (err) {
                console.error(
                  "Could not apply answer:",
                  err
                );
              }
            }
          }
        );

      unsubscribers.push(
        unsubscribeCall
      );

      try {
        const offer =
          await pc.createOffer();

        await pc.setLocalDescription(
          offer
        );

        await setDoc(
          callRef,
          {
            participantA:
              ownId,

            participantB:
              remoteParticipantId,

            offerer:
              ownId,

            offer: {
              type:
                offer.type,
              sdp:
                offer.sdp
            },

            updatedAt:
              serverTimestamp()
          },
          {
            merge: true
          }
        );
      } catch (err) {
        console.error(
          "Offer creation error:",
          err
        );
      }

      return;
    }

    // =====================================================
    // ANSWERER
    // =====================================================

    pc.onicecandidate =
      async (event) => {
        if (
          event.candidate
        ) {
          try {
            await addDoc(
              answerCandidatesRef,
              event.candidate.toJSON()
            );
          } catch (err) {
            console.error(
              "Answer ICE error:",
              err
            );
          }
        }
      };

    const unsubscribeOfferCandidates =
      onSnapshot(
        offerCandidatesRef,
        (snapshot) => {
          snapshot
            .docChanges()
            .forEach(
              (change) => {
                if (
                  change.type ===
                  "added"
                ) {
                  addRemoteIceCandidate(
                    remoteParticipantId,
                    pc,
                    change.doc.data()
                  );
                }
              }
            );
        }
      );

    unsubscribers.push(
      unsubscribeOfferCandidates
    );

    let answerAlreadyCreated =
      false;

    const unsubscribeCall =
      onSnapshot(
        callRef,
        async (snapshot) => {
          const data =
            snapshot.data();

          if (
            !data?.offer ||
            answerAlreadyCreated
          ) {
            return;
          }

          try {
            answerAlreadyCreated =
              true;

            await pc.setRemoteDescription(
              new RTCSessionDescription(
                data.offer
              )
            );

            await flushIceQueue(
              remoteParticipantId,
              pc
            );

            const answer =
              await pc.createAnswer();

            await pc.setLocalDescription(
              answer
            );

            await setDoc(
              callRef,
              {
                answerer:
                  ownId,

                answer: {
                  type:
                    answer.type,
                  sdp:
                    answer.sdp
                },

                updatedAt:
                  serverTimestamp()
              },
              {
                merge: true
              }
            );
          } catch (err) {
            answerAlreadyCreated =
              false;

            console.error(
              "Answer creation error:",
              err
            );
          }
        }
      );

    unsubscribers.push(
      unsubscribeCall
    );
  };

  // =========================================================
  // PARTICIPANT ACTIVE?
  // =========================================================

  const participantIsActive = (
    data
  ) => {
    if (
      data?.active === false
    ) {
      return false;
    }

    const lastSeen =
      data?.lastSeen;

    if (
      !lastSeen ||
      typeof lastSeen.toMillis !==
        "function"
    ) {
      return true;
    }

    return (
      Date.now() -
        lastSeen.toMillis() <
      STALE_AFTER_MS
    );
  };

  // =========================================================
  // LOBBY / ADMISSION
  // =========================================================

  const watchOwnAdmissionStatus = (
    participantRef
  ) => {
    if (
      admissionListenerRef.current
    ) {
      admissionListenerRef.current();
      admissionListenerRef.current =
        null;
    }

    admissionListenerRef.current =
      onSnapshot(
        participantRef,
        async (snapshot) => {
          if (!snapshot.exists()) {
            return;
          }

          const data =
            snapshot.data();

          if (
            data.status ===
            "admitted"
          ) {
            if (
              admissionListenerRef.current
            ) {
              admissionListenerRef.current();
              admissionListenerRef.current =
                null;
            }

            setIsWaitingForAdmission(
              false
            );

            setError("");

            setHasJoinedLesson(
              true
            );

            startParticipantListener();

            return;
          }

          if (
            data.status ===
            "denied"
          ) {
            if (
              admissionListenerRef.current
            ) {
              admissionListenerRef.current();
              admissionListenerRef.current =
                null;
            }

            if (
              heartbeatTimerRef.current
            ) {
              clearInterval(
                heartbeatTimerRef.current
              );

              heartbeatTimerRef.current =
                null;
            }

            setIsWaitingForAdmission(
              false
            );

            setHasJoinedLesson(
              false
            );

            setError(
              "The tutor declined your request to join the lesson."
            );

            try {
              await deleteDoc(
                participantRef
              );
            } catch (deleteError) {
              console.warn(
                "Could not remove declined lobby request:",
                deleteError
              );
            }

            participantDocRef.current =
              null;

            participantIdRef.current =
              null;
          }
        },
        (listenerError) => {
          console.error(
            "Admission listener error:",
            listenerError
          );

          setError(
            `Could not monitor your admission request: ${listenerError.message}`
          );
        }
      );
  };

  const admitWaitingParticipant =
    async (participantId) => {
      try {
        const participantRef =
          doc(
            getParticipantsCollection(),
            participantId
          );

        await updateDoc(
          participantRef,
          {
            status:
              "admitted",

            active:
              true,

            admittedAt:
              serverTimestamp(),

            lastSeen:
              serverTimestamp()
          }
        );
      } catch (err) {
        console.error(
          "Could not admit participant:",
          err
        );

        setError(
          `Could not admit participant: ${err.message}`
        );
      }
    };

  const denyWaitingParticipant =
    async (participantId) => {
      try {
        const participantRef =
          doc(
            getParticipantsCollection(),
            participantId
          );

        await updateDoc(
          participantRef,
          {
            status:
              "denied",

            active:
              false,

            deniedAt:
              serverTimestamp(),

            lastSeen:
              serverTimestamp()
          }
        );
      } catch (err) {
        console.error(
          "Could not deny participant:",
          err
        );

        setError(
          `Could not deny participant: ${err.message}`
        );
      }
    };

  // =========================================================
  // PARTICIPANT LISTENER
  // =========================================================

  const startParticipantListener =
    () => {
      if (
        participantListenerRef.current
      ) {
        participantListenerRef.current();
      }

      participantListenerRef.current =
        onSnapshot(
          getParticipantsCollection(),
          (snapshot) => {
            const ownId =
              participantIdRef.current;

            const activeRemoteIds =
              new Set();

            const currentWaiting =
              [];

            snapshot.forEach(
              (
                participantSnapshot
              ) => {
                const id =
                  participantSnapshot.id;

                if (
                  id === ownId
                ) {
                  return;
                }

                const data =
                  participantSnapshot.data();

                if (
                  data.status ===
                    "waiting" &&
                  data.active !==
                    false
                ) {
                  if (
                    isElectronDesktop
                  ) {
                    currentWaiting.push({
                      id,

                      name:
                        data.name ||
                        "Student",

                      role:
                        data.role ||
                        "participant"
                    });
                  }

                  return;
                }

                if (
                  data.status ===
                  "denied"
                ) {
                  return;
                }

                if (
                  data.status &&
                  data.status !==
                    "admitted"
                ) {
                  return;
                }

                if (
                  !participantIsActive(
                    data
                  )
                ) {
                  return;
                }

                activeRemoteIds.add(
                  id
                );

                updateRemoteParticipant(
                  id,
                  {
                    name:
                      data.name ||
                      "Participant",

                    role:
                      data.role ||
                      "participant",

                    isScreenSharing:
                      data.isScreenSharing ===
                      true,

                    micEnabled:
                      data.micEnabled !== false,

                    handRaised:
                      data.handRaised === true,

                    chatTyping:
                      data.chatTyping === true,

                    chatTypingAt:
                      data.chatTypingAt || 0
                  }
                );

                establishPeerConnection(
                  id,
                  data.name ||
                    "Participant"
                );
              }
            );

          if (
  isElectronDesktop
) {
  const currentWaitingIds =
    new Set(
      currentWaiting.map(
        (participant) =>
          participant.id
      )
    );

  const hasNewWaitingParticipant =
    currentWaiting.some(
      (participant) =>
        !previousWaitingIdsRef.current.has(
          participant.id
        )
    );

  if (
    hasNewWaitingParticipant
  ) {
    playLobbySound();
  }

  previousWaitingIdsRef.current =
    currentWaitingIds;

  setWaitingParticipants(
    currentWaiting
  );
}

            Array.from(
              peerConnectionsRef.current.keys()
            ).forEach(
              (participantId) => {
                if (
                  !activeRemoteIds.has(
                    participantId
                  )
                ) {
                  closePeerConnection(
                    participantId
                  );
                }
              }
            );
          },
          (listenerError) => {
            console.error(
              "Participant listener error:",
              listenerError
            );

            setError(
              `Could not monitor lesson participants: ${listenerError.message}`
            );
          }
        );
    };

  // =========================================================
  // HEARTBEAT
  // =========================================================

  const startHeartbeat = () => {
    if (
      heartbeatTimerRef.current
    ) {
      clearInterval(
        heartbeatTimerRef.current
      );
    }

    heartbeatTimerRef.current =
      setInterval(
        async () => {
          const participantRef =
            participantDocRef.current;

          if (!participantRef) {
            return;
          }

          try {
            await updateDoc(
              participantRef,
              {
                lastSeen:
                  serverTimestamp()
              }
            );
          } catch (err) {
            console.warn(
              "Heartbeat error:",
              err
            );
          }
        },
        HEARTBEAT_MS
      );
  };

  // =========================================================
  // CHAT LISTENER
  // =========================================================

  useEffect(() => {
    if (
      !hasJoinedLesson ||
      !roomId
    ) {
      return;
    }

    const messagesQuery =
      query(
        getMessagesCollection(),
        orderBy(
          "createdAt",
          "asc"
        )
      );

    let initialLoad = true;

    const unsubscribe =
      onSnapshot(
        messagesQuery,
        (snapshot) => {
          const messages =
            snapshot.docs.map(
              (messageDoc) => ({
                id:
                  messageDoc.id,
                ...messageDoc.data()
              })
            );

          setChatMessages(
            messages
          );

      if (!initialLoad) {
  const incoming =
    snapshot
      .docChanges()
      .filter(
        (change) =>
          change.type ===
          "added"
      )
      .filter(
        (change) =>
          change.doc.data()
            .senderId !==
          participantIdRef.current
      );

  // =====================================================
  // INCOMING MESSAGE SOUND
  // =====================================================

  if (
    incoming.length > 0
  ) {
    playIncomingChatSound();
  }

  // =====================================================
  // UNREAD COUNT
  //
  // Only increase the unread badge when
  // the chat panel is closed.
  // =====================================================

  if (
    !chatOpenRef.current &&
    incoming.length > 0
  ) {
    setUnreadMessages(
      (current) =>
        current +
        incoming.length
    );
  }
}

          initialLoad = false;
        },
        (chatError) => {
          console.error(
            "Chat listener error:",
            chatError
          );
        }
      );

    chatListenerRef.current =
      unsubscribe;

    return () => {
      unsubscribe();

      if (
        chatListenerRef.current ===
        unsubscribe
      ) {
        chatListenerRef.current =
          null;
      }
    };
  }, [
    hasJoinedLesson,
    roomId
  ]);

  // =========================================================
  // CHAT AUTO SCROLL
  // =========================================================

  useEffect(() => {
    if (
      chatOpen &&
      chatEndRef.current
    ) {
      chatEndRef.current.scrollIntoView({
        behavior: "smooth"
      });
    }
  }, [
    chatMessages,
    chatOpen,
    typingIndicatorText
  ]);

  // =========================================================
  // SEND CHAT
  // =========================================================

  const setChatTypingStatus = (
    isTyping,
    force = false
  ) => {
    const participantRef = participantDocRef.current;
    const now = Date.now();

    if (!participantRef) {
      return;
    }

    if (
      !force &&
      chatTypingStateRef.current === isTyping &&
      now - chatTypingLastSentAtRef.current < 900
    ) {
      return;
    }

    chatTypingStateRef.current = isTyping;
    chatTypingLastSentAtRef.current = now;

    updateDoc(participantRef, {
      chatTyping: isTyping,
      chatTypingAt: now
    }).catch((typingError) => {
      console.warn("Could not update chat typing status:", typingError);
    });
  };

  const updateChatDraft = (nextMessage) => {
    setChatMessage(nextMessage);

    if (chatTypingStopTimerRef.current) {
      window.clearTimeout(chatTypingStopTimerRef.current);
      chatTypingStopTimerRef.current = null;
    }

    if (!nextMessage.trim()) {
      setChatTypingStatus(false, true);
      return;
    }

    setChatTypingStatus(true);
    chatTypingStopTimerRef.current = window.setTimeout(() => {
      chatTypingStopTimerRef.current = null;
      setChatTypingStatus(false, true);
    }, 1800);
  };

  useEffect(() => {
    if (chatOpen) {
      return undefined;
    }

    if (chatTypingStopTimerRef.current) {
      window.clearTimeout(chatTypingStopTimerRef.current);
      chatTypingStopTimerRef.current = null;
    }
    if (chatTypingStateRef.current) {
      setChatTypingStatus(false, true);
    }

    return undefined;
  }, [chatOpen]);

  const sendChatMessage =
    async () => {
      const text =
        chatMessage.trim();

      if (
        !text ||
        sendingMessage ||
        !participantIdRef.current
      ) {
        return;
      }

      try {
        setSendingMessage(true);

        if (chatTypingStopTimerRef.current) {
          window.clearTimeout(chatTypingStopTimerRef.current);
          chatTypingStopTimerRef.current = null;
        }
        setChatTypingStatus(false, true);

        await addDoc(
          getMessagesCollection(),
          {
            text,

            senderId:
              participantIdRef.current,

            senderName:
              displayName.trim() ||
              (
                role === "tutor"
                  ? "Tutor"
                  : "Student"
              ),

            createdAt:
              serverTimestamp()
          }
        );

        setChatMessage("");
      } catch (err) {
        console.error(
          "Chat send error:",
          err
        );

        setError(
          `Could not send message: ${err.message}`
        );
      } finally {
        setSendingMessage(false);
      }
    };

  const clearLessonChat = async () => {
    if (
      !isElectronDesktop ||
      clearingChat
    ) {
      return;
    }

    const confirmed = window.confirm(
      "Clear every message from this lesson chat? This cannot be undone."
    );

    if (!confirmed) {
      return;
    }

    try {
      setClearingChat(true);

      const messagesSnapshot = await getDocs(
        getMessagesCollection()
      );

      await Promise.all(
        messagesSnapshot.docs.map(
          (messageDocument) =>
            deleteDoc(messageDocument.ref)
        )
      );

      setUnreadMessages(0);
    } catch (err) {
      console.error(
        "Chat clear error:",
        err
      );

      setError(
        `Could not clear chat: ${err.message}`
      );
    } finally {
      setClearingChat(false);
    }
  };

  const formatMessageTime = (
    timestamp
  ) => {
    if (
      !timestamp ||
      typeof timestamp.toDate !==
        "function"
    ) {
      return "";
    }

    return timestamp
      .toDate()
      .toLocaleTimeString(
        [],
        {
          hour: "2-digit",
          minute: "2-digit"
        }
      );
  };

  // =========================================================
  // JOIN LESSON
  // =========================================================

  const joinLesson =
    async () => {
      const participantDisplayName =
        displayName.trim();

      if (!participantDisplayName) {
        setError(
          "Please enter your name before joining the lesson."
        );
        return;
      }

      if (
        !localStreamRef.current
      ) {
        setError(
          "Please start your camera and microphone first."
        );

        return;
      }

      if (!roomId) {
        setError(
          "This lesson link is invalid."
        );

        return;
      }

      try {
        setJoining(true);
        setError("");
        saveDisplayName(
          participantDisplayName
        );

        const roomRef =
          getRoomRef();

        const roomSnapshot =
          await getDoc(
            roomRef
          );

        if (
          !roomSnapshot.exists()
        ) {
          await setDoc(
            roomRef,
            {
              createdAt:
                serverTimestamp(),

              status:
                "active"
            }
          );
        }

        const ourRole =
          isElectronDesktop
            ? "tutor"
            : "participant";

        if (isElectronDesktop) {
          await setDoc(roomRef, { status: 'active', endsAt: null, exitLaughStartedAt: null, exitLaugh: false, endingTutorId: null, endedAt: null }, { merge: true });
        } else if (['ending', 'ended'].includes(roomSnapshot.data()?.status)) {
          setError('This lesson has ended. Please wait for your tutor to start a new lesson.');
          return;
        }

        const admissionStatus =
          isElectronDesktop
            ? "admitted"
            : "waiting";

        setRole(
          ourRole
        );

        const participantId =
          createParticipantId();

        participantIdRef.current =
          participantId;

        const participantRef =
          doc(
            getParticipantsCollection(),
            participantId
          );

        participantDocRef.current =
          participantRef;

        await setDoc(
          participantRef,
          {
            name:
              participantDisplayName,

            role:
              ourRole,

            status:
              admissionStatus,

            joinedAt:
              serverTimestamp(),

            lastSeen:
              serverTimestamp(),

            active:
              true,

            isScreenSharing:
              false,

            micEnabled:
              micEnabledStateRef.current,

            handRaised:
              false,

            chatTyping:
              false,

            chatTypingAt:
              0
          }
        );

        startHeartbeat();

        if (
          isElectronDesktop
        ) {
          setHasJoinedLesson(
            true
          );

          setIsWaitingForAdmission(
            false
          );

          startParticipantListener();

          return;
        }

        setHasJoinedLesson(
          false
        );

        setIsWaitingForAdmission(
          true
        );

        watchOwnAdmissionStatus(
          participantRef
        );
      } catch (err) {
        console.error(
          "Join error:",
          err
        );

        setError(
          `Could not join lesson: ${err.message}`
        );
      } finally {
        setJoining(false);
      }
    };

  // =========================================================
  // CAMERA
  // =========================================================

  const toggleCamera = () => {
    const track =
      localStreamRef.current
        ?.getVideoTracks()[0];

    if (!track) {
      return;
    }

    const enabled =
      !track.enabled;

    track.enabled = enabled;

    const processedTrack =
      processedCameraStreamRef.current
        ?.getVideoTracks()[0];

    if (processedTrack) {
      processedTrack.enabled =
        enabled;
    }

    setCameraEnabled(enabled);
  };

  // =========================================================
  // MICROPHONE
  // =========================================================

  const stopMutedSpeechMonitoring = () => {
    if (mutedSpeechAnimationRef.current) {
      window.cancelAnimationFrame(
        mutedSpeechAnimationRef.current
      );
      mutedSpeechAnimationRef.current = null;
    }

    if (mutedSpeechWarningTimerRef.current) {
      window.clearTimeout(
        mutedSpeechWarningTimerRef.current
      );
      mutedSpeechWarningTimerRef.current = null;
    }

    if (mutedSpeechTrackRef.current) {
      try {
        mutedSpeechTrackRef.current.stop();
      } catch {
        // Ignore a track that has already stopped.
      }
      mutedSpeechTrackRef.current = null;
    }

    const context = mutedSpeechAudioContextRef.current;
    mutedSpeechAudioContextRef.current = null;
    if (context && context.state !== "closed") {
      context.close().catch(() => {});
    }

    setMutedSpeechWarning(false);
  };

  const startMutedSpeechMonitoring = async (microphoneTrack) => {
    stopMutedSpeechMonitoring();

    const AudioContextClass =
      window.AudioContext || window.webkitAudioContext;

    if (
      !microphoneTrack ||
      microphoneTrack.readyState !== "live" ||
      !AudioContextClass
    ) {
      return;
    }

    try {
      // A clone remains live locally while the transmitted track is muted.
      // It is never attached to a peer connection or recording.
      const monitoringTrack = microphoneTrack.clone();
      monitoringTrack.enabled = true;
      mutedSpeechTrackRef.current = monitoringTrack;

      const context = new AudioContextClass({ sampleRate: 48000 });
      mutedSpeechAudioContextRef.current = context;

      const source = context.createMediaStreamSource(
        new MediaStream([monitoringTrack])
      );
      const analyser = context.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.72;
      source.connect(analyser);

      if (context.state === "suspended") {
        await context.resume();
      }

      const samples = new Float32Array(analyser.fftSize);
      let loudSince = 0;
      let quietSince = performance.now();
      let readyForSpeech = true;

      const detectSpeech = (now) => {
        if (
          micEnabledStateRef.current ||
          monitoringTrack.readyState !== "live"
        ) {
          return;
        }

        analyser.getFloatTimeDomainData(samples);

        let sumOfSquares = 0;
        for (let index = 0; index < samples.length; index += 1) {
          sumOfSquares += samples[index] * samples[index];
        }

        const volume = Math.sqrt(sumOfSquares / samples.length);
        const soundsLikeSpeech = volume >= 0.028;

        if (soundsLikeSpeech) {
          quietSince = 0;
          if (!loudSince) {
            loudSince = now;
          }

          // Requiring sustained sound avoids warnings for most mouse clicks,
          // key taps and other very brief noises.
          if (
            readyForSpeech &&
            now - loudSince >= 420
          ) {
            readyForSpeech = false;
            setMutedSpeechWarning(true);

            if (mutedSpeechWarningTimerRef.current) {
              window.clearTimeout(
                mutedSpeechWarningTimerRef.current
              );
            }

            mutedSpeechWarningTimerRef.current = window.setTimeout(() => {
              mutedSpeechWarningTimerRef.current = null;
              setMutedSpeechWarning(false);
            }, 4000);
          }
        } else {
          loudSince = 0;
          if (!quietSince) {
            quietSince = now;
          }

          // Arm the detector again only after the person has stopped speaking.
          if (
            !readyForSpeech &&
            now - quietSince >= 900
          ) {
            readyForSpeech = true;
          }
        }

        mutedSpeechAnimationRef.current =
          window.requestAnimationFrame(detectSpeech);
      };

      mutedSpeechAnimationRef.current =
        window.requestAnimationFrame(detectSpeech);
    } catch (monitoringError) {
      console.warn(
        "Could not monitor speech while muted:",
        monitoringError
      );
      stopMutedSpeechMonitoring();
    }
  };

  const toggleMicrophone = () => {
    const track =
      localStreamRef.current
        ?.getAudioTracks()[0];

    if (!track) {
      return;
    }

    const enabled =
      !track.enabled;

    if (enabled) {
      stopMutedSpeechMonitoring();
    } else {
      startMutedSpeechMonitoring(track);
    }

    track.enabled = enabled;

    const processedTrack =
      voiceProcessedStreamRef.current
        ?.getAudioTracks()[0];
    if (processedTrack) {
      processedTrack.enabled = enabled;
    }

    micEnabledStateRef.current =
      enabled;

    setMicEnabled(enabled);

    if (participantDocRef.current) {
      updateDoc(
        participantDocRef.current,
        { micEnabled: enabled }
      ).catch((microphoneStateError) => {
        console.warn(
          "Could not update microphone status:",
          microphoneStateError
        );
      });
    }
  };

  const toggleHandRaised = () => {
    const nextHandRaised = !handRaised;
    setHandRaised(nextHandRaised);

    if (participantDocRef.current) {
      updateDoc(
        participantDocRef.current,
        { handRaised: nextHandRaised }
      ).catch((handStateError) => {
        console.warn(
          "Could not update raised-hand status:",
          handStateError
        );
        setHandRaised(!nextHandRaised);
      });
    }
  };

  // =========================================================
  // SCREEN AUDIO CLEANUP
  // =========================================================

  const cleanupScreenAudioMix =
    () => {
      if (
        mixedAudioStreamRef.current
      ) {
        mixedAudioStreamRef.current
          .getTracks()
          .forEach(
            (track) =>
              track.stop()
          );

        mixedAudioStreamRef.current =
          null;
      }

      if (
        screenAudioContextRef.current
      ) {
        const context =
          screenAudioContextRef.current;

        screenAudioContextRef.current =
          null;

        context
          .close()
          .catch(() => {});
      }
    };

  // =========================================================
  // REPLACE OUTGOING VIDEO
  // =========================================================

  const replaceVideoTrackForAll =
    async (track) => {
      const jobs = [];

      peerConnectionsRef.current.forEach(
        (pc, participantId) => {
          const sender =
            pc
              .getSenders()
              .find(
                (item) =>
                  item.track?.kind ===
                  "video" && item !== cameraSendersRef.current.get(participantId)
              );

          if (sender) {
            jobs.push(
              sender.replaceTrack(
                track
              )
            );
          }
        }
      );

      await Promise.allSettled(
        jobs
      );
      const cameraTrack = getCameraVideoTrack();
      if (cameraTrack) {
        await Promise.allSettled([...cameraSendersRef.current.values()].map((sender) => sender.replaceTrack(cameraTrack)));
      }
    };

  // =========================================================
  // REPLACE OUTGOING AUDIO
  // =========================================================

  const replaceAudioTrackForAll =
    async (track) => {
      const jobs = [];

      peerConnectionsRef.current.forEach(
        (pc) => {
          const sender =
            pc
              .getSenders()
              .find(
                (item) =>
                  item.track?.kind ===
                  "audio"
              );

          if (sender) {
            jobs.push(
              sender.replaceTrack(
                track
              )
            );
          }
        }
      );

      await Promise.allSettled(
        jobs
      );
    };

  // =========================================================
  // WAIT FOR A REAL SCREEN-SHARE FRAME
  // =========================================================

  const waitForRealVideoFrame = (
    video,
    timeoutMs = 5000
  ) => {
    return new Promise(
      (resolve, reject) => {
        let finished = false;

        const timeout =
          setTimeout(() => {
            if (finished) {
              return;
            }

            finished = true;

            reject(
              new Error(
                "Timed out waiting for the screen-share frame."
              )
            );
          }, timeoutMs);

        const finish = () => {
          if (finished) {
            return;
          }

          finished = true;

          clearTimeout(timeout);

          resolve();
        };

        if (
          typeof video.requestVideoFrameCallback ===
          "function"
        ) {
          video.requestVideoFrameCallback(
            () => {
              video.requestVideoFrameCallback(
                finish
              );
            }
          );

          return;
        }

        const check = () => {
          if (
            video.readyState >= 2 &&
            video.videoWidth > 0 &&
            video.videoHeight > 0
          ) {
            requestAnimationFrame(
              () => {
                requestAnimationFrame(
                  finish
                );
              }
            );

            return;
          }

          if (!finished) {
            setTimeout(
              check,
              50
            );
          }
        };

        check();
      }
    );
  };

  // =========================================================
  // FROZEN SCREEN CLEANUP
  // =========================================================

  const stopFrozenScreenStream =
    () => {
      setFrozenScreenImage(null);
      if (
        frozenScreenStreamRef.current
      ) {
        frozenScreenStreamRef.current
          .getTracks()
          .forEach(
            (track) =>
              track.stop()
          );

        frozenScreenStreamRef.current =
          null;
      }

      if (
        frozenScreenVideoRef.current
      ) {
        try {
          frozenScreenVideoRef.current.pause();

          frozenScreenVideoRef.current.srcObject =
            null;
        } catch {
          // Ignore.
        }

        frozenScreenVideoRef.current =
          null;
      }

      freezeCanvasRef.current =
        null;
    };

  // =========================================================
  // FREEZE SCREEN
  // =========================================================

  const freezeScreenShare =
    async () => {
      try {
        if (
          !screenSharingStateRef.current ||
          !screenStreamRef.current ||
          screenFrozenStateRef.current
        ) {
          return;
        }

        const screenTrack =
          screenStreamRef.current
            .getVideoTracks()[0];

        if (!screenTrack) {
          return;
        }

        if (
          !screenReadyRef.current &&
          screenReadyPromiseRef.current
        ) {
          await screenReadyPromiseRef.current;
        }

        const previewVideo =
          document.createElement(
            "video"
          );

        previewVideo.muted = true;
        previewVideo.autoplay = true;
        previewVideo.playsInline = true;
        previewVideo.srcObject =
          screenStreamRef.current;

        frozenScreenVideoRef.current =
          previewVideo;

        await previewVideo.play();

        await waitForRealVideoFrame(
          previewVideo
        );

        const settings =
          screenTrack.getSettings();

        const width =
          previewVideo.videoWidth ||
          settings.width ||
          1280;

        const height =
          previewVideo.videoHeight ||
          settings.height ||
          720;

        if (
          width <= 0 ||
          height <= 0
        ) {
          throw new Error(
            "The screen has not produced a usable frame yet."
          );
        }

        const canvas =
          document.createElement(
            "canvas"
          );

        canvas.width = width;
        canvas.height = height;

        freezeCanvasRef.current =
          canvas;

        const context =
          canvas.getContext(
            "2d",
            {
              alpha: false
            }
          );

        if (!context) {
          throw new Error(
            "Could not create frozen screen."
          );
        }

        context.drawImage(
          previewVideo,
          0,
          0,
          width,
          height
        );

        await new Promise(
          (resolve) => {
            requestAnimationFrame(
              () => {
                requestAnimationFrame(
                  resolve
                );
              }
            );
          }
        );

        const frozenStream =
  canvas.captureStream(
    2
  );

        frozenScreenStreamRef.current =
          frozenStream;

        const frozenTrack =
          frozenStream
            .getVideoTracks()[0];

        if (!frozenTrack) {
          throw new Error(
            "Could not create frozen video track."
          );
        }

      

        await replaceVideoTrackForAll(
          frozenTrack
        );

        // A static canvas stream may not deliver another frame when the
        // local video is reattached. Show the exact captured frame directly.
        setFrozenScreenImage(canvas.toDataURL("image/png"));

        if (
          localVideoRef.current
        ) {
          localVideoRef.current.srcObject =
            frozenStream;

          localVideoRef.current
            .play()
            .catch(() => {});
        }

        screenFrozenStateRef.current =
          true;

        setIsScreenFrozen(true);

        setScreenShareMessage(
          "Screen is frozen for the other participants."
        );
      } catch (err) {
        console.error(
          "Freeze error:",
          err
        );

        stopFrozenScreenStream();

        screenFrozenStateRef.current =
          false;

        setIsScreenFrozen(false);

        setError(
          `Could not freeze screen: ${err.message}`
        );
      }
    };

  // =========================================================
  // RESUME SCREEN
  // =========================================================

  const unfreezeScreenShare =
    async () => {
      try {
        if (
          !screenStreamRef.current
        ) {
          return;
        }

        const liveTrack =
          screenStreamRef.current
            .getVideoTracks()[0];

        if (!liveTrack) {
          return;
        }

        if (
          screenPreviewVideoRef.current
        ) {
          await waitForRealVideoFrame(
            screenPreviewVideoRef.current,
            3000
          ).catch(
            () => {}
          );
        }

        await replaceVideoTrackForAll(
          liveTrack
        );

        stopFrozenScreenStream();

        screenFrozenStateRef.current =
          false;

        setIsScreenFrozen(false);

        if (
          localVideoRef.current
        ) {
          localVideoRef.current.srcObject =
            screenStreamRef.current;

          localVideoRef.current
            .play()
            .catch(() => {});
        }

        setScreenShareMessage(
          "Screen sharing is live again."
        );

        setTimeout(
          () => {
            setScreenShareMessage(
              ""
            );
          },
          1500
        );
      } catch (err) {
        console.error(
          "Resume error:",
          err
        );

        setError(
          `Could not resume screen: ${err.message}`
        );
      }
    };

  // =========================================================
  // CLOSE FLOATING CONTROLLER
  // =========================================================

  const closeFloatingController =
    async () => {
      // Electron uses a genuine independent BrowserWindow.
      if (isElectronDesktop) {
        try {
          await window.learnWithRyanDesktop
            ?.closeFloatingControls?.();
        } catch (err) {
          console.warn(
            "Could not close Electron floating controls:",
            err
          );
        }

        setPipOpen(false);
        return;
      }

      // Normal web version uses Document Picture-in-Picture.
      try {
        if (
          pipWindowRef.current &&
          !pipWindowRef.current.closed
        ) {
          pipWindowRef.current.close();
        }
      } catch {
        // Ignore.
      }

      pipWindowRef.current =
        null;

      pipControlsRef.current = {
        status: null,
        freezeButton: null,
        micButton: null,
        stopButton: null,
        participantsContainer: null,
        participantCount: null
      };

      setPipOpen(false);
    };

  // =========================================================
  // UPDATE FLOATING CONTROLLER
  // =========================================================

  const updateFloatingController =
    () => {
      const {
        status,
        freezeButton,
        micButton
      } =
        pipControlsRef.current;

      const frozen =
        screenFrozenStateRef.current;

      const micOn =
        micEnabledStateRef.current;

      if (status) {
        status.textContent =
          frozen
            ? "FROZEN"
            : "LIVE";

        status.style.color =
          frozen
            ? "#c4b5fd"
            : "#86efac";
      }

      if (freezeButton) {
        freezeButton.textContent =
          frozen
            ? "▶ Resume"
            : "❄ Freeze";

        freezeButton.style.background =
          frozen
            ? "#16a34a"
            : "#6d28d9";
      }

      if (micButton) {
        micButton.textContent =
          micOn
            ? "🎤 Mute"
            : "🔇 Unmute";

        micButton.style.background =
          micOn
            ? "#334155"
            : "#991b1b";
      }
    };

  // =========================================================
  // PARTICIPANTS IN FLOATING WINDOW
  // =========================================================

  const renderPipParticipants =
    () => {
      const pipWindow =
        pipWindowRef.current;

      const container =
        pipControlsRef.current
          .participantsContainer;

      const countLabel =
        pipControlsRef.current
          .participantCount;

      if (
        !pipWindow ||
        pipWindow.closed ||
        !container
      ) {
        return;
      }

      const pipDoc =
        pipWindow.document;

      while (
        container.firstChild
      ) {
        const child =
          container.firstChild;

        const video =
          child.querySelector?.(
            "video"
          );

        if (video) {
          video.srcObject = null;
        }

        container.removeChild(
          child
        );
      }

      const people =
        remoteParticipants;

      if (countLabel) {
        countLabel.textContent =
          people.length === 1
            ? "1 participant"
            : `${people.length} participants`;
      }

      if (
        people.length === 0
      ) {
        const empty =
          pipDoc.createElement(
            "div"
          );

        empty.className =
          "participants-empty";

        empty.textContent =
          "Waiting for another participant...";

        container.appendChild(
          empty
        );

        return;
      }

      people.forEach(
        (participant) => {
          const tile =
            pipDoc.createElement(
              "div"
            );

          tile.className =
            "participant-tile";

          const video =
            pipDoc.createElement(
              "video"
            );

          video.autoplay = true;
          video.playsInline = true;

          // Prevent duplicate audio.
          video.muted = true;

          const stream =
            remoteStreamsRef.current.get(
              participant.id
            );

          if (stream) {
            video.srcObject =
              stream;

            video
              .play()
              .catch(() => {});
          }

          const nameBar =
            pipDoc.createElement(
              "div"
            );

          nameBar.className =
            "participant-label";

          const dot =
            pipDoc.createElement(
              "span"
            );

          dot.className =
            participant.connected
              ? "connected-dot"
              : "connecting-dot";

          const name =
            pipDoc.createElement(
              "span"
            );

          name.textContent =
            participant.name ||
            "Participant";

          nameBar.appendChild(dot);
          nameBar.appendChild(name);

          tile.appendChild(video);
          tile.appendChild(nameBar);

          container.appendChild(
            tile
          );
        }
      );
    };

  // =========================================================
  // OPEN FLOATING CONTROLLER
  // =========================================================

  const openFloatingController =
    async () => {
      // =====================================================
      // ELECTRON DESKTOP VERSION
      // =====================================================

      if (isElectronDesktop) {
        try {
          await window.learnWithRyanDesktop
            .openFloatingControls();

          setPipOpen(true);
          return true;
        } catch (err) {
          console.error(
            "Could not open Electron floating controls:",
            err
          );

          setError(
            `Could not open floating controls: ${err.message}`
          );

          return false;
        }
      }

      // =====================================================
      // NORMAL WEB BROWSER VERSION
      // =====================================================

      try {
        if (
          !(
            "documentPictureInPicture" in
            window
          )
        ) {
          return false;
        }

        if (
          pipWindowRef.current &&
          !pipWindowRef.current.closed
        ) {
          pipWindowRef.current.focus();

          return true;
        }

        // Small default floating window.
        const pipWindow =
          await window.documentPictureInPicture.requestWindow({
            width: 320,
            height: 260
          });

        pipWindowRef.current =
          pipWindow;

        setPipOpen(true);

        const pipDoc =
          pipWindow.document;

        pipDoc.title =
          "Lesson Screen Share";

        const style =
          pipDoc.createElement(
            "style"
          );

        style.textContent = `
          * {
            box-sizing: border-box;
          }

          html,
          body {
            margin: 0;
            width: 100%;
            height: 100%;
          }

          body {
            background: #0f172a;
            color: white;

            font-family:
              system-ui,
              -apple-system,
              BlinkMacSystemFont,
              "Segoe UI",
              sans-serif;

            overflow: hidden;
          }

          .controller {
            width: 100%;
            height: 100%;

            padding: 8px;

            display: flex;
            flex-direction: column;
          }

          .top {
            flex-shrink: 0;

            display: flex;
            justify-content: space-between;
            align-items: center;

            margin-bottom: 7px;
          }

          .title-wrap {
            display: flex;
            align-items: center;
            gap: 7px;
          }

          .screen-icon {
            font-size: 17px;
          }

          .small-title {
            font-size: 8px;
            letter-spacing: .8px;
            opacity: .6;
          }

          .status {
            font-size: 11px;
            font-weight: 800;
            margin-top: 2px;
          }

          .top-right {
            display: flex;
            align-items: center;
            gap: 6px;

            font-size: 9px;
            opacity: .7;
          }

          .share-dot {
            width: 7px;
            height: 7px;

            background: #22c55e;

            border-radius: 50%;
          }

          .participants {
            flex: 1;
            min-height: 0;

            display: grid;

            grid-template-columns:
              repeat(
                auto-fit,
                minmax(100px, 1fr)
              );

            gap: 5px;

            overflow-y: auto;

            padding-bottom: 5px;
          }

          .participant-tile {
            position: relative;

            min-height: 80px;

            aspect-ratio: 16 / 9;

            overflow: hidden;

            border-radius: 8px;

            background: #020617;

            border:
              1px solid
              rgba(255,255,255,.1);
          }

          .participant-tile video {
            position: absolute;
            inset: 0;

            width: 100%;
            height: 100%;

            object-fit: cover;

            background: #020617;
          }

          .participant-label {
            position: absolute;

            left: 5px;
            bottom: 5px;

            z-index: 3;

            display: flex;
            align-items: center;
            gap: 4px;

            max-width:
              calc(100% - 10px);

            padding: 3px 5px;

            border-radius: 5px;

            background:
              rgba(2,6,23,.72);

            color: white;

            font-size: 8px;
            font-weight: 600;
          }

          .connected-dot,
          .connecting-dot {
            width: 5px;
            height: 5px;
            border-radius: 50%;
          }

          .connected-dot {
            background: #22c55e;
          }

          .connecting-dot {
            background: #f59e0b;
          }

          .participants-empty {
            display: flex;
            align-items: center;
            justify-content: center;

            min-height: 85px;

            border-radius: 8px;

            background: #020617;

            font-size: 10px;
            opacity: .55;
          }

          .buttons {
            flex-shrink: 0;

            display: flex;

            gap: 5px;

            width: 100%;

            padding-top: 6px;

            border-top:
              1px solid
              rgba(255,255,255,.1);
          }

          button {
            flex: 1;

            border: none;

            border-radius: 7px;

            padding: 7px 5px;

            color: white;

            font-size: 9px;
            font-weight: 700;

            cursor: pointer;

            white-space: nowrap;
          }

          button:hover {
            opacity: .9;
          }

          .freeze {
            background: #6d28d9;
          }

          .mic {
            background: #334155;
          }

          .stop {
            background: #dc2626;
          }
        `;

        pipDoc.head.appendChild(
          style
        );

        const controller =
          pipDoc.createElement(
            "div"
          );

        controller.className =
          "controller";

        // HEADER

        const top =
          pipDoc.createElement(
            "div"
          );

        top.className =
          "top";

        const titleWrap =
          pipDoc.createElement(
            "div"
          );

        titleWrap.className =
          "title-wrap";

        const icon =
          pipDoc.createElement(
            "div"
          );

        icon.className =
          "screen-icon";

        icon.textContent =
          "🖥";

        const textWrap =
          pipDoc.createElement(
            "div"
          );

        const smallTitle =
          pipDoc.createElement(
            "div"
          );

        smallTitle.className =
          "small-title";

        smallTitle.textContent =
          "SCREEN SHARE";

        const status =
          pipDoc.createElement(
            "div"
          );

        status.className =
          "status";

        textWrap.appendChild(
          smallTitle
        );

        textWrap.appendChild(
          status
        );

        titleWrap.appendChild(
          icon
        );

        titleWrap.appendChild(
          textWrap
        );

        const topRight =
          pipDoc.createElement(
            "div"
          );

        topRight.className =
          "top-right";

        const participantCount =
          pipDoc.createElement(
            "span"
          );

        const shareDot =
          pipDoc.createElement(
            "div"
          );

        shareDot.className =
          "share-dot";

        topRight.appendChild(
          participantCount
        );

        topRight.appendChild(
          shareDot
        );

        top.appendChild(
          titleWrap
        );

        top.appendChild(
          topRight
        );

        // PARTICIPANTS

        const participantsContainer =
          pipDoc.createElement(
            "div"
          );

        participantsContainer.className =
          "participants";

        // BUTTONS

        const buttons =
          pipDoc.createElement(
            "div"
          );

        buttons.className =
          "buttons";

        const freezeButton =
          pipDoc.createElement(
            "button"
          );

        freezeButton.className =
          "freeze";

        freezeButton.addEventListener(
          "click",
          async () => {
            if (
              screenFrozenStateRef.current
            ) {
              await unfreezeScreenShare();
            } else {
              await freezeScreenShare();
            }

            setTimeout(
              updateFloatingController,
              0
            );
          }
        );

        const micButton =
          pipDoc.createElement(
            "button"
          );

        micButton.className =
          "mic";

        micButton.addEventListener(
          "click",
          () => {
            toggleMicrophone();

            setTimeout(
              updateFloatingController,
              0
            );
          }
        );

        const stopButton =
          pipDoc.createElement(
            "button"
          );

        stopButton.className =
          "stop";

        stopButton.textContent =
          "⏹ Stop";

        stopButton.addEventListener(
          "click",
          () => {
            stopScreenShare();
          }
        );

        buttons.appendChild(
          freezeButton
        );

        buttons.appendChild(
          micButton
        );

        buttons.appendChild(
          stopButton
        );

        controller.appendChild(
          top
        );

        controller.appendChild(
          participantsContainer
        );

        controller.appendChild(
          buttons
        );

        pipDoc.body.appendChild(
          controller
        );

        pipControlsRef.current = {
          status,
          freezeButton,
          micButton,
          stopButton,
          participantsContainer,
          participantCount
        };

        pipWindow.addEventListener(
          "pagehide",
          () => {
            pipWindowRef.current =
              null;

            pipControlsRef.current = {
              status: null,
              freezeButton: null,
              micButton: null,
              stopButton: null,
              participantsContainer:
                null,
              participantCount:
                null
            };

            setPipOpen(false);
          }
        );

        updateFloatingController();

        setTimeout(
          renderPipParticipants,
          0
        );

        return true;
      } catch (err) {
        console.warn(
          "Floating controller error:",
          err
        );

        setPipOpen(false);

        return false;
      }
    };

  // =========================================================
  // KEEP PIP UPDATED
  // =========================================================

  useEffect(() => {
    updateFloatingController();
  }, [
    isScreenFrozen,
    micEnabled,
    pipOpen
  ]);

  useEffect(() => {
    if (
      !pipOpen ||
      !pipWindowRef.current
    ) {
      return;
    }

    const timer =
      setTimeout(
        renderPipParticipants,
        50
      );

    return () =>
      clearTimeout(timer);
  }, [
    remoteParticipants,
    pipOpen
  ]);

  // =========================================================
  // ELECTRON NATIVE FLOATING CONTROLS
  // =========================================================

  // Keep the native Electron floating window informed about
  // microphone/freeze/share state.
  useEffect(() => {
    if (
      !isElectronDesktop ||
      !window.learnWithRyanDesktop
        ?.setFloatingState
    ) {
      return;
    }

    window.learnWithRyanDesktop
      .setFloatingState({
        micEnabled,
        handRaised,
        isScreenFrozen,
        isScreenSharing,
        screenRecordingStatus,
        screenRecordingTime: formatScreenRecordingTime(screenRecordingSeconds)
      });
  }, [
    isElectronDesktop,
    micEnabled,
    handRaised,
    isScreenFrozen,
    isScreenSharing,
    screenRecordingStatus,
    screenRecordingSeconds
  ]);

  // Send the participant roster and lightweight live camera
  // preview frames to the separate Electron floating window.
  // The actual lesson remains full-quality WebRTC; these JPEG
  // frames are only for the small always-on-top preview tiles.
  useEffect(() => {
    if (
      !isElectronDesktop ||
      !isScreenSharing ||
      !pipOpen ||
      !window.learnWithRyanDesktop
        ?.setFloatingParticipants ||
      !window.learnWithRyanDesktop
        ?.sendFloatingParticipantFrame
    ) {
      return;
    }

    const desktop =
      window.learnWithRyanDesktop;

    const roster =
      remoteParticipants.map(
        (participant) => ({
          id: participant.id,
          name:
            participant.name ||
            "Participant",
          connected:
            !!participant.connected
        })
      );

    desktop.setFloatingParticipants(
      roster
    );

    const cache =
      floatingPreviewCacheRef.current;

    const wantedIds =
      new Set(
        roster.map(
          (participant) =>
            participant.id
        )
      );

    cache.forEach(
      (entry, participantId) => {
        if (
          !wantedIds.has(
            participantId
          )
        ) {
          try {
            entry.video.pause();
            entry.video.srcObject =
              null;
          } catch {
            // Ignore.
          }

          cache.delete(
            participantId
          );
        }
      }
    );

    const ensureEntry =
      (participantId) => {
        const stream =
          remoteStreamsRef.current.get(
            participantId
          );

        if (!stream) {
          return null;
        }

        let entry =
          cache.get(
            participantId
          );

        if (!entry) {
          const video =
            document.createElement(
              "video"
            );

          video.autoplay = true;
          video.muted = true;
          video.playsInline = true;
          video.srcObject = stream;

          video
            .play()
            .catch(() => {});

          const canvas =
            document.createElement(
              "canvas"
            );

          canvas.width = 320;
          canvas.height = 180;

          entry = {
            video,
            canvas,
            context:
              canvas.getContext(
                "2d"
              )
          };

          cache.set(
            participantId,
            entry
          );
        } else if (
          entry.video.srcObject !==
          stream
        ) {
          entry.video.srcObject =
            stream;

          entry.video
            .play()
            .catch(() => {});
        }

        return entry;
      };

    const sendFrames = () => {
      roster.forEach(
        (participant) => {
          const entry =
            ensureEntry(
              participant.id
            );

          if (
            !entry ||
            !entry.context ||
            entry.video.readyState < 2
          ) {
            return;
          }

          try {
            entry.context.drawImage(
              entry.video,
              0,
              0,
              entry.canvas.width,
              entry.canvas.height
            );

            const frame =
              entry.canvas.toDataURL(
                "image/jpeg",
                0.62
              );

            desktop
              .sendFloatingParticipantFrame({
                id: participant.id,
                frame
              });
          } catch (err) {
            console.warn(
              "Could not send floating participant preview:",
              err
            );
          }
        }
      );
    };

    sendFrames();

    const timer =
      window.setInterval(
        sendFrames,
        300
      );

    return () => {
      window.clearInterval(
        timer
      );
    };
  }, [
    isElectronDesktop,
    isScreenSharing,
    pipOpen,
    remoteParticipants
  ]);

  // =========================================================
  // SCREEN-SHARE RECORDING — DESKTOP TUTOR ONLY
  // =========================================================

  const formatScreenRecordingTime = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  };

  const clearScreenRecordingTimer = () => {
    if (screenRecordingTimerRef.current) {
      window.clearInterval(screenRecordingTimerRef.current);
      screenRecordingTimerRef.current = null;
    }
  };

  const broadcastScreenRecordingStatus = (status) => {
    screenRecordingStatusRef.current = status;
    setScreenRecordingStatus(status);
    broadcastWhiteboardMessage({
      type: "screen-recording-status",
      status
    });
  };

  const showScreenRecordingNotice = (status) => {
    if (isElectronDesktop) return;
    if (screenRecordingNoticeTimerRef.current) {
      window.clearTimeout(screenRecordingNoticeTimerRef.current);
    }
    if (screenRecordingNoticeRemoveTimerRef.current) {
      window.clearTimeout(screenRecordingNoticeRemoveTimerRef.current);
    }

    const kind = status === "recording"
      ? "started"
      : status === "paused"
        ? "paused"
        : status === "resumed"
          ? "resumed"
          : "stopped";
    setScreenRecordingNotice({ kind, visible: true, id: Date.now() });
    screenRecordingNoticeTimerRef.current = window.setTimeout(() => {
      setScreenRecordingNotice((notice) =>
        notice ? { ...notice, visible: false } : null
      );
      screenRecordingNoticeRemoveTimerRef.current = window.setTimeout(
        () => setScreenRecordingNotice(null),
        450
      );
    }, 5000);
  };

  const waitForRecordingVideo = async (video) => {
    if (video.readyState >= 2 && video.videoWidth > 0) {
      await video.play().catch(() => {});
      return;
    }

    await new Promise((resolve, reject) => {
      const timeout = window.setTimeout(
        () => reject(new Error("A recording video source did not become ready.")),
        5000
      );

      const finish = () => {
        window.clearTimeout(timeout);
        video.removeEventListener("loadeddata", finish);
        video.removeEventListener("error", fail);
        video.play().catch(() => {});
        resolve();
      };

      const fail = () => {
        window.clearTimeout(timeout);
        video.removeEventListener("loadeddata", finish);
        video.removeEventListener("error", fail);
        reject(new Error("A recording video source could not be loaded."));
      };

      video.addEventListener("loadeddata", finish, { once: true });
      video.addEventListener("error", fail, { once: true });
      video.play().catch(() => {});
    });
  };

  const createCameraScreenRecordingStream = async (
    sharedVideoTrack,
    cameraVideoTrack
  ) => {
    const screenVideo = document.createElement("video");
    const cameraVideo = document.createElement("video");

    [screenVideo, cameraVideo].forEach((video) => {
      video.muted = true;
      video.playsInline = true;
      video.autoplay = true;
    });

    screenVideo.srcObject = new MediaStream([sharedVideoTrack]);
    cameraVideo.srcObject = new MediaStream([cameraVideoTrack]);

    await Promise.all([
      waitForRecordingVideo(screenVideo),
      waitForRecordingVideo(cameraVideo)
    ]);

    const sharedSettings = sharedVideoTrack.getSettings?.() || {};
    const width = Math.max(
      640,
      Number(sharedSettings.width) || screenVideo.videoWidth || 1920
    );
    const height = Math.max(
      360,
      Number(sharedSettings.height) || screenVideo.videoHeight || 1080
    );

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext("2d", {
      alpha: false,
      desynchronized: true
    });

    if (!context) {
      throw new Error("The recording canvas could not be created.");
    }

    screenRecordingCanvasRef.current = canvas;
    screenRecordingScreenVideoRef.current = screenVideo;
    screenRecordingCameraVideoRef.current = cameraVideo;

    const drawRoundedRectangle = (x, y, boxWidth, boxHeight, radius) => {
      const safeRadius = Math.min(radius, boxWidth / 2, boxHeight / 2);
      context.beginPath();
      context.moveTo(x + safeRadius, y);
      context.lineTo(x + boxWidth - safeRadius, y);
      context.quadraticCurveTo(x + boxWidth, y, x + boxWidth, y + safeRadius);
      context.lineTo(x + boxWidth, y + boxHeight - safeRadius);
      context.quadraticCurveTo(
        x + boxWidth,
        y + boxHeight,
        x + boxWidth - safeRadius,
        y + boxHeight
      );
      context.lineTo(x + safeRadius, y + boxHeight);
      context.quadraticCurveTo(x, y + boxHeight, x, y + boxHeight - safeRadius);
      context.lineTo(x, y + safeRadius);
      context.quadraticCurveTo(x, y, x + safeRadius, y);
      context.closePath();
    };

    const drawFrame = () => {
      if (!screenRecordingCanvasRef.current) {
        return;
      }

      context.fillStyle = "#000000";
      context.fillRect(0, 0, width, height);
      context.drawImage(screenVideo, 0, 0, width, height);

      // Keep the recording-only camera compact and less wide than the live
      // camera tile. It occupies approximately 18% of the captured width.
      const cameraWidth = Math.round(width * 0.18);
      const cameraHeight = Math.round(cameraWidth * 9 / 16);
      const margin = Math.max(16, Math.round(width * 0.015));
      const cameraX = width - cameraWidth - margin;
      const cameraY = height - cameraHeight - margin;
      const radius = Math.max(10, Math.round(cameraWidth * 0.045));

      context.save();
      context.shadowColor = "rgba(0,0,0,.42)";
      context.shadowBlur = Math.max(12, Math.round(cameraWidth * 0.055));
      context.shadowOffsetY = Math.max(4, Math.round(cameraWidth * 0.018));
      context.fillStyle = "#0f172a";
      drawRoundedRectangle(cameraX, cameraY, cameraWidth, cameraHeight, radius);
      context.fill();
      context.restore();

      context.save();
      drawRoundedRectangle(cameraX, cameraY, cameraWidth, cameraHeight, radius);
      context.clip();

      const sourceWidth = cameraVideo.videoWidth || 16;
      const sourceHeight = cameraVideo.videoHeight || 9;
      const sourceRatio = sourceWidth / sourceHeight;
      const destinationRatio = cameraWidth / cameraHeight;
      let cropX = 0;
      let cropY = 0;
      let cropWidth = sourceWidth;
      let cropHeight = sourceHeight;

      if (sourceRatio > destinationRatio) {
        cropWidth = sourceHeight * destinationRatio;
        cropX = (sourceWidth - cropWidth) / 2;
      } else {
        cropHeight = sourceWidth / destinationRatio;
        cropY = (sourceHeight - cropHeight) / 2;
      }

      // Mirror the tutor camera to match the familiar local preview.
      context.translate(cameraX + cameraWidth, cameraY);
      context.scale(-1, 1);
      context.drawImage(
        cameraVideo,
        cropX,
        cropY,
        cropWidth,
        cropHeight,
        0,
        0,
        cameraWidth,
        cameraHeight
      );
      context.restore();

      context.save();
      context.strokeStyle = "rgba(255,255,255,.88)";
      context.lineWidth = Math.max(2, Math.round(width * 0.0015));
      drawRoundedRectangle(cameraX, cameraY, cameraWidth, cameraHeight, radius);
      context.stroke();
      context.restore();

      screenRecordingAnimationRef.current =
        window.requestAnimationFrame(drawFrame);
    };

    drawFrame();

    const canvasStream = canvas.captureStream(30);
    const canvasTrack = canvasStream.getVideoTracks()[0];

    if (!canvasTrack) {
      throw new Error("The combined recording video could not be created.");
    }

    return new MediaStream([canvasTrack]);
  };

  const cleanupScreenRecordingMedia = () => {
    if (screenRecordingAnimationRef.current) {
      window.cancelAnimationFrame(screenRecordingAnimationRef.current);
      screenRecordingAnimationRef.current = null;
    }

    [
      screenRecordingScreenVideoRef,
      screenRecordingCameraVideoRef
    ].forEach((videoRef) => {
      const video = videoRef.current;
      if (video) {
        try {
          video.pause();
          video.srcObject = null;
        } catch {}
      }
      videoRef.current = null;
    });

    screenRecordingCanvasRef.current = null;
    screenRecordingStreamRef.current?.getTracks().forEach((track) => track.stop());
    screenRecordingStreamRef.current = null;
    screenRecordingAudioSourcesRef.current.forEach((source) => {
      try { source.disconnect(); } catch {}
    });
    screenRecordingAudioSourcesRef.current = [];
    const context = screenRecordingAudioContextRef.current;
    screenRecordingAudioContextRef.current = null;
    if (context && context.state !== "closed") context.close().catch(() => {});
  };

  const downloadScreenRecording = (chunks, mimeType) =>
    saveRecording(chunks, mimeType, "lesson-screen-recording", setScreenShareMessage);

  const startScreenRecording = async (includeCamera = false) => {
    if (
      !isElectronDesktop ||
      !screenSharingStateRef.current ||
      screenRecordingStatusRef.current !== "idle"
    ) return;

    const sharedVideoTrack = screenStreamRef.current?.getVideoTracks()[0];
    if (!sharedVideoTrack || !window.MediaRecorder) {
      setScreenShareMessage("Screen recording is not supported on this setup.");
      return;
    }

    try {
      const cameraVideoTrack = includeCamera
        ? getCameraVideoTrack()
        : null;

      if (
        includeCamera &&
        (
          !cameraVideoTrack ||
          cameraVideoTrack.readyState !== "live" ||
          cameraVideoTrack.enabled === false
        )
      ) {
        setScreenShareMessage(
          "Your camera is not currently available, so this recording will " +
          "contain the shared screen only."
        );
      }

      const recordingStream =
        includeCamera &&
        cameraVideoTrack &&
        cameraVideoTrack.readyState === "live" &&
        cameraVideoTrack.enabled !== false
          ? await createCameraScreenRecordingStream(
              sharedVideoTrack,
              cameraVideoTrack
            )
          : new MediaStream([sharedVideoTrack.clone()]);
      const localMicTrack =
        voiceProcessedStreamRef.current?.getAudioTracks()[0] ||
        localStreamRef.current?.getAudioTracks()[0];
      const remoteMicTracks = Array.from(remoteStreamsRef.current.values())
        .flatMap((stream) => stream?.getAudioTracks() || []);
      const sharedAudioTrack = screenStreamRef.current?.getAudioTracks()[0];
      const audioTracks = [localMicTrack, ...remoteMicTracks, sharedAudioTrack]
        .filter((track, index, tracks) =>
          track?.readyState === "live" &&
          tracks.findIndex((candidate) => candidate?.id === track.id) === index
        );

      if (audioTracks.length) {
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (AudioContextClass) {
          const context = new AudioContextClass();
          const destination = context.createMediaStreamDestination();
          const sources = audioTracks.map((track) => {
            const source = context.createMediaStreamSource(new MediaStream([track]));
            source.connect(destination);
            return source;
          });
          if (context.state === "suspended") await context.resume();
          const mixedTrack = destination.stream.getAudioTracks()[0];
          if (mixedTrack) {
            recordingStream.addTrack(mixedTrack);
            screenRecordingAudioContextRef.current = context;
            screenRecordingAudioSourcesRef.current = sources;
          }
        }
      }

      const mimeType = chooseRecordingMimeType(MediaRecorder, {
        hasAudio: recordingStream.getAudioTracks().length > 0,
        requireMp4: isElectronDesktop
      });
      const recorder = new MediaRecorder(recordingStream, {
        mimeType,
        videoBitsPerSecond: 6000000,
        audioBitsPerSecond: 128000
      });

      screenRecordingStreamRef.current = recordingStream;
      screenRecordingChunksRef.current = [];
      screenRecorderRef.current = recorder;
      recorder.ondataavailable = (event) => {
        if (event.data?.size) screenRecordingChunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        clearScreenRecordingTimer();
        void downloadScreenRecording([...screenRecordingChunksRef.current], recorder.mimeType || mimeType);
        screenRecordingChunksRef.current = [];
        cleanupScreenRecordingMedia();
        screenRecorderRef.current = null;
        broadcastScreenRecordingStatus("idle");
        setScreenRecordingSeconds(0);
      };
      recorder.start(1000);
      setScreenRecordingSeconds(0);
      broadcastScreenRecordingStatus("recording");
      screenRecordingTimerRef.current = window.setInterval(() => {
        setScreenRecordingSeconds((seconds) => seconds + 1);
      }, 1000);
    } catch (recordingError) {
      console.error("Could not record the shared screen:", recordingError);
      clearScreenRecordingTimer();
      cleanupScreenRecordingMedia();
      screenRecorderRef.current = null;
      broadcastScreenRecordingStatus("idle");
      setScreenShareMessage(recordingError.code === "RECORDING_FORMAT_UNSUPPORTED" ? recordingError.message : "The screen recording could not start.");
    }
  };

  const toggleScreenRecordingPause = () => {
    const recorder = screenRecorderRef.current;
    if (!recorder) return;
    if (recorder.state === "recording") {
      recorder.pause();
      clearScreenRecordingTimer();
      screenRecordingStatusRef.current = "paused";
      setScreenRecordingStatus("paused");
      broadcastWhiteboardMessage({ type: "screen-recording-status", status: "paused" });
    } else if (recorder.state === "paused") {
      recorder.resume();
      screenRecordingStatusRef.current = "recording";
      setScreenRecordingStatus("recording");
      broadcastWhiteboardMessage({ type: "screen-recording-status", status: "resumed" });
      screenRecordingTimerRef.current = window.setInterval(() => {
        setScreenRecordingSeconds((seconds) => seconds + 1);
      }, 1000);
    }
  };

  const stopScreenRecording = () => {
    clearScreenRecordingTimer();
    const recorder = screenRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
    } else {
      cleanupScreenRecordingMedia();
      screenRecorderRef.current = null;
      broadcastScreenRecordingStatus("idle");
      setScreenRecordingSeconds(0);
    }
  };

  // =========================================================
  // START SCREEN SHARE
  // =========================================================

  const startScreenShare =
    async () => {
      try {
        setError("");
        setScreenShareMessage("");

        if (
          peerConnectionsRef.current.size ===
          0
        ) {
          setError(
            "Wait until at least one other participant has joined before sharing your screen."
          );

          return;
        }

        if (
          !navigator.mediaDevices
            ?.getDisplayMedia
        ) {
          setError(
            "Screen sharing is not supported by this browser."
          );

          return;
        }

        let screenStream = null;


        try {
       screenStream =
  await navigator.mediaDevices.getDisplayMedia({
    video: true,
    audio: true
  });
        } catch (audioError) {
  console.warn(
    "Screen audio capture failed:",
    audioError
  );

  /*
    User cancelled the picker.
  */
  if (
    audioError.name === "NotAllowedError" ||
    audioError.name === "AbortError"
  ) {
    return;
  }

  /*
    System-audio capture failed.

    Do NOT immediately call getDisplayMedia()
    again, because that would reopen the
    screen picker a second time.
  */
  if (
    audioError.name === "NotReadableError"
  ) {
    setError(
      "Your screen could not be shared with system audio. Please try again."
    );

    return;
  }

  throw audioError;
}

        if (!screenStream) {
          return;
        }

        const screenVideoTrack =
          screenStream
            .getVideoTracks()[0];

        if (!screenVideoTrack) {
          screenStream
            .getTracks()
            .forEach(
              (track) =>
                track.stop()
            );

          throw new Error(
            "No screen video track was returned."
          );
        }

        const readinessVideo =
          document.createElement(
            "video"
          );

        readinessVideo.muted = true;
        readinessVideo.autoplay = true;
        readinessVideo.playsInline = true;
        readinessVideo.srcObject =
          screenStream;

        screenPreviewVideoRef.current =
          readinessVideo;

        screenReadyRef.current =
          false;

        const readyPromise =
          (async () => {
            await readinessVideo.play();

            await waitForRealVideoFrame(
              readinessVideo
            );

            screenReadyRef.current =
              true;

            return true;
          })();

        screenReadyPromiseRef.current =
          readyPromise;

        await readyPromise;

        screenStreamRef.current =
          screenStream;

        await replaceVideoTrackForAll(
          screenVideoTrack
        );

        // ===================================================
        // SCREEN AUDIO
        // ===================================================

        const screenAudioTrack =
          screenStream
            .getAudioTracks()[0];

            console.log(
  "SCREEN AUDIO TRACKS:",
  screenStream.getAudioTracks()
);

        const microphoneTrack =
          getCurrentOutgoingAudioTrack();

        if (screenAudioTrack) {
          try {
            cleanupScreenAudioMix();

            const AudioContextClass =
              window.AudioContext ||
              window.webkitAudioContext;

            if (AudioContextClass) {
              const audioContext =
                new AudioContextClass();

              if (
                audioContext.state ===
                "suspended"
              ) {
                await audioContext.resume();
              }

              const destination =
                audioContext.createMediaStreamDestination();

              if (microphoneTrack) {
                const micStream =
                  new MediaStream(
                    [microphoneTrack]
                  );

                const micSource =
                  audioContext.createMediaStreamSource(
                    micStream
                  );

                micSource.connect(
                  destination
                );
              }

              const sharedAudioStream =
                new MediaStream(
                  [screenAudioTrack]
                );

              const screenSource =
                audioContext.createMediaStreamSource(
                  sharedAudioStream
                );

              screenSource.connect(
                destination
              );

              const mixedTrack =
                destination.stream
                  .getAudioTracks()[0];

              if (mixedTrack) {
                mixedAudioStreamRef.current =
                  destination.stream;

                screenAudioContextRef.current =
                  audioContext;

                await replaceAudioTrackForAll(
                  mixedTrack
                );

                setScreenShareMessage(
                  "Screen sharing with audio. Your microphone is still live."
                );
              }
            }
          } catch (mixError) {
            console.error(
              "Audio mixing error:",
              mixError
            );

            cleanupScreenAudioMix();

            if (microphoneTrack) {
              await replaceAudioTrackForAll(
                microphoneTrack
              );
            }

            setScreenShareMessage(
              "Screen sharing is active, but shared audio could not be mixed. Your microphone is still live."
            );
          }
        
        } else {
          setScreenShareMessage(
            "Screen sharing is active. No shared audio track was supplied by the browser."
          );
        }

        screenSharingStateRef.current =
          true;

        screenFrozenStateRef.current =
          false;

        setIsScreenSharing(true);
        setIsScreenFrozen(false);

        if (participantDocRef.current) {
          try {
            await updateDoc(
              participantDocRef.current,
              {
                isScreenSharing: true,
                lastSeen: serverTimestamp()
              }
            );
          } catch (presenceError) {
            console.warn(
              "Could not update screen-share presence:",
              presenceError
            );
          }
        }

        screenVideoTrack.onended =
          () => {
            stopScreenShare();
          };

        await openFloatingController();
      } catch (err) {
        console.error(
          "Screen share error:",
          err
        );

        screenReadyRef.current =
          false;

        screenReadyPromiseRef.current =
          null;

        if (
          screenPreviewVideoRef.current
        ) {
          try {
            screenPreviewVideoRef.current.pause();
            screenPreviewVideoRef.current.srcObject =
              null;
          } catch {
            // Ignore.
          }

          screenPreviewVideoRef.current =
            null;
        }

        if (
          err.name !==
            "NotAllowedError" &&
          err.name !==
            "AbortError"
        ) {
          setError(
            `Could not share screen: ${err.message}`
          );
        }
      }
    };

  // =========================================================
  // STOP SCREEN SHARE
  // =========================================================

  const stopScreenShare =
    async () => {
      try {
        if (screenRecordingStatusRef.current !== "idle") {
          stopScreenRecording();
        }

        screenSharingStateRef.current =
          false;

        screenFrozenStateRef.current =
          false;

        // Tell the other participants to leave presentation mode.
        if (participantDocRef.current) {
          try {
            await updateDoc(
              participantDocRef.current,
              {
                isScreenSharing:
                  false,

                lastSeen:
                  serverTimestamp()
              }
            );
          } catch (presenceError) {
            console.warn(
              "Could not clear screen-share presence:",
              presenceError
            );
          }
        }

        await closeFloatingController();

        stopFrozenScreenStream();

        setIsScreenFrozen(false);

        const cameraTrack =
          getCameraVideoTrack();

        const microphoneTrack =
          getCurrentOutgoingAudioTrack();

        if (
          cameraTrack
        ) {
          await replaceVideoTrackForAll(
            cameraTrack
          );
        }

        if (
          microphoneTrack
        ) {
          await replaceAudioTrackForAll(
            microphoneTrack
          );
        }

        cleanupScreenAudioMix();

        if (
          screenStreamRef.current
        ) {
          const stream =
            screenStreamRef.current;

          screenStreamRef.current =
            null;

          stream
            .getTracks()
            .forEach(
              (track) => {
                track.onended =
                  null;

                track.stop();
              }
            );
        }

        /*
          This causes the local preview effect to
          automatically return to the selected camera stream
          (raw camera or processed background-blur stream).
        */

        screenReadyRef.current =
          false;

        screenReadyPromiseRef.current =
          null;

        if (
          screenPreviewVideoRef.current
        ) {
          try {
            screenPreviewVideoRef.current.pause();

            screenPreviewVideoRef.current.srcObject =
              null;
          } catch {
            // Ignore.
          }

          screenPreviewVideoRef.current =
            null;
        }

        setIsScreenSharing(false);

        setScreenShareMessage("");
      } catch (err) {
        console.error(
          "Stop share error:",
          err
        );

        setError(
          `Could not stop screen sharing: ${err.message}`
        );
      }
    };

  // =========================================================
  // STOP LOCAL MEDIA
  // =========================================================

  const stopLocalMedia =
    () => {
      stopBackgroundProcessing();
      cleanupVoiceProcessing();
      cleanupMicrophoneTest();
      stopMutedSpeechMonitoring();

      if (
        localStreamRef.current
      ) {
        localStreamRef.current
          .getTracks()
          .forEach(
            (track) =>
              track.stop()
          );

        localStreamRef.current =
          null;
      }

      if (
        localVideoRef.current
      ) {
        localVideoRef.current.srcObject =
          null;
      }

      setCameraStarted(false);
      setCameraEnabled(false);
      setMicEnabled(false);
      setHandRaised(false);
    };

  // =========================================================
  // ELECTRON COMMANDS FROM NATIVE FLOATING WINDOW
  // =========================================================

  useEffect(() => {
    const desktop =
      window.learnWithRyanDesktop;

    if (
      !isElectronDesktop ||
      typeof desktop?.onDesktopCommand !==
        "function"
    ) {
      return;
    }

    const unsubscribe =
      desktop.onDesktopCommand(
        async (message) => {
          const command =
            typeof message === "string"
              ? message
              : message?.command;
          const payload =
            typeof message === "object" &&
            message
              ? message.payload || {}
              : {};

          if (command === "toggle-mic") {
            toggleMicrophone();
            return;
          }

          if (command === "toggle-freeze") {
            if (
              screenFrozenStateRef.current
            ) {
              await unfreezeScreenShare();
            } else {
              await freezeScreenShare();
            }
            return;
          }

          if (command === "stop-share") {
            await stopScreenShare();
            return;
          }

          if (command === "toggle-screen-recording") {
            if (screenRecordingStatusRef.current === "idle") {
              await startScreenRecording(
                payload.includeCamera === true
              );
            } else {
              toggleScreenRecordingPause();
            }
            return;
          }

          if (command === "stop-screen-recording") {
            stopScreenRecording();
            return;
          }

          if (
            command ===
            "offer-remote-control"
          ) {
            const participantId =
              payload.participantId;

            if (!participantId) {
              return;
            }

            const sent =
              sendControlMessage(
                participantId,
                {
                  type:
                    "remote-control-offer",
                  tutorName:
                    displayName.trim() ||
                    "Tutor"
                }
              );

            if (!sent) {
              setError(
                "Remote control could not be offered because the participant control connection is not ready yet. Wait a moment and try again."
              );

              window.learnWithRyanDesktop
                ?.remoteControlDeclined?.({
                  id: participantId,
                  name:
                    payload.participantName ||
                    "Participant"
                });
            }
            return;
          }

          if (
            command ===
            "revoke-remote-control"
          ) {
            const participantId =
              payload.participantId;

            if (participantId) {
              sendControlMessage(
                participantId,
                {
                  type:
                    "remote-control-revoke",
                  reason:
                    payload.reason ||
                    "tutor-stopped"
                }
              );
            }
          }
        }
      );

    return () => {
      if (
        typeof unsubscribe ===
        "function"
      ) {
        unsubscribe();
      }
    };
  }, [
    isElectronDesktop,
    displayName
  ]);

  // =========================================================
  // LEAVE LESSON
  // =========================================================

  // Preload only the first laugh; playback is triggered by the tutor's exit.
  useEffect(() => {
    const audio = new Audio('/lesson-audio/witch-laugh-first.wav');
    audio.preload = 'auto';
    audio.volume = 0.16;
    exitLaughRef.current = audio;
    return () => { audio.pause(); audio.removeAttribute('src'); exitLaughRef.current = null; };
  }, []);

  const playExitLaugh = (offsetSeconds = 0) => {
    const audio = exitLaughRef.current;
    if (!audio) return;
    audio.currentTime = Math.max(0, Math.min(WITCH_LAUGH_DURATION_MS / 1000 - 0.01, offsetSeconds));
    audio.play().catch(error => console.warn('Exit laugh could not play:', error));
  };

  const leaveRoom = async () => {
    if (leavingRoomRef.current) return;
    leavingRoomRef.current = true;
    const isTutorLeaving = isElectronDesktop && role === 'tutor';
    const cameraTrack = processedCameraStreamRef.current?.getVideoTracks()[0];
    const canDisappear = HALLOWEEN_BACKGROUNDS.some(option => option.value === backgroundEffectRef.current)
      && backgroundImageRef.current && backgroundMaskReadyRef.current
      && cameraTrack?.readyState === 'live' && cameraTrack.enabled
      && !screenSharingStateRef.current;
    const exitDuration = canDisappear ? SMOKE_EXIT_DURATION_MS + 650 : 0;
    // Publish the deadline before the animation. Students still leave if the
    // tutor's connection drops before the final 'ended' update is delivered.
    if (isTutorLeaving) {
      try {
        await setDoc(getRoomRef(), {
          status: exitDuration ? 'ending' : 'ended',
          endingTutorId: participantIdRef.current,
          endsAt: Date.now() + exitDuration,
          exitLaughStartedAt: canDisappear ? Date.now() : null,
          exitLaugh: !!canDisappear,
          endedAt: exitDuration ? null : serverTimestamp()
        }, { merge: true });
      } catch (error) {
        console.error('Could not end the shared lesson:', error);
        setError('Could not end the lesson for everyone. Please try Leave Lesson again.');
        leavingRoomRef.current = false;
        return;
      }
    }
    try {
      if (canDisappear) {
        smokeExitStartedRef.current = performance.now();
        playExitLaugh();
        await new Promise(resolve => setTimeout(resolve, exitDuration));
      }
    } finally {
      try {
        if (isTutorLeaving && exitDuration) {
          // The published deadline already handles student exit if this fails.
          void updateDoc(getRoomRef(), { status: 'ended', endedAt: serverTimestamp() })
            .catch(error => console.warn('Could not finalize lesson status:', error));
        }
      } finally {
        try { await finishLeavingRoom(); }
        finally { smokeExitStartedRef.current = null; leavingRoomRef.current = false; }
      }
    }
  };

  const finishLeavingRoom =
    async () => {
      try {
        if (screenRecordingStatusRef.current !== "idle") {
          stopScreenRecording();
        }

        screenSharingStateRef.current =
          false;

        screenFrozenStateRef.current =
          false;

        await closeFloatingController();

        if (
          heartbeatTimerRef.current
        ) {
          clearInterval(
            heartbeatTimerRef.current
          );

          heartbeatTimerRef.current =
            null;
        }

        if (
          participantListenerRef.current
        ) {
          participantListenerRef.current();

          participantListenerRef.current =
            null;
        }

        if (
          admissionListenerRef.current
        ) {
          admissionListenerRef.current();

          admissionListenerRef.current =
            null;
        }

        setIsWaitingForAdmission(false);
        setWaitingParticipants([]);

        if (
          chatListenerRef.current
        ) {
          chatListenerRef.current();

          chatListenerRef.current =
            null;
        }

        if (participantDocRef.current) {
          const departingParticipant = participantDocRef.current;
          participantDocRef.current = null;
          // Local media and UI must close even if Firestore is offline.
          void deleteDoc(departingParticipant).catch(error =>
            console.warn('Could not delete participant:', error)
          );
        }

        Array.from(
          peerConnectionsRef.current.keys()
        ).forEach(
          (participantId) => {
            closePeerConnection(
              participantId
            );
          }
        );

        controlChannelsRef.current.forEach(
          (channel) => {
            try {
              channel.close();
            } catch {
              // Ignore.
            }
          }
        );

        controlChannelsRef.current.clear();
        setRemoteControlOffer(null);
        setRemoteControlSession({
          active: false,
          tutorParticipantId: null,
          tutorName: "Tutor"
        });

        stopFrozenScreenStream();

        if (
          screenStreamRef.current
        ) {
          screenStreamRef.current
            .getTracks()
            .forEach(
              (track) => {
                track.onended =
                  null;

                track.stop();
              }
            );

          screenStreamRef.current =
            null;
        }

        cleanupScreenAudioMix();

        setIsScreenSharing(false);
        setIsScreenFrozen(false);

        stopLocalMedia();

        setHasJoinedLesson(false);
        setHasLeftLesson(true);
        setJoining(false);

        setRemoteParticipants([]);
        setRemoteControlOffer(null);
        setRemoteControlSession({
          active: false,
          tutorParticipantId: null,
          tutorName: "Tutor"
        });
      } catch (err) {
        console.error(
          "Leave room error:",
          err
        );

        // Even if part of the cleanup fails, keep the user on
        // the lesson page and show the left-lesson screen.
        try {
          stopLocalMedia();
        } catch {
          // Ignore.
        }

        setHasJoinedLesson(false);
        setHasLeftLesson(true);
        setJoining(false);
        setIsWaitingForAdmission(false);
        setWaitingParticipants([]);
        setRemoteParticipants([]);
        setRemoteControlOffer(null);
        setRemoteControlSession({
          active: false,
          tutorParticipantId: null,
          tutorName: "Tutor"
        });

        setError(
          "You have left the lesson."
        );
      }
    };

  // Shared lesson lifecycle: both admitted students and the waiting room
  // follow the tutor's end signal; a student leaving never ends the room.
  useEffect(() => {
    if (!roomId || isElectronDesktop || (!hasJoinedLesson && !isWaitingForAdmission)) return undefined;
    let timer = null;
    let handledDeadline = null;
    let disposed = false;
    let ended = false;
    const endForStudent = async () => {
      if (disposed || ended || leavingRoomRef.current) return;
      ended = true;
      leavingRoomRef.current = true;
      try { await finishLeavingRoom(); }
      finally { leavingRoomRef.current = false; }
    };
    const unsubscribe = onSnapshot(getRoomRef(), snapshot => {
      const room = snapshot.data();
      if (!room || !['ending', 'ended'].includes(room.status)) {
        clearTimeout(timer);
        handledDeadline = null;
        return;
      }
      if (room.status === 'ended') {
        clearTimeout(timer);
        void endForStudent();
        return;
      }
      if (handledDeadline === room.endsAt) return;
      handledDeadline = room.endsAt;
      clearTimeout(timer);
      const remaining = Math.max(0, Number(room.endsAt) - Date.now()) || 0;
      const laughElapsed = Math.max(0, Date.now() - Number(room.exitLaughStartedAt ?? (Number(room.endsAt) - SMOKE_EXIT_DURATION_MS - 650)));
      if (room.exitLaugh && remaining > 0 && laughElapsed < WITCH_LAUGH_DURATION_MS) playExitLaugh(laughElapsed / 1000);
      timer = setTimeout(endForStudent, Math.min(remaining, SMOKE_EXIT_DURATION_MS + 650));
    }, error => console.error('Lesson status listener error:', error));
    return () => { disposed = true; clearTimeout(timer); unsubscribe(); };
    // The listener is scoped to room membership; callbacks use stable media refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, isElectronDesktop, hasJoinedLesson, isWaitingForAdmission]);

  // =========================================================
  // EXIT AFTER LEAVING
  // =========================================================

  const exitAfterLeaving = () => {
    setError("");

    window.close();

    // Normal browser tabs usually cannot be closed by
    // JavaScript unless JavaScript opened them.
    setTimeout(() => {
      if (!window.closed) {
        setError(
          "You can now close this tab."
        );
      }
    }, 300);
  };

  // =========================================================
  // REJOIN LESSON
  // =========================================================

  const rejoinLesson = () => {
    setError("");
    setHasLeftLesson(false);
    setHasJoinedLesson(false);
    setJoining(false);
    setIsWaitingForAdmission(false);
    setWaitingParticipants([]);

    setRemoteParticipants([]);
    setRemoteControlOffer(null);

    setRemoteControlSession({
      active: false,
      tutorParticipantId: null,
      tutorName: "Tutor"
    });

    if (
      admissionListenerRef.current
    ) {
      try {
        admissionListenerRef.current();
      } catch {
        // Ignore.
      }

      admissionListenerRef.current =
        null;
    }

    if (
      participantListenerRef.current
    ) {
      try {
        participantListenerRef.current();
      } catch {
        // Ignore.
      }

      participantListenerRef.current =
        null;
    }

    if (
      heartbeatTimerRef.current
    ) {
      clearInterval(
        heartbeatTimerRef.current
      );

      heartbeatTimerRef.current =
        null;
    }

    participantIdRef.current =
      null;

    participantDocRef.current =
      null;

    stopBackgroundProcessing();
    cleanupVoiceProcessing();
    cleanupMicrophoneTest();
    stopMutedSpeechMonitoring();

    if (
      localStreamRef.current
    ) {
      localStreamRef.current
        .getTracks()
        .forEach(
          (track) => {
            try {
              track.stop();
            } catch {
              // Ignore.
            }
          }
        );

      localStreamRef.current =
        null;
    }

    if (
      localVideoRef.current
    ) {
      try {
        localVideoRef.current.srcObject =
          null;
      } catch {
        // Ignore.
      }
    }

    setCameraStarted(false);
    setCameraEnabled(true);
    setMicEnabled(true);
    setHandRaised(false);

    micEnabledStateRef.current =
      true;

    backgroundEffectRef.current =
      "none";

    setBackgroundEffect(
      "none"
    );

    setBackgroundEffectLoading(
      false
    );

    setBackgroundEffectError(
      ""
    );
  };

  // =========================================================
  // CLEANUP
  // =========================================================

  useEffect(() => {
    return () => {
      try {
        if (
          pipWindowRef.current &&
          !pipWindowRef.current.closed
        ) {
          pipWindowRef.current.close();
        }
      } catch {
        // Ignore.
      }

      if (
        heartbeatTimerRef.current
      ) {
        clearInterval(
          heartbeatTimerRef.current
        );
      }

      if (
        participantListenerRef.current
      ) {
        participantListenerRef.current();
      }

      if (
        admissionListenerRef.current
      ) {
        admissionListenerRef.current();
      }

      peerListenersRef.current.forEach(
        (listeners) => {
          listeners.forEach(
            (unsubscribe) => {
              try {
                unsubscribe();
              } catch {
                // Ignore.
              }
            }
          );
        }
      );

      peerConnectionsRef.current.forEach(
        (pc) => {
          try {
            pc.close();
          } catch {
            // Ignore.
          }
        }
      );

      controlChannelsRef.current.forEach(
        (channel) => {
          try {
            channel.close();
          } catch {
            // Ignore.
          }
        }
      );

      controlChannelsRef.current.clear();

      stopBackgroundProcessing();
      stopMutedSpeechMonitoring();

      closeBackgroundSegmenter();

      if (
        localStreamRef.current
      ) {
        localStreamRef.current
          .getTracks()
          .forEach(
            (track) =>
              track.stop()
          );
      }

      if (
        screenStreamRef.current
      ) {
        screenStreamRef.current
          .getTracks()
          .forEach(
            (track) =>
              track.stop()
          );
      }

      if (
        frozenScreenStreamRef.current
      ) {
        frozenScreenStreamRef.current
          .getTracks()
          .forEach(
            (track) =>
              track.stop()
          );
      }

      if (
        mixedAudioStreamRef.current
      ) {
        mixedAudioStreamRef.current
          .getTracks()
          .forEach(
            (track) =>
              track.stop()
          );
      }

      if (
        screenAudioContextRef.current
      ) {
        screenAudioContextRef.current
          .close()
          .catch(() => {});
      }
    };
  }, []);

  // =========================================================
  // INVALID ROOM
  // =========================================================

  if (!roomId) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#071225",
          color: "white",
          display: "flex",
          justifyContent: "center",
          alignItems: "center"
        }}
      >
        <div
          style={{
            textAlign: "center"
          }}
        >
          <h1>
            Invalid Lesson Link
          </h1>

          <p>
            This lesson link does not contain a room code.
          </p>
        </div>
      </div>
    );
  }

  // =========================================================
  // LEFT LESSON SCREEN
  // =========================================================

  if (hasLeftLesson) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background:
            "linear-gradient(135deg, #071225 0%, #102445 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem",
          color: "white"
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: "520px",
            padding: "2.5rem",
            borderRadius: "24px",
            background:
              "rgba(15, 35, 65, 0.92)",
            border:
              "1px solid rgba(255,255,255,0.12)",
            boxShadow:
              "0 25px 60px rgba(0,0,0,0.35)",
            textAlign: "center"
          }}
        >
          <div
            style={{
              width: "70px",
              height: "70px",
              margin: "0 auto 1.25rem",
              borderRadius: "50%",
              background:
                "rgba(96,165,250,0.15)",
              border:
                "1px solid rgba(96,165,250,0.35)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "2rem"
            }}
          >
            ✓
          </div>

          <h1
            style={{
              margin: 0,
              fontSize: "2rem",
              fontWeight: "700"
            }}
          >
            You have left the lesson.
          </h1>

          <p
            style={{
              margin:
                "0.75rem 0 0",
              opacity: 0.7,
              lineHeight: 1.5
            }}
          >
            You can rejoin the lesson or exit.
          </p>

          <div
            style={{
              display: "flex",
              gap: "0.75rem",
              justifyContent: "center",
              marginTop: "2rem",
              flexWrap: "wrap"
            }}
          >
            <button
              type="button"
              onClick={
                exitAfterLeaving
              }
              style={{
                padding:
                  "0.85rem 1.5rem",
                minWidth: "130px",
                borderRadius: "12px",
                border:
                  "1px solid rgba(255,255,255,0.18)",
                background:
                  "#253858",
                color: "white",
                fontWeight: "700",
                fontSize: "0.95rem",
                cursor: "pointer"
              }}
            >
              Exit
            </button>

            <button
              type="button"
              onClick={
                rejoinLesson
              }
              style={{
                padding:
                  "0.85rem 1.5rem",
                minWidth: "130px",
                borderRadius: "12px",
                border:
                  "1px solid #60a5fa",
                background:
                  "#1d4ed8",
                color: "white",
                fontWeight: "700",
                fontSize: "0.95rem",
                cursor: "pointer"
              }}
            >
              Rejoin
            </button>
          </div>

          {error &&
            error !==
              "You have left the lesson." && (
              <div
                style={{
                  marginTop: "1rem",
                  color: "#fca5a5",
                  fontSize: "0.85rem"
                }}
              >
                {error}
              </div>
            )}
        </div>
      </div>
    );
  }

  // =========================================================
  // COMMON BUTTON
  // =========================================================

  const controlButtonStyle = {
    padding: "0.8rem 1.2rem",
    border: "none",
    borderRadius: "10px",
    cursor: "pointer",
    fontWeight: "bold",
    color: "white"
  };

  // =========================================================
  // REMOTE PRESENTER
  // =========================================================

  // Firestore presence tells us when another participant has
  // started sharing their screen. When that happens, the
  // receiving side automatically switches into a full-window
  // presentation layout.
  const remotePresenter =
    remoteParticipants.find(
      (participant) =>
        participant.isScreenSharing ===
        true
    ) || null;

  const canControlRemotePresenter =
    !!remotePresenter &&
    remoteControlSession.active &&
    remoteControlSession
      .tutorParticipantId ===
      remotePresenter.id;

  // =========================================================
  // PAGE
  // =========================================================

  return (
    <div className={HALLOWEEN_LESSON_THEME_ENABLED ? "halloween-lesson-room" : undefined}
      style={{
        minHeight: "100vh",
        background: "var(--lesson-deep, #071225)",
        color: "white",
        padding: "2rem",

        paddingRight:
          chatOpen
            ? "390px"
            : "2rem",

        boxSizing: "border-box",

        transition:
          "padding-right 0.25s ease"
      }}
    >
      <HalloweenLessonTheme />
      <div className="hl-content"
        style={{
          maxWidth: "1400px",
          margin: "0 auto"
        }}
      >
        {/* =================================================
            WAITING FOR ADMISSION
            ================================================= */}

        {isWaitingForAdmission && (
          <div className="hl-surface hl-waiting-card"
            style={{
              maxWidth: "620px",
              margin: "4rem auto",
              background: "var(--lesson-panel, #102445)",
              borderRadius: "24px",
              padding: "2.5rem",
              boxShadow:
                "0 20px 60px rgba(0,0,0,0.35)",
              textAlign: "center"
            }}
          >
            <div
              style={{
                width: "72px",
                height: "72px",
                borderRadius: "50%",
                margin:
                  "0 auto 1.5rem",
                background:
                  "rgba(37,99,235,.18)",
                border:
                  "1px solid rgba(96,165,250,.35)",
                display: "flex",
                justifyContent:
                  "center",
                alignItems:
                  "center",
                fontSize: "2rem"
              }}
            >
              ⏳
            </div>

            <h1
              style={{
                marginBottom:
                  "0.75rem"
              }}
            >
              Waiting for the tutor
            </h1>

            <p
              style={{
                opacity: 0.72,
                lineHeight: 1.6,
                margin: 0
              }}
            >
              Your request to join the lesson has
              been sent.
              <br />
              You'll enter automatically when the
              tutor admits you.
            </p>

            <div
              style={{
                marginTop:
                  "1.5rem",
                padding:
                  "0.8rem 1rem",
                borderRadius:
                  "12px",
                background:
                  "rgba(15,23,42,.5)",
                fontSize:
                  "0.85rem",
                opacity: 0.72
              }}
            >
              Lesson room{" "}
              <strong>
                {roomId}
              </strong>
            </div>
          </div>
        )}

        {/* =================================================
            PRE-JOIN
            ================================================= */}

        {!hasJoinedLesson &&
          !isWaitingForAdmission && (
          <div className="hl-surface hl-join-card"
            style={{
              maxWidth: "820px",
              margin: "2rem auto",
              background: "var(--lesson-panel, #102445)",
              borderRadius: "24px",
              padding: "2rem",

              boxShadow:
                "0 20px 60px rgba(0,0,0,0.35)"
            }}
          >
            <div
              style={{
                textAlign: "center",
                marginBottom: "1.5rem"
              }}
            >
              <h1>
                Ready to join?
              </h1>

              <p
                style={{
                  opacity: 0.7
                }}
              >
                Lesson room{" "}
                <strong>
                  {roomId}
                </strong>
              </p>
            </div>

            {/* NAME */}

            <div
              style={{
                maxWidth: "420px",
                margin:
                  "0 auto 1.25rem"
              }}
            >
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  fontWeight: "bold"
                }}
              >
                Your name
              </label>

              <input
                type="text"
                value={displayName}
                required
                autoComplete="name"
                maxLength={60}
                onChange={
                  (event) => {
                    setDisplayName(
                      event.target.value
                    );
                    if (error) {
                      setError("");
                    }
                  }
                }
                placeholder="Enter your name"
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: "0.9rem 1rem",
                  borderRadius: "10px",
                  border:
                    "1px solid #38557c",
                  background: "var(--lesson-deep, #071225)",
                  color: "white",
                  fontSize: "1rem"
                }}
              />
            </div>

            {/* CAMERA PREVIEW */}

            <div className="hl-video-frame"
              style={{
                background: "var(--lesson-deep, #050b16)",
                borderRadius: "18px",
                overflow: "hidden",
                position: "relative",
                aspectRatio: "16 / 9",
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
              }}
            >
              {cameraStarted ? (
                <>
                  <video
                    ref={attachLocalVideo}
                    autoPlay
                    playsInline
                    muted
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: isScreenSharing ? "contain" : "cover",
                      transform: isScreenSharing ? "none" : "scaleX(-1)"
                    }}
                  />

                  {isScreenSharing && isScreenFrozen && <FrozenScreenPreview image={frozenScreenImage} />}

                  {!cameraEnabled && !isScreenSharing && (
                    <div className="hl-video-frame"
                      style={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent:
                          "center",
                        background:
                          "var(--lesson-deep, #050b16)",
                        fontWeight:
                          "bold"
                      }}
                    >
                      Camera off
                    </div>
                  )}
                </>
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    opacity: 0.7
                  }}
                >
                  <div
                    style={{
                      fontSize: "3.5rem"
                    }}
                  >
                    📷
                  </div>

                  <p>
                    Camera preview
                  </p>
                </div>
              )}
            </div>

    

            {/* PRE-JOIN CONTROLS */}

            <div
              style={{
                marginTop: "1.25rem"
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "0.75rem",
                  flexWrap: "wrap"
                }}
              >
                {!cameraStarted ? (
                  <button
                    onClick={startPreview}
                    style={{
                      ...controlButtonStyle,
                      background: "var(--lesson-control, #253858)"
                    }}
                  >
                    Start Camera & Microphone
                  </button>
                ) : (
                  <>
                    <button
                      onClick={
                        toggleMicrophone
                      }
                      style={{
                        ...controlButtonStyle,
                        background:
                          micEnabled
                            ? "var(--lesson-control, #253858)"
                            : "#991b1b"
                      }}
                    >
                      {micEnabled
                        ? "🎤 Mute"
                        : "🎤 Unmute"}
                    </button>

                    <button
                      onClick={
                        toggleCamera
                      }
                      style={{
                        ...controlButtonStyle,
                        background:
                          cameraEnabled
                            ? "var(--lesson-control, #253858)"
                            : "#991b1b"
                      }}
                    >
                      {cameraEnabled
                        ? "📹 Camera Off"
                        : "📹 Camera On"}
                    </button>
                  </>
                )}
              </div>

              {cameraStarted && (
                <div
                  style={{
                    margin: "0.75rem auto 0",
                    padding: "0.6rem 0.7rem",
                    maxWidth: "680px",
                    borderRadius: "11px",
                    background: "rgba(7,18,37,.42)",
                    border: "1px solid rgba(148,163,184,.18)"
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: "0.78rem",
                      color: "#cbd5e1",
                      marginBottom: "0.45rem"
                    }}
                  >
                    Devices
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(185px, 1fr))",
                      gap: "0.55rem"
                    }}
                  >
                    <label style={{ display: "grid", gap: "0.25rem" }}>
                      <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "#cbd5e1" }}>
                        📹 Camera
                      </span>
                      <select
                        value={selectedCameraId}
                        disabled={switchingDevice}
                        onChange={(event) => switchCameraDevice(event.target.value)}
                        style={{
                          width: "100%",
                          minWidth: 0,
                          padding: "0.46rem 0.55rem",
                          borderRadius: "8px",
                          border: "1px solid rgba(100,116,139,.6)",
                          background: "rgba(7,18,37,.8)",
                          color: "white",
                          fontSize: "0.78rem"
                        }}
                      >
                        {mediaDevices
                          .filter((device) => device.kind === "videoinput")
                          .map((device, index) => (
                            <option key={device.deviceId} value={device.deviceId}>
                              {device.label || `Camera ${index + 1}`}
                            </option>
                          ))}
                      </select>
                    </label>

                    <label style={{ display: "grid", gap: "0.25rem" }}>
                      <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "#cbd5e1" }}>
                        🎤 Microphone
                      </span>
                      <select
                        value={selectedMicrophoneId}
                        disabled={switchingDevice || microphoneTestStatus === "recording"}
                        onChange={(event) => switchMicrophoneDevice(event.target.value)}
                        style={{
                          width: "100%",
                          minWidth: 0,
                          padding: "0.46rem 0.55rem",
                          borderRadius: "8px",
                          border: "1px solid rgba(100,116,139,.6)",
                          background: "rgba(7,18,37,.8)",
                          color: "white",
                          fontSize: "0.78rem"
                        }}
                      >
                        {mediaDevices
                          .filter((device) => device.kind === "audioinput")
                          .map((device, index) => (
                            <option key={device.deviceId} value={device.deviceId}>
                              {device.label || `Microphone ${index + 1}`}
                            </option>
                          ))}
                      </select>
                    </label>
                  </div>

                  <div
                    style={{
                      marginTop: "0.55rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      flexWrap: "wrap"
                    }}
                  >
                    <button
                      type="button"
                      onClick={testMicrophone}
                      disabled={switchingDevice || microphoneTestStatus === "recording"}
                      style={{
                        ...controlButtonStyle,
                        padding: "0.45rem 0.7rem",
                        borderRadius: "8px",
                        fontSize: "0.76rem",
                        background: microphoneTestStatus === "recording"
                          ? "#991b1b"
                          : "var(--lesson-primary, #2563eb)",
                        cursor: switchingDevice || microphoneTestStatus === "recording"
                          ? "not-allowed"
                          : "pointer"
                      }}
                    >
                      {microphoneTestStatus === "recording"
                        ? `● Recording… ${microphoneTestSeconds}s`
                        : "🎙 Test mic (5s)"}
                    </button>

                    <span
                      role="status"
                      aria-live="polite"
                      style={{
                        color: microphoneTestStatus === "recording"
                          ? "#fca5a5"
                          : "#bfdbfe",
                        fontSize: "0.74rem",
                        fontWeight: 600
                      }}
                    >
                      {switchingDevice ? "Switching device…" : microphoneTestMessage}
                    </span>
                  </div>

                  <audio
                    ref={microphoneTestAudioRef}
                    controls={microphoneTestStatus === "ready" || microphoneTestStatus === "playing"}
                    onPlay={() => setMicrophoneTestStatus("playing")}
                    onEnded={() => {
                      setMicrophoneTestStatus("ready");
                      setMicrophoneTestMessage("Playback finished — test again if needed.");
                    }}
                    style={{
                      display: microphoneTestStatus === "ready" || microphoneTestStatus === "playing"
                        ? "block"
                        : "none",
                      width: "100%",
                      marginTop: "0.45rem",
                      height: "30px"
                    }}
                  />
                </div>
              )}

              {cameraStarted && <CameraBackgroundPicker value={backgroundEffect} busy={backgroundEffectLoading || backgroundImageLoading} error={backgroundEffectError} onChange={selectCameraBackground} brightness={cameraBrightness} onBrightnessChange={changeCameraBrightness} />}

            </div>

            {/* JOIN */}

            <div
              style={{
                textAlign: "center",
                marginTop: "1.5rem"
              }}
            >
              <button
                onClick={joinLesson}
                disabled={
                  !displayName.trim() ||
                  !cameraStarted ||
                  joining ||
                  backgroundEffectLoading
                }
                style={{
                  padding: "1rem 2rem",
                  border: "none",
                  borderRadius: "12px",

                  background:
                    !displayName.trim() ||
                    !cameraStarted ||
                    joining ||
                    backgroundEffectLoading
                      ? "var(--lesson-control, #475569)"
                      : "var(--lesson-primary, #2563eb)",

                  color: "white",
                  fontSize: "1.05rem",
                  fontWeight: "bold",

                  cursor:
                    !displayName.trim() ||
                    !cameraStarted ||
                    joining ||
                    backgroundEffectLoading
                      ? "not-allowed"
                      : "pointer"
                }}
              >
                {joining
                  ? (
                      isElectronDesktop
                        ? "Joining..."
                        : "Sending request..."
                    )
                  : backgroundEffectLoading
                    ? "Preparing Camera..."
                    : isElectronDesktop
                      ? "Join Lesson"
                      : "Request to Join"}
              </button>
            </div>

            {error && (
              <div
                style={{
                  marginTop: "1.25rem",
                  background: "#4c1111",

                  border:
                    "1px solid #ef4444",

                  borderRadius: "12px",
                  padding: "1rem",
                  textAlign: "center"
                }}
              >
                {error}
              </div>
            )}
          </div>
        )}

        {hasJoinedLesson && cameraStarted && <details style={{ marginBottom: 16 }}><summary style={{ cursor: 'pointer', padding: 10 }}>Change camera background</summary><CameraBackgroundPicker value={backgroundEffect} busy={backgroundEffectLoading || backgroundImageLoading} error={backgroundEffectError} onChange={selectCameraBackground} brightness={cameraBrightness} onBrightnessChange={changeCameraBrightness} /></details>}

        {/* =================================================
            ACTIVE LESSON
            ================================================= */}

        {hasJoinedLesson &&
          !remotePresenter && (
          <>
            {/* HEADER */}

            <div
              style={{
                display: "flex",

                justifyContent:
                  "space-between",

                alignItems: "center",

                gap: "1rem",
                flexWrap: "wrap",

                marginBottom: "1rem"
              }}
            >
              <div>
                <h1
                  style={{
                    margin: 0
                  }}
                >
                  Online Lesson
                </h1>

                <div
                  style={{
                    marginTop: "0.4rem",
                    opacity: 0.7
                  }}
                >
                  Room {roomId}
                  {" • "}
                  {role === "tutor"
                    ? "Tutor"
                    : "Participant"}
                  {" • "}
                  {
                    remoteParticipants.length +
                    1
                  }{" "}
                  in lesson
                </div>
              </div>
            </div>

            {/* =================================================
                WAITING ROOM — TUTOR
                ================================================= */}

            {isElectronDesktop &&
              waitingParticipants.length >
                0 && (
                <div
                  style={{
                    marginBottom:
                      "1rem",
                    padding:
                      "1rem",
                    borderRadius:
                      "16px",
                    background:
                      "rgba(37,99,235,.16)",
                    border:
                      "1px solid rgba(96,165,250,.45)"
                  }}
                >
                  <div
                    style={{
                      fontWeight:
                        "bold",
                      marginBottom:
                        "0.75rem",
                      fontSize:
                        "1rem"
                    }}
                  >
                    🔔{" "}
                    {waitingParticipants.length ===
                    1
                      ? "Someone is waiting to join"
                      : `${waitingParticipants.length} people are waiting to join`}
                  </div>

                  {waitingParticipants.map(
                    (participant) => (
                      <div className="hl-surface"
                        key={
                          participant.id
                        }
                        style={{
                          display:
                            "flex",
                          justifyContent:
                            "space-between",
                          alignItems:
                            "center",
                          gap:
                            "1rem",
                          flexWrap:
                            "wrap",
                          padding:
                            "0.8rem",
                          marginTop:
                            "0.5rem",
                          borderRadius:
                            "12px",
                          background:
                            "var(--lesson-panel, #102445)"
                        }}
                      >
                        <div>
                          <strong>
                            {participant.name}
                          </strong>

                          <div
                            style={{
                              marginTop:
                                "0.2rem",
                              opacity:
                                0.65,
                              fontSize:
                                "0.8rem"
                            }}
                          >
                            Wants to join this lesson
                          </div>
                        </div>

                        <div
                          style={{
                            display:
                              "flex",
                            gap:
                              "0.5rem"
                          }}
                        >
                          <button
                            type="button"
                            onClick={() =>
                              denyWaitingParticipant(
                                participant.id
                              )
                            }
                            style={{
                              ...controlButtonStyle,
                              background:
                                "#991b1b"
                            }}
                          >
                            Deny
                          </button>

                          <button
                            type="button"
                            onClick={() =>
                              admitWaitingParticipant(
                                participant.id
                              )
                            }
                            style={{
                              ...controlButtonStyle,
                              background:
                                "#16a34a"
                            }}
                          >
                            Admit
                          </button>
                        </div>
                      </div>
                    )
                  )}
                </div>
              )}

            {/* =================================================
                VIDEO GRID
                ================================================= */}

            <div
              style={{
                display: "grid",

                gridTemplateColumns:
                  remoteParticipants.length <=
                  1
                    ? "repeat(2, minmax(0, 1fr))"
                    : "repeat(auto-fit, minmax(260px, 1fr))",

                gap: "1rem"
              }}
            >
              {/* =================================================
                  LOCAL VIDEO / SHARED SCREEN
                  ================================================= */}

              <div className="hl-surface"
                style={{
                  background: "var(--lesson-panel, #102445)",
                  borderRadius: "18px",
                  padding: "0.75rem",
                  minWidth: 0
                }}
              >
                {/* HEADER */}

                <div
                  style={{
                    marginBottom: "0.5rem",

                    fontWeight: "bold",

                    display: "flex",

                    alignItems: "center",

                    justifyContent:
                      "space-between",

                    gap: "0.5rem"
                  }}
                >
                  <span>
                    You
                    {role === "tutor" &&
                      " • Tutor"}
                  </span>

                  {isScreenSharing && (
                    <span
                      style={{
                        fontSize:
                          "0.75rem",

                        color:
                          "#86efac",

                        fontWeight:
                          "bold",

                        background:
                          "rgba(34,197,94,0.10)",

                        border:
                          "1px solid rgba(34,197,94,0.25)",

                        borderRadius:
                          "999px",

                        padding:
                          "0.25rem 0.55rem"
                      }}
                    >
                      🖥 Presenting
                    </span>
                  )}
                </div>

                {/* VIDEO ELEMENT */}

                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    aspectRatio: "16 / 9"
                  }}
                >
                  <video
                    ref={attachLocalVideo}
                    autoPlay
                    playsInline
                    muted
                    style={{
                      width: "100%",
                      height: "100%",
                      background: "#000",
                      borderRadius: "12px",
                      objectFit: isScreenSharing
                        ? "contain"
                        : "cover",
                      transform: isScreenSharing
                        ? "none"
                        : "scaleX(-1)"
                    }}
                  />

                  {isScreenSharing && isScreenFrozen && <FrozenScreenPreview image={frozenScreenImage} />}

                  <CameraStatusBadges
                    micEnabled={micEnabled}
                    handRaised={handRaised}
                  />
                </div>

                {/* SCREEN SHARE STATUS */}

                {isScreenSharing && (
                  <div
                    style={{
                      marginTop:
                        "0.55rem",

                      fontSize:
                        "0.75rem",

                      opacity: 0.65,

                      textAlign:
                        "center"
                    }}
                  >
                    This is what participants are seeing
                  </div>
                )}
              </div>

              {/* =================================================
                  REMOTE PARTICIPANTS
                  ================================================= */}

              {remoteParticipants.map(
                (participant) => {
                  const canControlThisParticipant =
                    remoteControlSession.active &&
                    remoteControlSession
                      .tutorParticipantId ===
                      participant.id;

                  return (
                    <div className="hl-surface"
                      key={
                        participant.id
                      }
                      style={{
                        background:
                          "var(--lesson-panel, #102445)",
                        borderRadius:
                          "18px",
                        padding:
                          "0.75rem",
                        minWidth: 0
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent:
                            "space-between",
                          alignItems:
                            "center",
                          gap:
                            "0.5rem",
                          marginBottom:
                            "0.5rem"
                        }}
                      >
                        <strong>
                          {participant.name}
                        </strong>

                        <span
                          style={{
                            fontSize:
                              "0.75rem",
                            opacity: 0.75
                          }}
                        >
                          {canControlThisParticipant
                            ? "🖱 Remote control active"
                            : participant.connected
                              ? "● Connected"
                              : "Connecting..."}
                        </span>
                      </div>

                      <div
                        style={{
                          position: "relative"
                        }}
                      >
                        <video
                          ref={
                            (element) =>
                              attachRemoteVideo(
                                participant.id,
                                element
                              )
                          }
                          autoPlay
                          playsInline
                          tabIndex={
                            canControlThisParticipant
                              ? 0
                              : -1
                          }
                          onContextMenu={
                            (event) => {
                              if (
                                canControlThisParticipant
                              ) {
                                event.preventDefault();
                              }
                            }
                          }
                          onPointerDown={
                            (event) => {
                              if (
                                !canControlThisParticipant
                              ) {
                                return;
                              }

                              event.preventDefault();
                              const video =
                                event.currentTarget;
                              video.focus();

                              try {
                                video.setPointerCapture(
                                  event.pointerId
                                );
                              } catch {
                                // Ignore.
                              }

                              const point =
                                getNormalisedVideoPoint(
                                  event,
                                  video
                                );

                              if (!point) {
                                return;
                              }

                              sendRemoteControlInput(
                                participant.id,
                                {
                                  type:
                                    "pointer-button",
                                  action: "down",
                                  button:
                                    event.button,
                                  ...point
                                }
                              );
                            }
                          }
                          onPointerUp={
                            (event) => {
                              if (
                                !canControlThisParticipant
                              ) {
                                return;
                              }

                              event.preventDefault();
                              const video =
                                event.currentTarget;
                              const point =
                                getNormalisedVideoPoint(
                                  event,
                                  video
                                );

                              if (point) {
                                sendRemoteControlInput(
                                  participant.id,
                                  {
                                    type:
                                      "pointer-button",
                                    action: "up",
                                    button:
                                      event.button,
                                    ...point
                                  }
                                );
                              }

                              try {
                                video.releasePointerCapture(
                                  event.pointerId
                                );
                              } catch {
                                // Ignore.
                              }
                            }
                          }
                          onPointerMove={
                            (event) => {
                              if (
                                !canControlThisParticipant
                              ) {
                                return;
                              }

                              const now =
                                performance.now();

                              if (
                                now -
                                  remoteControlMoveTimerRef.current <
                                20
                              ) {
                                return;
                              }

                              remoteControlMoveTimerRef.current =
                                now;

                              const point =
                                getNormalisedVideoPoint(
                                  event,
                                  event.currentTarget
                                );

                              if (!point) {
                                return;
                              }

                              sendRemoteControlInput(
                                participant.id,
                                {
                                  type:
                                    "pointer-move",
                                  ...point
                                }
                              );
                            }
                          }
                          onWheel={
                            (event) => {
                              if (
                                !canControlThisParticipant
                              ) {
                                return;
                              }

                              event.preventDefault();
                              const point =
                                getNormalisedVideoPoint(
                                  event,
                                  event.currentTarget
                                );

                              if (!point) {
                                return;
                              }

                              sendRemoteControlInput(
                                participant.id,
                                {
                                  type: "wheel",
                                  deltaX:
                                    event.deltaX,
                                  deltaY:
                                    event.deltaY,
                                  ...point
                                }
                              );
                            }
                          }
                          onKeyDown={
                            (event) => {
                              if (
                                !canControlThisParticipant
                              ) {
                                return;
                              }

                              if (
                                event.key === "Escape"
                              ) {
                                event.preventDefault();
                                releaseRemoteControl();
                                return;
                              }

                              event.preventDefault();
                              sendRemoteControlInput(
                                participant.id,
                                {
                                  type: "key",
                                  action: "down",
                                  key: event.key,
                                  code: event.code,
                                  repeat:
                                    event.repeat,
                                  ctrlKey:
                                    event.ctrlKey,
                                  altKey:
                                    event.altKey,
                                  shiftKey:
                                    event.shiftKey,
                                  metaKey:
                                    event.metaKey
                                }
                              );
                            }
                          }
                          onKeyUp={
                            (event) => {
                              if (
                                !canControlThisParticipant ||
                                event.key === "Escape"
                              ) {
                                return;
                              }

                              event.preventDefault();
                              sendRemoteControlInput(
                                participant.id,
                                {
                                  type: "key",
                                  action: "up",
                                  key: event.key,
                                  code: event.code,
                                  ctrlKey:
                                    event.ctrlKey,
                                  altKey:
                                    event.altKey,
                                  shiftKey:
                                    event.shiftKey,
                                  metaKey:
                                    event.metaKey
                                }
                              );
                            }
                          }
                          style={{
                            width: "100%",
                            aspectRatio:
                              "16 / 9",
                            maxHeight:
                              "500px",
                            background:
                              "#000",
                            borderRadius:
                              "12px",
                            objectFit: participant.isScreenSharing ? "contain" : "cover",
                            display: "block",
                            cursor:
                              canControlThisParticipant
                                ? "crosshair"
                                : "default",
                            outline:
                              canControlThisParticipant
                                ? "2px solid #22c55e"
                                : "none",
                            touchAction:
                              canControlThisParticipant
                                ? "none"
                                : "auto",
                            userSelect: "none"
                          }}
                        />

                        <CameraStatusBadges
                          micEnabled={participant.micEnabled}
                          handRaised={participant.handRaised}
                        />

                        {canControlThisParticipant && (
                          <div
                            style={{
                              position: "absolute",
                              top: "10px",
                              left: "50%",
                              transform:
                                "translateX(-50%)",
                              background:
                                "rgba(15,23,42,.88)",
                              border:
                                "1px solid rgba(34,197,94,.55)",
                              borderRadius:
                                "999px",
                              padding:
                                "0.35rem 0.65rem",
                              fontSize:
                                "0.72rem",
                              pointerEvents:
                                "none"
                            }}
                          >
                            🖱 Click this shared screen to control {remoteControlSession.tutorName}
                          </div>
                        )}
                      </div>

                      {canControlThisParticipant && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent:
                              "space-between",
                            alignItems: "center",
                            gap: "0.75rem",
                            marginTop: "0.6rem",
                            padding:
                              "0.55rem 0.65rem",
                            borderRadius:
                              "10px",
                            background:
                              "rgba(22,163,74,.14)",
                            border:
                              "1px solid rgba(34,197,94,.35)",
                            fontSize:
                              "0.75rem"
                          }}
                        >
                          <span>
                            Remote control is active. Press Esc to release control.
                          </span>

                          <button
                            onClick={
                              releaseRemoteControl
                            }
                            style={{
                              border: "none",
                              borderRadius: "8px",
                              padding:
                                "0.45rem 0.7rem",
                              background:
                                "#dc2626",
                              color: "white",
                              cursor: "pointer",
                              fontWeight: "bold"
                            }}
                          >
                            Stop controlling
                          </button>
                        </div>
                      )}
                    </div>
                  );
                }
              )}
            </div>

            {/* ERROR */}

            {error && (
              <div
                style={{
                  marginTop: "1rem",

                  background:
                    "#4c1111",

                  border:
                    "1px solid #ef4444",

                  borderRadius:
                    "10px",

                  padding:
                    "0.8rem 1rem",

                  textAlign:
                    "center"
                }}
              >
                {error}
              </div>
            )}

            {/* SCREEN SHARE MESSAGE */}

            {screenShareMessage && (
              <div
                style={{
                  marginTop: "1rem",

                  background:
                    isScreenFrozen
                      ? "#24104f"
                      : "#3b2a08",

                  border:
                    isScreenFrozen
                      ? "1px solid #8b5cf6"
                      : "1px solid #f59e0b",

                  borderRadius:
                    "10px",

                  padding:
                    "0.8rem 1rem",

                  textAlign:
                    "center"
                }}
              >
                {screenShareMessage}
              </div>
            )}

            {/* =================================================
                MAIN CONTROLS
                ================================================= */}

            <div
              style={{
                marginTop: "1.5rem",

                display: "flex",

                justifyContent:
                  "center",

                gap: "0.75rem",

                flexWrap: "wrap"
              }}
            >
              <button
                onClick={toggleHandRaised}
                aria-pressed={handRaised}
                style={{
                  ...controlButtonStyle,
                  background: handRaised
                    ? "#b45309"
                    : "var(--lesson-control, #253858)"
                }}
              >
                {handRaised
                  ? "✋ Lower Hand"
                  : "✋ Raise Hand"}
              </button>

              {/* MICROPHONE */}

              <button
                onClick={
                  toggleMicrophone
                }
                style={{
                  ...controlButtonStyle,

                  background:
                    micEnabled
                      ? "var(--lesson-control, #253858)"
                      : "#991b1b"
                }}
              >
                {micEnabled
                  ? "🎤 Mute"
                  : "🎤 Unmute"}
              </button>

              {/* CAMERA */}

              <button
                onClick={
                  toggleCamera
                }
                style={{
                  ...controlButtonStyle,

                  background:
                    cameraEnabled
                      ? "var(--lesson-control, #253858)"
                      : "#991b1b"
                }}
              >
                {cameraEnabled
                  ? "📹 Camera Off"
                  : "📹 Camera On"}
              </button>

              {/* SHARE SCREEN */}

              <button
                onClick={
                  startScreenShare
                }
                disabled={
                  isScreenSharing
                }
                style={{
                  ...controlButtonStyle,

                  background:
                    isScreenSharing
                      ? "var(--lesson-control, #475569)"
                      : "var(--lesson-control, #253858)",

                  opacity:
                    isScreenSharing
                      ? 0.6
                      : 1,

                  cursor:
                    isScreenSharing
                      ? "default"
                      : "pointer"
                }}
              >
                🖥{" "}
                {isScreenSharing
                  ? "Screen Sharing"
                  : "Share Screen"}
              </button>

              {/* WHITEBOARD — DESKTOP TUTOR ONLY */}

              {isElectronDesktop && (
                <button
                  onClick={
                    openWhiteboard
                  }
                  style={{
                    ...controlButtonStyle,
                    background:
                      whiteboardOpen
                        ? "var(--lesson-primary, #2563eb)"
                        : "var(--lesson-control, #253858)"
                  }}
                >
                  ✏️ Whiteboard
                </button>
              )}

              {/* CHAT */}

              <button
                onClick={
                  () =>
                    setChatOpen(
                      (current) =>
                        !current
                    )
                }
                style={{
                  ...controlButtonStyle,

                  background:
                    chatOpen
                      ? "var(--lesson-primary, #2563eb)"
                      : "var(--lesson-control, #253858)",

                  position:
                    "relative"
                }}
              >
                💬 Chat

                {unreadMessages >
                  0 && (
                  <span
                    style={{
                      position:
                        "absolute",

                      top:
                        "-9px",

                      right:
                        "-9px",

                      minWidth:
                        "22px",

                      height:
                        "22px",

                      padding:
                        "0 5px",

                      display:
                        "flex",

                      alignItems:
                        "center",

                      justifyContent:
                        "center",

                      borderRadius:
                        "999px",

                      background:
                        "#ef4444",

                      color:
                        "white",

                      fontSize:
                        "0.72rem",

                      border:
                        "2px solid #071225"
                    }}
                  >
                    {unreadMessages >
                    99
                      ? "99+"
                      : unreadMessages}
                  </span>
                )}
              </button>

              {/* LEAVE */}

              <button
                onClick={
                  leaveRoom
                }
                style={{
                  ...controlButtonStyle,

                  background:
                    "#dc2626"
                }}
              >
                📞 Leave Lesson
              </button>
            </div>
          </>
        )}
      </div>

      {/* =====================================================
          FULL-WINDOW PRESENTATION MODE — RECEIVING SIDE
          ===================================================== */}

      {hasJoinedLesson &&
        remotePresenter && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 8000,
              background: "#000",
              color: "white",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              right: chatOpen ? "min(360px, 100vw)" : 0
            }}
          >
            <div style={{ position: "relative", flex: "1 1 0", minHeight: 0 }}>
            {/* SHARED SCREEN */}

            <video
              ref={(element) =>
                attachRemoteVideo(
                  remotePresenter.id,
                  element
                )
              }
              autoPlay
              playsInline
              tabIndex={
                canControlRemotePresenter
                  ? 0
                  : -1
              }
              onContextMenu={(event) => {
                if (
                  canControlRemotePresenter
                ) {
                  event.preventDefault();
                }
              }}
              onPointerDown={(event) => {
                if (
                  !canControlRemotePresenter
                ) {
                  return;
                }

                event.preventDefault();

                const video =
                  event.currentTarget;

                video.focus();

                try {
                  video.setPointerCapture(
                    event.pointerId
                  );
                } catch {
                  // Ignore.
                }

                const point =
                  getNormalisedVideoPoint(
                    event,
                    video
                  );

                if (!point) {
                  return;
                }

                sendRemoteControlInput(
                  remotePresenter.id,
                  {
                    type:
                      "pointer-button",
                    action:
                      "down",
                    button:
                      event.button,
                    ...point
                  }
                );
              }}
              onPointerUp={(event) => {
                if (
                  !canControlRemotePresenter
                ) {
                  return;
                }

                event.preventDefault();

                const video =
                  event.currentTarget;

                const point =
                  getNormalisedVideoPoint(
                    event,
                    video
                  );

                if (point) {
                  sendRemoteControlInput(
                    remotePresenter.id,
                    {
                      type:
                        "pointer-button",
                      action:
                        "up",
                      button:
                        event.button,
                      ...point
                    }
                  );
                }

                try {
                  video.releasePointerCapture(
                    event.pointerId
                  );
                } catch {
                  // Ignore.
                }
              }}
              onPointerMove={(event) => {
                if (
                  !canControlRemotePresenter
                ) {
                  return;
                }

                const now =
                  performance.now();

                if (
                  now -
                    remoteControlMoveTimerRef.current <
                  20
                ) {
                  return;
                }

                remoteControlMoveTimerRef.current =
                  now;

                const point =
                  getNormalisedVideoPoint(
                    event,
                    event.currentTarget
                  );

                if (!point) {
                  return;
                }

                sendRemoteControlInput(
                  remotePresenter.id,
                  {
                    type:
                      "pointer-move",
                    ...point
                  }
                );
              }}
              onWheel={(event) => {
                if (
                  !canControlRemotePresenter
                ) {
                  return;
                }

                event.preventDefault();

                const point =
                  getNormalisedVideoPoint(
                    event,
                    event.currentTarget
                  );

                if (!point) {
                  return;
                }

                sendRemoteControlInput(
                  remotePresenter.id,
                  {
                    type: "wheel",
                    deltaX:
                      event.deltaX,
                    deltaY:
                      event.deltaY,
                    ...point
                  }
                );
              }}
              onKeyDown={(event) => {
                if (
                  !canControlRemotePresenter
                ) {
                  return;
                }

                if (
                  event.key ===
                  "Escape"
                ) {
                  event.preventDefault();
                  releaseRemoteControl();
                  return;
                }

                event.preventDefault();

                sendRemoteControlInput(
                  remotePresenter.id,
                  {
                    type: "key",
                    action: "down",
                    key: event.key,
                    code: event.code,
                    repeat:
                      event.repeat,
                    ctrlKey:
                      event.ctrlKey,
                    altKey:
                      event.altKey,
                    shiftKey:
                      event.shiftKey,
                    metaKey:
                      event.metaKey
                  }
                );
              }}
              onKeyUp={(event) => {
                if (
                  !canControlRemotePresenter ||
                  event.key ===
                    "Escape"
                ) {
                  return;
                }

                event.preventDefault();

                sendRemoteControlInput(
                  remotePresenter.id,
                  {
                    type: "key",
                    action: "up",
                    key: event.key,
                    code: event.code,
                    ctrlKey:
                      event.ctrlKey,
                    altKey:
                      event.altKey,
                    shiftKey:
                      event.shiftKey,
                    metaKey:
                      event.metaKey
                  }
                );
              }}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "contain",
                background: "#000",
                outline: "none",
                cursor:
                  canControlRemotePresenter
                    ? "crosshair"
                    : "default",
                touchAction:
                  canControlRemotePresenter
                    ? "none"
                    : "auto",
                userSelect: "none"
              }}
            />

            <PresenterNotice key={remotePresenter.id} name={remotePresenter.name} />
            <div style={{ position: "absolute", right: 16, bottom: 16, zIndex: 4, width: "min(240px, 28vw)", aspectRatio: "16 / 9", borderRadius: 14, overflow: "hidden", background: "var(--lesson-deep, #071225)", border: "2px solid rgba(255,255,255,.2)" }}>
              <video ref={(element) => { const stream = remoteCameraStreamsRef.current.get(remotePresenter.id); if (element && stream && element.srcObject !== stream) { element.srcObject = stream; element.play().catch(() => {}); } }} autoPlay muted playsInline style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              {remotePresenter.cameraEnabled === false && <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", background: "var(--lesson-deep, #071225)" }}>Camera off</div>}
              <div style={{ position: "absolute", left: 7, bottom: 6, padding: "0.2rem 0.4rem", background: "rgba(2,6,23,.72)", borderRadius: 6, fontSize: "0.65rem" }}>{remotePresenter.name || "Presenter"}</div>
            </div>
            {/* REMOTE CONTROL STATUS */}

            {canControlRemotePresenter && (
              <div
                style={{
                  position: "absolute",
                  top: "68px",
                  left: "50%",
                  transform:
                    "translateX(-50%)",
                  zIndex: 4,
                  display: "flex",
                  alignItems: "center",
                  gap: "0.6rem",
                  padding:
                    "0.5rem 0.75rem",
                  borderRadius: "10px",
                  background:
                    "rgba(5,46,22,.92)",
                  border:
                    "1px solid rgba(34,197,94,.45)",
                  fontSize: "0.75rem"
                }}
              >
                <span>
                  🖱 Remote control active — click the shared screen and type normally. Esc releases control.
                </span>

                <button
                  onClick={
                    releaseRemoteControl
                  }
                  style={{
                    border: "none",
                    borderRadius: "7px",
                    padding:
                      "0.35rem 0.55rem",
                    background: "#dc2626",
                    color: "white",
                    cursor: "pointer",
                    fontWeight: "bold",
                    fontSize: "0.7rem"
                  }}
                >
                  Stop
                </button>
              </div>
            )}

            </div>
            {/* PRESENTATION CONTROLS BELOW THE SHARED SCREEN */}

            <div className="hl-controls"
              style={{
                position: "relative",
                flexShrink: 0,
                zIndex: 5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.55rem",
                width: "100%",
                boxSizing: "border-box",
                padding:
                  "0.65rem 0.8rem",
                borderRadius: "18px",
                background:
                  "rgba(15,23,42,.92)",
                border:
                  "1px solid rgba(255,255,255,.15)",
                boxShadow:
                  "0 10px 36px rgba(0,0,0,.45)",
                backdropFilter:
                  "blur(10px)",
                flexWrap: "wrap"
              }}
            >
              <button
                onClick={toggleHandRaised}
                aria-pressed={handRaised}
                style={{
                  ...controlButtonStyle,
                  background: handRaised
                    ? "#b45309"
                    : "var(--lesson-control, #334155)",
                  padding: "0.65rem 0.9rem"
                }}
              >
                {handRaised
                  ? "✋ Lower Hand"
                  : "✋ Raise Hand"}
              </button>

              <button
                onClick={
                  toggleMicrophone
                }
                style={{
                  ...controlButtonStyle,
                  background:
                    micEnabled
                      ? "var(--lesson-control, #334155)"
                      : "#991b1b",
                  padding:
                    "0.65rem 0.9rem"
                }}
              >
                {micEnabled
                  ? "🎤 Mute"
                  : "🔇 Unmute"}
              </button>

              <button
                onClick={
                  toggleCamera
                }
                style={{
                  ...controlButtonStyle,
                  background:
                    cameraEnabled
                      ? "var(--lesson-control, #334155)"
                      : "#991b1b",
                  padding:
                    "0.65rem 0.9rem"
                }}
              >
                {cameraEnabled
                  ? "📹 Camera Off"
                  : "📹 Camera On"}
              </button>

              {isElectronDesktop && (
                <button
                  onClick={
                    openWhiteboard
                  }
                  style={{
                    ...controlButtonStyle,
                    background:
                      whiteboardOpen
                        ? "var(--lesson-primary, #2563eb)"
                        : "var(--lesson-control, #334155)",
                    padding:
                      "0.65rem 0.9rem"
                  }}
                >
                  ✏️ Whiteboard
                </button>
              )}

              <button
                onClick={() =>
                  setChatOpen(
                    (current) =>
                      !current
                  )
                }
                style={{
                  ...controlButtonStyle,
                  background:
                    chatOpen
                      ? "var(--lesson-primary, #2563eb)"
                      : "var(--lesson-control, #334155)",
                  padding:
                    "0.65rem 0.9rem",
                  position: "relative"
                }}
              >
                💬 Chat

                {unreadMessages > 0 && (
                  <span
                    style={{
                      position: "absolute",
                      top: "-8px",
                      right: "-8px",
                      minWidth: "20px",
                      height: "20px",
                      padding: "0 4px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent:
                        "center",
                      borderRadius: "999px",
                      background: "#ef4444",
                      border:
                        "2px solid #0f172a",
                      fontSize: "0.65rem"
                    }}
                  >
                    {unreadMessages > 99
                      ? "99+"
                      : unreadMessages}
                  </span>
                )}
              </button>

              <button
                onClick={
                  leaveRoom
                }
                style={{
                  ...controlButtonStyle,
                  background: "#dc2626",
                  padding:
                    "0.65rem 0.9rem"
                }}
              >
                📞 Leave
              </button>
            </div>
          </div>
        )}

      {/* =====================================================
          CHAT PANEL
          ===================================================== */}

      {hasJoinedLesson &&
        chatOpen && (
          <aside className="hl-surface hl-chat"
            style={{
              position: "fixed",
              top: 0,
              right: 0,
              bottom: 0,

              width: "360px",
              maxWidth: "100vw",

              background:
                "var(--lesson-panel, #102445)",

              borderLeft:
                "1px solid rgba(255,255,255,0.12)",

              zIndex: 13000,

              display: "flex",
              flexDirection:
                "column",

              boxShadow:
                "-10px 0 35px rgba(0,0,0,0.28)"
            }}
          >
            {/* HEADER */}

            <div
              style={{
                height: "72px",

                flexShrink: 0,

                padding:
                  "0 1rem",

                display:
                  "flex",

                justifyContent:
                  "space-between",

                alignItems:
                  "center",

                borderBottom:
                  "1px solid rgba(255,255,255,0.1)"
              }}
            >
              <div>
                <div
                  style={{
                    fontSize:
                      "1.05rem",

                    fontWeight:
                      "bold"
                  }}
                >
                  💬 Lesson Chat
                </div>

                <div
                  style={{
                    fontSize:
                      "0.75rem",

                    opacity:
                      0.6,

                    marginTop:
                      "0.2rem"
                  }}
                >
                  Everyone in this lesson
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem"
                }}
              >
                {isElectronDesktop && (
                  <button
                    type="button"
                    onClick={clearLessonChat}
                    disabled={clearingChat}
                    title="Delete every message in this lesson chat"
                    style={{
                      width: "auto",
                      height: "36px",
                      padding: "0 0.7rem",
                      border: "1px solid rgba(248,113,113,0.55)",
                      borderRadius: "9px",
                      background: clearingChat
                        ? "#4b2630"
                        : "#7f1d1d",
                      color: "white",
                      fontSize: "0.76rem",
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      cursor: clearingChat
                        ? "default"
                        : "pointer",
                      opacity: clearingChat ? 0.7 : 1
                    }}
                  >
                    {clearingChat
                      ? "Clearing…"
                      : "Clear chat"}
                  </button>
                )}

                <button
                  onClick={
                    () =>
                      setChatOpen(
                        false
                      )
                  }
                  style={{
                    width: "36px",
                    height: "36px",

                    border:
                      "none",

                    borderRadius:
                      "50%",

                    background:
                      "#1e365b",

                    color:
                      "white",

                    cursor:
                      "pointer"
                  }}
                >
                  ✕
                </button>
              </div>
            </div>

            {/* MESSAGES */}

            <div
              style={{
                flex: 1,

                minHeight: 0,

                overflowY:
                  "auto",

                padding:
                  "1rem"
              }}
            >
              {chatMessages.length ===
              0 ? (
                <div
                  style={{
                    textAlign:
                      "center",

                    opacity:
                      0.55,

                    padding:
                      "3rem 1rem"
                  }}
                >
                  <div
                    style={{
                      fontSize:
                        "2.5rem"
                    }}
                  >
                    💬
                  </div>

                  <p>
                    No messages yet.
                  </p>
                </div>
              ) : (
                chatMessages.map(
                  (message) => {
                    const isOwn =
                      message.senderId ===
                      participantIdRef.current;

                    return (
                      <div
                        key={
                          message.id
                        }
                        style={{
                          display:
                            "flex",

                          justifyContent:
                            isOwn
                              ? "flex-end"
                              : "flex-start",

                          marginBottom:
                            "0.9rem"
                        }}
                      >
                        <div
                          style={{
                            maxWidth:
                              "82%"
                          }}
                        >
                          <div
                            style={{
                              display:
                                "flex",

                              justifyContent:
                                isOwn
                                  ? "flex-end"
                                  : "flex-start",

                              gap:
                                "0.4rem",

                              marginBottom:
                                "0.25rem",

                              fontSize:
                                "0.72rem",

                              opacity:
                                0.65
                            }}
                          >
                            <span>
                              {isOwn
                                ? "You"
                                : message.senderName ||
                                  "Participant"}
                            </span>

                            {formatMessageTime(
                              message.createdAt
                            ) && (
                              <>
                                <span>
                                  •
                                </span>

                                <span>
                                  {formatMessageTime(
                                    message.createdAt
                                  )}
                                </span>
                              </>
                            )}
                          </div>

                          <div
                            style={{
                              background:
                                isOwn
                                  ? "var(--lesson-primary, #2563eb)"
                                  : "#1e365b",

                              padding:
                                "0.7rem 0.85rem",

                              borderRadius:
                                isOwn
                                  ? "14px 14px 3px 14px"
                                  : "14px 14px 14px 3px",

                              lineHeight:
                                1.45,

                              overflowWrap:
                                "anywhere",

                              whiteSpace:
                                "pre-wrap",

                              userSelect: "text",
                              WebkitUserSelect: "text",
                              cursor: "text"
                            }}
                          >
                            {
                              renderChatMessage(message.text)
                            }
                          </div>
                        </div>
                      </div>
                    );
                  }
                )
              )}

              {typingIndicatorText && (
                <div
                  role="status"
                  aria-live="polite"
                  style={{
                    minHeight: "28px",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.45rem",
                    marginTop: "0.15rem",
                    color: "#bfdbfe",
                    fontSize: "0.78rem",
                    fontWeight: 600
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      display: "inline-flex",
                      gap: "3px"
                    }}
                  >
                    {[0, 1, 2].map((dot) => (
                      <span
                        key={dot}
                        style={{
                          width: "5px",
                          height: "5px",
                          borderRadius: "50%",
                          background: "#60a5fa",
                          opacity: 0.55 + dot * 0.2
                        }}
                      />
                    ))}
                  </span>
                  <span>{typingIndicatorText}</span>
                </div>
              )}

              <div
                ref={
                  chatEndRef
                }
              />
            </div>

            {/* CHAT INPUT */}

            <div
              style={{
                flexShrink: 0,

                borderTop:
                  "1px solid rgba(255,255,255,0.1)",

                padding:
                  "0.85rem"
              }}
            >
              <div
                style={{
                  display:
                    "flex",

                  gap:
                    "0.55rem",

                  alignItems:
                    "flex-end"
                }}
              >
                <textarea
                  value={
                    chatMessage
                  }

                  onChange={
                    (event) =>
                      updateChatDraft(
                        event.target.value
                      )
                  }

                  onKeyDown={
                    (event) => {
                      if (
                        event.key ===
                          "Enter" &&
                        !event.shiftKey
                      ) {
                        event.preventDefault();

                        sendChatMessage();
                      }
                    }
                  }

                  placeholder="Type a message..."

                  rows={1}

                  style={{
                    flex: 1,

                    minHeight:
                      "44px",

                    maxHeight:
                      "120px",

                    resize:
                      "vertical",

                    border:
                      "1px solid #38557c",

                    borderRadius:
                      "12px",

                    background:
                      "var(--lesson-deep, #071225)",

                    color:
                      "white",

                    padding:
                      "0.7rem 0.8rem",

                    fontFamily:
                      "inherit",

                    fontSize:
                      "0.9rem"
                  }}
                />

                <button
                  onClick={
                    sendChatMessage
                  }

                  disabled={
                    !chatMessage.trim() ||
                    sendingMessage
                  }

                  style={{
                    width:
                      "44px",

                    height:
                      "44px",

                    flexShrink:
                      0,

                    border:
                      "none",

                    borderRadius:
                      "12px",

                    background:
                      chatMessage.trim() &&
                      !sendingMessage
                        ? "var(--lesson-primary, #2563eb)"
                        : "var(--lesson-control, #475569)",

                    color:
                      "white",

                    cursor:
                      chatMessage.trim() &&
                      !sendingMessage
                        ? "pointer"
                        : "default",

                    fontWeight:
                      "bold"
                  }}
                >
                  ➤
                </button>
              </div>

              <div
                style={{
                  marginTop:
                    "0.4rem",

                  fontSize:
                    "0.68rem",

                  opacity:
                    0.45
                }}
              >
                Enter to send • Shift + Enter for a new line
              </div>
            </div>
          </aside>
        )}

      <CollaborativeWhiteboard
        open={
          hasJoinedLesson &&
          whiteboardOpen
        }
        isTutor={
          isElectronDesktop
        }
        participantId={
          participantIdRef.current
        }
        participantName={
          displayName
        }
        cameraParticipants={
          remoteParticipants.map(
            (participant) => ({
              ...participant,
              stream:
                remoteStreamsRef.current.get(
                  participant.id
                ) || null
            })
          )
        }
        micEnabled={
          micEnabled
        }
        handRaised={
          handRaised
        }
        localAudioTrack={getCurrentOutgoingAudioTrack()}
        localVideoTrack={getCameraVideoTrack()}
        remoteAudioTracks={
          remoteParticipants.flatMap(
            (participant) =>
              remoteStreamsRef.current
                .get(participant.id)
                ?.getAudioTracks() || []
          )
        }
        cameraEnabled={
          cameraEnabled
        }
        isScreenSharing={
          isScreenSharing
        }
        chatOpen={
          chatOpen
        }
        unreadMessages={
          unreadMessages
        }
        onToggleMicrophone={
          toggleMicrophone
        }
        onToggleHandRaised={
          toggleHandRaised
        }
        onToggleCamera={
          toggleCamera
        }
        onStartScreenShare={
          startScreenShare
        }
        onToggleChat={() =>
          setChatOpen(
            (current) =>
              !current
          )
        }
        onLeaveLesson={
          leaveRoom
        }
        command={
          whiteboardCommand
        }
        sendMessage={
          broadcastWhiteboardMessage
        }
        onSnapshotChange={
          (nextStrokes) => {
            whiteboardSnapshotRef.current =
              nextStrokes;
            scheduleWhiteboardSave(nextStrokes);
          }
        }
        onTutorClose={
          closeWhiteboard
        }
      />

      {raisedHandParticipants.length > 0 && (
        <div
          role="status"
          aria-live="assertive"
          style={{
            position: "fixed",
            top: mutedSpeechWarning ? "148px" : "24px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 29000,
            width: "max-content",
            maxWidth: "calc(100vw - 32px)",
            padding: "13px 20px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            borderRadius: "999px",
            border: "2px solid #fde68a",
            background: "rgba(146,64,14,.97)",
            color: "white",
            boxShadow: "0 12px 34px rgba(0,0,0,.42)",
            fontSize: "1rem",
            fontWeight: 850,
            pointerEvents: "none",
            transition: "top .2s ease"
          }}
        >
          <span style={{ fontSize: "1.45rem" }}>✋</span>
          {raisedHandParticipants.length === 1
            ? `${raisedHandParticipants[0].name || "Participant"} has raised their hand`
            : `${raisedHandParticipants.length} participants have raised their hands`}
        </div>
      )}

      {mutedSpeechWarning && (
        <div
          role="alert"
          aria-live="assertive"
          style={{
            position: "fixed",
            top: "32px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 30000,
            width: "min(620px, calc(100vw - 32px))",
            padding: "20px 26px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "16px",
            border: "3px solid #fca5a5",
            borderRadius: "18px",
            background: "rgba(153,27,27,.98)",
            color: "white",
            boxShadow: "0 18px 48px rgba(0,0,0,.48)",
            textAlign: "center",
            pointerEvents: "none"
          }}
        >
          <span
            aria-hidden="true"
            style={{
              flexShrink: 0,
              fontSize: "2.2rem",
              lineHeight: 1
            }}
          >
            🔇
          </span>

          <div>
            <div
              style={{
                fontSize: "clamp(1.25rem, 3vw, 1.75rem)",
                fontWeight: 900,
                lineHeight: 1.15
              }}
            >
              You’re muted
            </div>
            <div
              style={{
                marginTop: "5px",
                fontSize: "clamp(1rem, 2.2vw, 1.2rem)",
                fontWeight: 700
              }}
            >
              Unmute your microphone to be heard
            </div>
          </div>
        </div>
      )}

      {!isElectronDesktop && screenRecordingNotice && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: "fixed",
            top: "22px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 12000,
            display: "inline-flex",
            alignItems: "center",
            gap: "9px",
            maxWidth: "calc(100vw - 32px)",
            padding: "10px 16px",
            borderRadius: "999px",
            border: screenRecordingNotice.kind === "paused"
              ? "1px solid #fbbf24"
              : screenRecordingNotice.kind === "stopped"
                ? "1px solid #4ade80"
                : "1px solid #f87171",
            background: screenRecordingNotice.kind === "paused"
              ? "rgba(120,53,15,.96)"
              : screenRecordingNotice.kind === "stopped"
                ? "rgba(20,83,45,.96)"
                : "rgba(127,29,29,.96)",
            color: "white",
            boxShadow: "0 8px 25px rgba(0,0,0,.35)",
            fontSize: "0.86rem",
            fontWeight: 800,
            whiteSpace: "nowrap",
            pointerEvents: "none",
            opacity: screenRecordingNotice.visible ? 1 : 0,
            transition: "opacity 420ms ease"
          }}
        >
          <span style={{ color: screenRecordingNotice.kind === "paused" ? "#fbbf24" : screenRecordingNotice.kind === "stopped" ? "#86efac" : "#f87171" }}>
            {screenRecordingNotice.kind === "paused"
              ? "Ⅱ"
              : screenRecordingNotice.kind === "stopped"
                ? "■"
                : "●"}
          </span>
          {screenRecordingNotice.kind === "paused"
            ? "Screen Recording Paused"
            : screenRecordingNotice.kind === "resumed"
              ? "Screen Recording Resumed"
              : screenRecordingNotice.kind === "stopped"
                ? "Screen Recording Stopped"
                : "Screen Recording Started"}
        </div>
      )}

      {/* =====================================================
          REMOTE CONTROL OFFER MODAL — STUDENT SIDE
          ===================================================== */}

      {remoteControlOffer && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 20000,
            background: "rgba(2,6,23,.72)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "1rem"
          }}
        >
          <div className="hl-surface"
            style={{
              width: "min(460px, 100%)",
              background: "var(--lesson-panel, #102445)",
              border: "1px solid rgba(255,255,255,.14)",
              borderRadius: "18px",
              boxShadow: "0 24px 70px rgba(0,0,0,.45)",
              padding: "1.4rem"
            }}
          >
            <div
              style={{
                width: "52px",
                height: "52px",
                borderRadius: "14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#0f766e",
                fontSize: "1.5rem",
                marginBottom: "1rem"
              }}
            >
              🖱
            </div>

            <h2 style={{ margin: 0, fontSize: "1.25rem" }}>
              Remote control request
            </h2>

            <p
              style={{
                margin: "0.75rem 0 0",
                lineHeight: 1.55,
                opacity: 0.82
              }}
            >
              {remoteControlOffer.tutorName || "Your tutor"} is offering you control of the shared screen. If you accept, your mouse and keyboard actions on the shared-screen video can control the tutor's computer until either of you stops remote control.
            </p>

            <p
              style={{
                margin: "0.75rem 0 0",
                fontSize: "0.8rem",
                opacity: 0.6
              }}
            >
              Only interact with the tutor's computer while they have explicitly granted you control. Press Esc at any time to release control.
            </p>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "0.65rem",
                marginTop: "1.25rem"
              }}
            >
              <button
                onClick={
                  declineRemoteControlOffer
                }
                style={{
                  border: "1px solid rgba(255,255,255,.15)",
                  borderRadius: "10px",
                  padding: "0.7rem 1rem",
                  background: "var(--lesson-control, #1e293b)",
                  color: "white",
                  cursor: "pointer",
                  fontWeight: "bold"
                }}
              >
                Decline
              </button>

              <button
                onClick={
                  acceptRemoteControlOffer
                }
                style={{
                  border: "none",
                  borderRadius: "10px",
                  padding: "0.7rem 1rem",
                  background: "#16a34a",
                  color: "white",
                  cursor: "pointer",
                  fontWeight: "bold"
                }}
              >
                Accept control
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =====================================================
          FALLBACK SCREEN SHARE TOOLBAR
          ===================================================== */}

      {hasJoinedLesson &&
        isScreenSharing &&
        !pipOpen &&
        !isElectronDesktop && (
          <div className="hl-controls"
            style={{
              position:
                "fixed",

              bottom:
                "24px",

              left:
                "50%",

              transform:
                "translateX(-50%)",

              zIndex:
                9999,

              display:
                "flex",

              alignItems:
                "center",

              gap:
                "0.6rem",

              flexWrap:
                "wrap",

              background:
                "rgba(15,23,42,0.97)",

              border:
                "1px solid rgba(255,255,255,.15)",

              borderRadius:
                "16px",

              padding:
                "0.65rem 0.8rem",

              boxShadow:
                "0 10px 30px rgba(0,0,0,.45)"
            }}
          >
            <div>
              <div
                style={{
                  fontSize:
                    "0.65rem",

                  opacity:
                    0.6
                }}
              >
                SCREEN SHARE
              </div>

              <strong
                style={{
                  color:
                    isScreenFrozen
                      ? "#c4b5fd"
                      : "#86efac"
                }}
              >
                {isScreenFrozen
                  ? "Frozen"
                  : "Live"}
              </strong>
            </div>

            <button
              onClick={
                isScreenFrozen
                  ? unfreezeScreenShare
                  : freezeScreenShare
              }
              style={{
                ...controlButtonStyle,

                background:
                  isScreenFrozen
                    ? "#16a34a"
                    : "#6d28d9"
              }}
            >
              {isScreenFrozen
                ? "▶ Resume"
                : "❄ Freeze"}
            </button>

            <button
              onClick={toggleHandRaised}
              aria-pressed={handRaised}
              style={{
                ...controlButtonStyle,
                background: handRaised
                  ? "#b45309"
                  : "var(--lesson-control, #334155)"
              }}
            >
              {handRaised
                ? "✋ Lower Hand"
                : "✋ Raise Hand"}
            </button>

            <button
              onClick={
                toggleMicrophone
              }
              style={{
                ...controlButtonStyle,

                background:
                  micEnabled
                    ? "var(--lesson-control, #334155)"
                    : "#991b1b"
              }}
            >
              {micEnabled
                ? "🎤 Mute"
                : "🔇 Unmute"}
            </button>

            <button
              onClick={
                openFloatingController
              }
              style={{
                ...controlButtonStyle,

                background:
                  "#0369a1"
              }}
            >
              ↗ Floating Controls
            </button>

            <button
              onClick={
                stopScreenShare
              }
              style={{
                ...controlButtonStyle,

                background:
                  "#dc2626"
              }}
            >
              ⏹ Stop Share
            </button>
          </div>
        )}

      {/* =====================================================
          FLOATING WINDOW ACTIVE INDICATOR
          ===================================================== */}

      {hasJoinedLesson &&
        isScreenSharing &&
        pipOpen &&
        !isElectronDesktop && (
          <div
            style={{
              position:
                "fixed",

              bottom:
                "20px",

              right:
                chatOpen
                  ? "380px"
                  : "20px",

              zIndex:
                9000,

              background:
                "#0f172a",

              border:
                "1px solid rgba(255,255,255,.15)",

              borderRadius:
                "999px",

              padding:
                "0.55rem 0.8rem",

              fontSize:
                "0.8rem",

              boxShadow:
                "0 8px 25px rgba(0,0,0,.35)"
            }}
          >
            🖥{" "}
            {isScreenFrozen
              ? "Screen frozen"
              : "Screen sharing"}
            {" • "}
            Floating controls active
          </div>
        )}
    </div>
  );
};

export default VideoLessonRoom;
